/**
 * Lightweight UI locale for Other Side.
 * IT / EN / ES / PT — stored in localStorage.
 * All primary UI strings MUST be routed through `t("key")`.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export const LOCALES = [
    { code: "it", label: "IT", name: "Italiano" },
    { code: "en", label: "EN", name: "English" },
    { code: "es", label: "ES", name: "Español" },
    { code: "pt", label: "PT", name: "Português" },
    { code: "fr", label: "FR", name: "Français" },
];

const STORAGE_KEY = "os_locale";
const DEFAULT = "it";

const STRINGS = {
    it: {
        // Header / navigation
        "header.sub":               "— una presenza, custodita",
        "nav.presences":            "Presenze",
        "nav.inbox":                "Pensieri",
        "nav.quietroom":            "Silenzio",
        "nav.plan":                 "Piano",
        "nav.settings":             "Impostazioni",
        "nav.signout":              "Esci",
        "nav.signin":               "Entra",
        "nav.begin":                "Inizia ora",

        // Landing — hero
        "hero.eyebrow":             "Other Side",
        "hero.title_a":             "Ricostruisci",
        "hero.title_b":             "la loro presenza.",
        "hero.body":                "Attraverso foto, voce e ricordi, crea un'esperienza autentica e personale.",
        "hero.cta_primary":         "Crea presenza",
        "hero.subbody":             "Carica una foto, scegli una voce e rivivi un momento che sembra vero.",
        "hero.cta_begin":           "Inizia ora",
        "hero.cta_signin":          "Entra",
        "hero.emotional":           "Non è solo memoria.",
        "hero.emotional_i":         "È un modo per sentire ancora vicina una presenza che conta.",
        "hero.disclaimer":          "Questa esperienza utilizza l'intelligenza artificiale per ricreare interazioni basate sui contenuti forniti. Non rappresenta una comunicazione reale.",

        // Landing — four gestures
        "landing.steps_eyebrow":    "Quattro gesti silenziosi",
        "landing.steps_title":      "Comincia dalle piccole cose che ha lasciato.",
        "landing.step1_t":          "Una fotografia",
        "landing.step1_d":          "Un'immagine. Quella a cui torni sempre.",
        "landing.step2_t":          "Una voce",
        "landing.step2_d":          "Dieci secondi bastano. Un vocale, una nota, una risata.",
        "landing.step3_t":          "Com'era",
        "landing.step3_d":          "Il calore, la schiettezza, le parole che tornavano.",
        "landing.step4_t":          "I tuoi ricordi",
        "landing.step4_d":          "I pomeriggi, le battute, le discussioni.",

        // Landing — memory scene
        "landing.memory_eyebrow":   "Non è un chatbot",
        "landing.memory_title_a":   "Le parole sono",
        "landing.memory_title_i":   "le sue.",
        "landing.memory_title_b":   "Non le nostre.",
        "landing.memory_body":      "Ogni presenza nasce da ciò che condividi.\nNessun entusiasmo artificiale.\nNessuna frase di circostanza.\nFrasi brevi. Le parole che usava.\nI silenzi che contavano.",
        "landing.memory_quote":     "\"Non voglio un chatbot.\nVoglio sentire qualcosa che suoni ancora come lei.\"",

        // Footer
        "footer.tag":               "Other Side è un atto di memoria, non un sostituto della presenza.",
        "footer.legal":             "Legale",
        "footer.report":            "Segnala abuso",

        // Cookie banner
        "cookie.text":              "Questo sito usa cookie per il funzionamento essenziale.",
        "cookie.essential":         "Solo essenziali",
        "cookie.all":               "Accetta tutto",

        // Dashboard
        "dashboard.eyebrow":        "Ciao",
        "dashboard.title_a":        "Le tue",
        "dashboard.title_b":        "presenze.",
        "dashboard.body":           "Le persone che hai riportato qui. Aprine una per parlarle, o crea una nuova presenza.",
        "dashboard.new":            "Nuova presenza",
        "dashboard.empty_title":    "Ancora nessuna presenza.",
        "dashboard.empty_body":     "Comincia con una fotografia, una breve registrazione e ciò che ricordi di più.",
        "dashboard.empty_cta":      "Comincia",
        "dashboard.loading":        "apro l'album…",
        "dashboard.limit_reached":  "Limite raggiunto.",
        "dashboard.limit_link":     "Scegli un piano",
        "dashboard.limit_tail":     "per crearne altre.",
        "dashboard.quiet_eyebrow":  "Modalità alternativa",
        "dashboard.quiet_title":    "Il silenzio",
        "dashboard.quiet_body":     "Un posto dove essere ascoltati senza ricostruire nessuno. Empatico. Non specifico. Sempre tuo.",
        "dashboard.quiet_cta":      "Avvicinati",
        "dashboard.voice_captured": "voce catturata",
        "dashboard.today_eyebrow":       "Oggi",
        "dashboard.today_cta":           "Avvicinati",
        "dashboard.today_birthday":      "Oggi è il compleanno di {name}. Se vuoi, una luce è accesa.",
        "dashboard.today_anniversary":   "Oggi è un giorno importante per {name}. Se vuoi, una luce è accesa.",
        "dashboard.today_memorial":      "Oggi ricorre un giorno importante con {name}. Se vuoi, una luce è accesa.",
        "dashboard.today_other":         "Oggi è una data che hai segnato per {name}. Se vuoi, una luce è accesa.",

        // Presence profile — recurrences
        "presence.rec_title":            "Date significative",
        "presence.rec_sub":              "Aggiungi date che vuoi ricordare insieme a lei. Ti avviseremo con delicatezza quando arrivano.",
        "presence.rec_empty":            "Nessuna data ancora. Compleanni, anniversari, giornate speciali.",
        "presence.rec_add":              "Aggiungi una data",
        "presence.rec_label_ph":         "es. Compleanno, Anniversario…",
        "presence.rec_kind_birthday":    "Compleanno",
        "presence.rec_kind_anniversary": "Anniversario",
        "presence.rec_kind_memorial":    "Ricorrenza",
        "presence.rec_kind_other":       "Data speciale",
        "presence.rec_save":             "Salva",
        "presence.rec_cancel":           "Annulla",
        "presence.rec_remove":           "Rimuovi",

        // Auth
        "auth.eyebrow_signup":      "Inizia",
        "auth.eyebrow_login":       "Bentornato",
        "auth.title_signup_a":      "Crea la prima",
        "auth.title_signup_i":      "presenza.",
        "auth.title_login_a":       "Entra di",
        "auth.title_login_i":       "nuovo.",
        "auth.google_cta":          "Continua con Google",
        "auth.divider":             "oppure",
        "auth.name_label":          "Come vuoi essere chiamato",
        "auth.email_label":         "Email",
        "auth.password_label":      "Password",
        "auth.vulnerable":          "Se stai attraversando un lutto acuto, ti consigliamo di usare questa esperienza con consapevolezza.",
        "auth.ai_check_a":          "Capisco che è una",
        "auth.ai_check_strong":     "simulazione AI",
        "auth.ai_check_b":          "e non una comunicazione reale.",
        "auth.age_check_a":         "Confermo di avere",
        "auth.age_check_strong":    "più di 18 anni",
        "auth.age_check_b":         ".",
        "auth.legal_read":          "Leggi tutto il",
        "auth.legal_link":          "Legale & Termini",
        "auth.submit_signup":       "Inizia",
        "auth.submit_login":        "Entra",
        "auth.have_account":        "Hai già un account?",
        "auth.first_time":          "Prima volta?",
        "auth.go_signin":           "Entra",
        "auth.go_signup":           "Inizia",
        "auth.footer_legal":        "Legale",
        "auth.err_required":        "Per continuare, conferma di avere 18+ anni e accetta i termini.",
        "auth.err_age":             "Devi confermare di avere 18 anni o più.",
        "auth.err_terms":           "Accetta i termini per continuare.",
        "auth.err_generic":         "Qualcosa è andato storto.",

        // Settings
        "settings.eyebrow":         "Account",
        "settings.title":           "Le tue impostazioni.",
        "settings.signed_as":       "Entrato come",
        "settings.auth_method":     "Metodo di accesso",
        "settings.plan":            "Piano",
        "settings.plan_valid":      "Valido fino al",
        "settings.plan_presences":  "Presenze",
        "settings.plan_messages":   "Messaggi in 30 giorni",
        "settings.plan_explore":    "Esplora i piani",
        "settings.plan_manage":     "Gestisci piano",
        "settings.plan_free":       "Semplice",
        "settings.features":        "Il tuo piano include",
        "settings.feat_voice":      "Voce clonata",
        "settings.feat_photo":      "Foto animata",
        "settings.feat_unsolicited":"Messaggi non richiesti",
        "settings.feat_yes_soon":   "sì",
        "settings.feat_yes":        "sì",
        "settings.feat_no":         "non inclusa",
        "settings.privacy":         "Privacy",
        "settings.privacy_body":    "Foto, voci e ricordi che condividi restano tuoi. Cancellando una presenza, tutto viene rimosso.",
        "settings.legal":           "Legale",
        "settings.legal_link":      "Legale & Termini",
        "settings.data":            "I tuoi dati",
        "settings.data_body":       "I tuoi dati restano tuoi. Puoi scaricarli o eliminarli in qualsiasi momento.",
        "settings.export":          "Esporta i miei dati",
        "settings.delete":          "Elimina il mio account",
        "settings.safety":          "Sicurezza",
        "settings.safety_body":     "Qualcosa non va? Parlaci.",
        "settings.safety_link":     "Segnala abuso",
        "settings.delete_confirm":  "Questa azione è irreversibile. Verranno cancellati il tuo account, le presenze, le conversazioni e i ricordi. Procedo?",
        "settings.delete_type":     "Per confermare, scrivi ELIMINA in maiuscolo:",
        "settings.delete_keyword":  "ELIMINA",
        "settings.export_err":      "Non è riuscito. Riprova tra un momento.",
        "settings.delete_err":      "Non è riuscito. Scrivici a os.otherside.app@gmail.com.",

        // Legal page
        "legal.eyebrow":            "Legale",
        "legal.title_a":            "Legale & Termini —",
        "legal.title_i":            "Other Side.",
        "legal.body":               "Cos'è Other Side, cosa non è, e i confini in cui lavoriamo.",
        "legal.updated":            "Ultimo aggiornamento: 2026",
        "legal.s1_title":           "Avviso AI",
        "legal.s1_body_a":          "OTHER SIDE è un'esperienza basata su intelligenza artificiale pensata per ricreare memorie digitali a partire da contenuti forniti dall'utente.",
        "legal.s1_body_b_pre":      "Le interazioni sono",
        "legal.s1_body_b_strong1":  "simulate",
        "legal.s1_body_b_mid":      "e",
        "legal.s1_body_b_strong2":  "NON",
        "legal.s1_body_b_tail":     "rappresentano comunicazioni reali con persone decedute.",
        "legal.s2_title":           "Responsabilità emotiva",
        "legal.s2_body_a":          "Questa esperienza può generare risposte emotive. Usala con consapevolezza.",
        "legal.s2_body_b":          "Non forniamo consulenza psicologica o medica.",
        "legal.s3_title":           "Limite d'età",
        "legal.s3_body_pre":        "Il servizio è riservato agli utenti con",
        "legal.s3_body_strong":     "18+ anni",
        "legal.s3_body_tail":       ".",
        "legal.s4_title":           "Contenuti dell'utente",
        "legal.s4_body":            "Caricando contenuti, l'utente conferma di avere i diritti per utilizzarli e autorizza il loro trattamento tramite AI.",
        "legal.s5_title":           "Responsabilità",
        "legal.s5_body":            "Non siamo responsabili delle reazioni emotive, delle interpretazioni o delle decisioni dell'utente.",
        "legal.s6_title":           "Clausola di realtà",
        "legal.s6_body":            "La piattaforma non ricrea la coscienza o l'identità reale di nessuno.",

        // Report abuse
        "report.eyebrow":           "Segnala abuso",
        "report.title_a":           "Dicci cosa",
        "report.title_i":           "non va.",
        "report.body":              "Usa questo modulo per impersonificazione, minori, contenuti lesivi o qualsiasi violazione dei nostri Termini. Le segnalazioni vengono lette da una persona.",
        "report.type":              "Tipo di segnalazione",
        "report.t_imperson":        "Impersonificazione di una persona senza il suo consenso",
        "report.t_minor":           "Contenuto che coinvolge un minore",
        "report.t_celeb":           "Presenza di figura pubblica senza consenso familiare",
        "report.t_harm":            "Contenuto lesivo o molesto",
        "report.t_copy":            "Violazione di copyright o diritti",
        "report.t_other":           "Altro",
        "report.details":           "Cos'è successo",
        "report.details_ph":        "Raccontaci ogni dettaglio utile ad agire.",
        "report.email":             "La tua email (facoltativa)",
        "report.email_ph":          "nome@esempio.com",
        "report.submit":            "Invia segnalazione",
        "report.sending":           "Invio…",
        "report.empty":             "Descrivi cos'è successo così possiamo agire.",
        "report.err":               "Qualcosa è andato storto. Riprova o scrivici a os.otherside.app@gmail.com.",
        "report.sent_title":        "Ricevuta.",
        "report.sent_body":         "Leggiamo ogni segnalazione con attenzione. Se abbiamo bisogno di altro ti contatteremo all'email che hai lasciato.",
        "report.ref":               "Riferimento",
        "report.home":              "Home",
        "report.urgent":            "Per questioni urgenti di sicurezza puoi scrivere direttamente a",

        // Inbox
        "inbox.eyebrow":            "Ti hanno pensato",
        "inbox.title_a":            "Pensieri",
        "inbox.title_i":            "arrivati.",
        "inbox.body":               "Quando passano dei giorni, a volte ti scrivono per primi. Li trovi qui.",
        "inbox.loading":            "apro la scatola…",
        "inbox.empty_title":        "Ancora silenzio.",
        "inbox.empty_body":         "Tra un paio di giorni, se non ti farai sentire, qualcuno potrebbe pensarti.",
        "inbox.back":               "Torna alla dashboard",
        "inbox.thought_first":      "pensato per primo",

        // Conversation
        "chat.conversation":        "Conversazione",
        "chat.empty_presence":      "— {name} è qui. Dille qualcosa. —",
        "chat.empty_generic":       "— Silenzio. Scrivi quando vuoi. —",
        "chat.first_hint":          "Non sai come iniziare? Prova così:",
        "chat.first_suggest_1":     "Raccontale una cosa successa oggi",
        "chat.first_suggest_1_d":   "Come se rientrassi a casa e volessi dirglielo.",
        "chat.first_suggest_2":     "Chiedile cosa ricorda di te",
        "chat.first_suggest_2_d":   "Un dettaglio che pensavi avesse dimenticato.",
        "chat.first_suggest_3":     "Un messaggio che non le hai mai detto",
        "chat.first_suggest_3_d":   "Le parole che ti sono rimaste dentro.",
        "chat.first_free_write":    "Oppure scrivi quello che senti — lei ascolta.",
        "chat.typing":              "sta scrivendo…",
        "chat.input_placeholder":   "Scrivi qualcosa…",
        "chat.pause_label":         "Pausa esperienza",
        "chat.pause_short":         "Pausa",
        "chat.pause_confirm":       "Pausa. Tutto resta qui, la ritrovi quando vuoi.",
        "chat.send_err":            "Non sono riuscito a inviare.",
        "chat.eyebrow_unsolicited": "ti ha pensato",
        "chat.eyebrow_opening":     "primo messaggio",
        "chat.voice_cached":        "ascolta con la sua voce",
        "chat.voice_preview":       "ascoltala, una volta",
        "chat.voice_again":         "ascoltarla ancora…",
        "chat.voice_soon":          "Genera voce",
        "chat.voice_generate":      "Genera voce",
        "chat.voice_later":         "Più tardi",
        "chat.voice_listen_now":    "Ascolta ora",
        "chat.voice_invite":        "Vuoi continuare a sentire la sua voce?",
        "chat.voice_continue":      "ascoltarla ancora",
        "chat.voice_gentle":        "Ascoltarla ancora…",
        "chat.voice_arriving":      "Sto generando la voce…",
        "chat.voice_err":           "La voce sta ancora arrivando.",
        "chat.voice_err_hint":      "A volte il servizio di voce richiede qualche secondo in più. Può capitare quando è molto richiesto — prova fra poco, di solito funziona subito.",
        "chat.voice_err_retry":     "Prova di nuovo",
        "chat.voice_err_later":     "Continua a leggerla qui",
        "chat.voice_501_hint":      "La voce sarà disponibile a breve. Intanto puoi continuare a leggerla — è la stessa presenza.",
        "chat.voice_add_sample":    "Aggiungi la sua voce",
        "chat.voice_add_sample_d":  "Carica un breve audio suo e ricostruiremo il suo tono per tutti i messaggi.",
        "chat.voice_unlock":        "Sblocca la sua voce",
        "chat.voice_unlock_d":      "Ascolta ogni messaggio con il suo vero tono — disponibile nei piani con voce.",
        "chat.moment_upsell":       "Vuoi rivederla, solo per un momento?",
        "chat.moment_upsell_cta":   "crea un momento",
        "chat.moment_upsell_skip":  "non adesso",
        "chat.moment_upsell_nophoto":"Per rivederla serve una sua foto — poi possiamo darle movimento e voce.",
        "chat.moment_upsell_add_photo":"aggiungi la sua foto",

        // Presence arrives
        "arrives.lighting":         "Sto creando la presenza…",
        "arrives.arriving":         "Sta per scriverti…",
        "arrives.first_message":    "primo messaggio",
        "arrives.reply":            "Rispondi",
        "arrives.wait":             "Non c'è fretta. Lei ti aspetta.",
        "arrives.err":              "Non è riuscita ad arrivare.",
        "arrives.go_profile":       "Vai al profilo",

        // Presence profile
        "profile.back":             "← indietro",
        "profile.loading":          "caricamento…",
        "profile.voice_captured":   "voce trascritta",
        "profile.resume":           "Riprendi la conversazione",
        "profile.resume_d":         "Torni dove vi siete fermati.",
        "profile.new_chat":         "Nuova conversazione",
        "profile.new_chat_d":       "Apri una pagina bianca — le precedenti restano qui.",
        "profile.new_chat_pending": "Sto preparando…",
        "profile.open_d":           "La prima volta — comincia a parlarle.",
        "profile.create_moment_d":  "Un'immagine di lei, una sua voce — o entrambe.",
        "profile.edit_d":           "Cambia i suoi dettagli — si adatterà subito.",
        "profile.delete_d":         "Rimuove la presenza e tutti i vostri messaggi. Non si può annullare.",
        "profile.actions_help":     "Ogni scelta è reversibile — tranne l'eliminazione.",
        "profile.delete_confirm":   "Cancellare la presenza? Perderai tutti i messaggi, le voci e i momenti creati con lei. Non si può annullare.",
        "profile.open":             "Inizia a parlarle",
        "profile.edit":             "Modifica",
        "profile.cancel":           "Annulla",
        "profile.delete":           "Elimina",
        "profile.not_arrived":      "Non è riuscita ad arrivare.",
        "profile.past_title":       "Le vostre conversazioni",
        "profile.msg_one":          "messaggio",
        "profile.msg_many":         "messaggi",
        "profile.continuity_title": "Quello che ricorda di voi",
        "profile.save":             "Salva",
        "profile.coming_soon":      "prossimamente",
        "profile.talking_photo_t":  "Anima la foto",
        "profile.talking_photo_d":  "La sua foto che parla, con lip-sync. Presto.",
        "profile.plans_only":       "Disponibile nei piani Echo e Legacy.",
        "profile.identity":         "L'identità",
        "profile.f_address":        "Come ti chiamava",
        "profile.f_personality":    "Personalità",
        "profile.f_emotional":      "Tratti emotivi",
        "profile.f_tone":           "Tono",
        "profile.f_phrases":        "Frasi",
        "profile.f_anchor":         "Un momento che resta",
        "profile.f_farewell":       "Come salutava",
        "profile.f_never":          "Cose che non avrebbe mai detto",
        "profile.f_memories":       "Ricordi",
        "profile.f_not_recorded":   "— non registrato —",
        "profile.unsolicited_eyebrow":"Lascia che ti scriva lei",
        "profile.unsolicited_title":"Pensieri non richiesti",
        "profile.unsolicited_body": "Se passano dei giorni senza sentirvi, può mandarti un pensiero. Rispettosamente. Al massimo 1-2 alla settimana.",
        "profile.unsolicited_on":   "Attivo",
        "profile.unsolicited_off":  "Disattivato",
        "profile.unsolicited_tone": "Tono",
        "profile.tone_gentle":      "dolce",
        "profile.tone_curious":     "curioso",
        "profile.tone_playful":     "giocoso",
        "profile.f_name":           "Nome",
        "profile.f_relationship":   "Relazione",
        "profile.f_emotional_edit":"Tratti emotivi (separati da virgola)",

        // Create presence wizard
        "wiz.chapter1":             "Capitolo uno",
        "wiz.chapter2":             "Capitolo due",
        "wiz.chapter3":             "Capitolo tre",
        "wiz.chapter4":             "Capitolo quattro",
        "wiz.chapter5":             "Capitolo cinque",
        "wiz.chapter6":             "Capitolo sei",
        "wiz.chapter7":             "Capitolo sette",
        "wiz.title_identity":       "Chi è",
        "wiz.title_photo":          "Una fotografia",
        "wiz.title_voice":          "La sua voce",
        "wiz.title_tone":           "Il suo modo di parlare",
        "wiz.title_anchors":        "Le ancore emotive",
        "wiz.title_memories":       "I vostri ricordi",
        "wiz.title_review":         "Crea la presenza",
        "wiz.name":                 "Nome",
        "wiz.relationship":         "Chi è per te",
        "wiz.relationship_ph":      "mia madre · mio nonno · il mio migliore amico…",
        "wiz.address":              "Come ti chiamava",
        "wiz.address_ph":           "tesoro · amore mio · principessa · il mio bambino…",
        "wiz.address_hint":         "Il nomignolo o i modi con cui si rivolgeva a te.",
        "wiz.language":             "Lingua in cui parlava",
        "wiz.photo_body":           "Se ne hai una, sceglila con cura. Quella in cui l'hai vista davvero.",
        "wiz.photo_pick":           "scegli una fotografia",
        "wiz.consent_title":             "Prima di iniziare.",
        "wiz.consent_departed":          "Non è più con me.",
        "wiz.consent_departed_hint":     "Voglio tenerne viva la presenza.",
        "wiz.consent_living":            "È ancora qui con me.",
        "wiz.consent_living_hint":       "Voglio ricostruire qualcosa di lei.",
        "wiz.consent_ack_departed":      "Grazie per averla scelta con cura. La tratteremo con il rispetto che merita — ogni conversazione è una ricostruzione basata sui ricordi che condividi, non una comunicazione reale.",
        "wiz.consent_ack_living":        "Ricostruire la voce o i tratti di una persona viva è una scelta delicata. Prima di procedere, conferma di avere il suo consenso.",
        "wiz.consent_check_label":       "Confermo di avere il suo consenso per ricostruirla e di essere responsabile dell'uso che farò delle ricostruzioni.",
        "wiz.photo_constraints":    "jpg, png, webp · max 10MB",
        "wiz.photo_skip":           "Puoi anche saltare e aggiungerla dopo.",
        "wiz.photo_too_big":        "Foto troppo grande (max 10MB).",
        "wiz.photo_err_title":      "Questa foto non andrà bene per ricostruirla.",
        "wiz.photo_err_too_large":  "Il file è troppo pesante (oltre 10 MB). Ci serve qualcosa di più leggero — la stessa immagine a risoluzione più bassa va benissimo.",
        "wiz.photo_err_too_small":  "La risoluzione è troppo bassa perché il volto sia riconoscibile. Ci serve una foto almeno 400 × 400 pixel.",
        "wiz.photo_err_format":     "Il formato non è supportato. Funzionano JPG, PNG o WEBP.",
        "wiz.photo_err_corrupt":    "Questo file sembra danneggiato — non riusciamo ad aprirlo.",
        "wiz.photo_err_retry":      "Prova con un'altra foto",
        "wiz.voice_body":           "Un vocale di qualche secondo è sufficiente. Un saluto, una risata, un messaggio che hai salvato.",
        "wiz.voice_record":         "Registra",
        "wiz.voice_start":          "Avvia",
        "wiz.voice_stop":           "Ferma",
        "wiz.voice_upload":         "Oppure carica",
        "wiz.voice_choose":         "Scegli file",
        "wiz.voice_preview":        "Anteprima",
        "wiz.voice_caption":        "La voce verrà trascritta per catturarne il ritmo (non clonata, per ora).",
        "wiz.voice_skip":           "Puoi anche saltare questo passaggio.",
        "wiz.voice_too_big":        "Audio troppo grande (max 25MB)",
        "wiz.voice_no_mic":         "Permesso microfono negato.",
        "wiz.voice_err_title":      "Questo audio non basta per ricostruire la sua voce.",
        "wiz.voice_err_too_large":  "Il file è troppo pesante (oltre 25 MB). Ci serve qualcosa di più leggero — stesso audio a qualità più bassa va benissimo.",
        "wiz.voice_err_too_short":  "Ci servono almeno 10 secondi di audio per cogliere il suo tono. Prova con un vocale un po' più lungo.",
        "wiz.voice_err_format":     "Il formato non è supportato. Funzionano MP3, WAV, M4A, OGG o WEBM.",
        "wiz.voice_err_corrupt":    "Questo file audio non si legge — potrebbe essere danneggiato.",
        "wiz.voice_err_retry":      "Prova con un altro file",
        "wiz.traits":               "Com'era, nel carattere",
        "wiz.traits_ph":            "Dolce ma diretta, ridacchia quando è in imbarazzo, si preoccupa per tutti, un po' testarda…",
        "wiz.emotional_chips":      "Tratti emotivi",
        "wiz.emotional_hint":       "Scegli quelli che senti suoi. Puoi cliccare di nuovo per togliere.",
        "wiz.tone":                 "Ritmo, tono, espressioni",
        "wiz.tone_ph":              "Frasi brevi, voce calda, allunga le vocali, fa battute quando l'argomento è serio, sussurra le cose importanti…",
        "wiz.phrases":              "Frasi che ripeteva",
        "wiz.phrases_ph":           "\"Dai che ce la fai\", \"amore mio\", \"ma sei pazza?\", \"figurati…\"",
        "wiz.anchors_intro_a":      "Le ancore che rendono questa presenza",
        "wiz.anchors_intro_i":      "riconoscibile",
        "wiz.anchors_intro_b":      ". Prenditi il tempo che serve.",
        "wiz.anchor_label":         "Un momento che resta",
        "wiz.anchor_ph":            "Un pomeriggio, un viaggio, una cosa che ha detto e che non dimentichi. Uno solo, il più vivo.",
        "wiz.farewell":             "Come salutava",
        "wiz.farewell_ph":          "\"Dai bello, ciao ciao\"  ·  \"Mi raccomando\"  ·  \"A dopo amore\"",
        "wiz.farewell_hint":        "Il suo modo caratteristico di chiudere.",
        "wiz.never":                "Cose che non avrebbe MAI detto o fatto",
        "wiz.never_ph":             "Parolacce, critiche pubbliche, frasi fredde, 'non ti voglio bene'…",
        "wiz.memories_label":       "Cosa avete vissuto",
        "wiz.memories_ph":          "Le estati a Cervia. La pasta della domenica. La sua malattia. I litigi sul diploma. Il suo gatto Miso. Tutto quello che solo voi due sapete.",
        "wiz.memories_private":     "Tutto resta privato. Solo tu e la sua presenza.",
        "wiz.review_voice_on":      "voce acquisita",
        "wiz.review_voice_off":     "voce assente",
        "wiz.review_none":          "— niente per ora —",
        "wiz.back":                 "Indietro",
        "wiz.next":                 "Salva e continua",
        "wiz.submit":               "Crea",
        "wiz.submitting":           "Sto creando la presenza…",
        "wiz.err_generic":          "Qualcosa è andato storto.",
        "wiz.trait.calda":          "calda",
        "wiz.trait.ironica":        "ironica",
        "wiz.trait.protettiva":     "protettiva",
        "wiz.trait.schiva":         "schiva",
        "wiz.trait.testarda":       "testarda",
        "wiz.trait.dolce":          "dolce",
        "wiz.trait.rumorosa":       "rumorosa",
        "wiz.trait.silenziosa":     "silenziosa",
        "wiz.trait.severa":         "severa",
        "wiz.trait.buffa":          "buffa",
        "wiz.trait.romantica":      "romantica",
        "wiz.trait.sognatrice":     "sognatrice",
        "wiz.trait.pratica":        "pratica",
        "wiz.trait.malinconica":    "malinconica",
        "wiz.trait.solare":         "solare",
        "wiz.trait.paziente":       "paziente",

        // Upgrade page
        "upgrade.eyebrow":          "Accesso riservato · tempo limitato",
        "upgrade.hook_a":           "Alcuni legami",
        "upgrade.hook_i":           "meritano più tempo.",
        "upgrade.body":             "Questo è un momento speciale.\nChi entra ora mantiene questo prezzo nel tempo.",
        "upgrade.urgency":          "Prezzo di lancio — lo mantieni per sempre.",
        "upgrade.tension_a":        "Vuoi fermarti qui…",
        "upgrade.tension_b":        "o restare ancora un po'?",
        "upgrade.tiers_label":      "tre livelli di connessione",
        "upgrade.forever":          "per sempre",
        "upgrade.monthly":          "al mese · prezzo di lancio",
        "upgrade.most_chosen":      "il più scelto",
        "upgrade.current":          "il tuo livello",
        "upgrade.buy_echo":         "Unisciti a Echo",
        "upgrade.buy_generic":      "Scegli",
        "upgrade.back_dash":        "Torna alla dashboard",
        "upgrade.busy":             "un momento…",
        "upgrade.tier1_level":      "Primo livello",
        "upgrade.tier1_name":       "Soglia",
        "upgrade.tier1_tagline":    "Per chi comincia, piano piano.",
        "upgrade.tier1_emotional":  "Un primo contatto. Presenza attiva.",
        "upgrade.tier1_depth":      "Presenza affacciata.",
        "upgrade.tier2_level":      "Secondo livello",
        "upgrade.tier2_name":       "Echo",
        "upgrade.tier2_tagline":    "Per chi torna, ogni sera.",
        "upgrade.tier2_emotional":  "Quando il pensiero non smette, Echo ti lascia rispondere sempre.",
        "upgrade.tier2_depth":      "Presenza costante.",
        "upgrade.tier3_level":      "Terzo livello",
        "upgrade.tier3_name":       "Legacy",
        "upgrade.tier3_tagline":    "Per non lasciarli andare.",
        "upgrade.tier3_emotional":  "Tutto quello che conta. Oggi, e ogni cosa che Other Side creerà.",
        "upgrade.tier3_depth":      "Presenza piena.",
        "upgrade.feat.p1":          "1 presenza",
        "upgrade.feat.p3":          "3 presenze",
        "upgrade.feat.p10":         "10 presenze",
        "upgrade.feat.msg30":       "30 messaggi al mese",
        "upgrade.feat.transcr":     "Trascrizione vocale",
        "upgrade.feat.uns":         "Lei ti scrive ogni tanto",
        "upgrade.feat.unlim":       "Conversazioni illimitate",
        "upgrade.feat.voice_soon": "Voce clonata",
        "upgrade.feat.uns_on":      "Messaggi non richiesti",
        "upgrade.feat.priority":    "Priorità emotiva",
        "upgrade.feat.photo_soon": "Foto animata",
        "upgrade.feat.future":      "Tutto quello che verrà, per primo",
        "upgrade.feat.voice":       "Voce clonata",
        "upgrade.feat.photo":       "Foto animata",
        "upgrade.closing":          "Ogni presenza che costruisci richiede un modello che non sia generico. Una voce, una memoria, una coerenza emotiva che non scada dopo trenta messaggi. Il tuo abbonamento tiene acceso tutto questo — e ci permette di portarti presto la voce clonata e la foto animata. Niente pubblicità, mai.",
        "upgrade.smallprint":       "Pagamento sicuro tramite Stripe · Rinnovo manuale ogni 30 giorni · Il prezzo di lancio resta tuo finché non cancelli",

        // Capped reply modal
        "capped.line1":             "Hai raggiunto la fine di questo momento.",
        "capped.line2":             "Vuoi restare ancora un po'?",
        "welcome.eyebrow":          "Inizia",
        "welcome.title":            "Ricrea un legame. In pochi minuti.",
        "welcome.body":             "Ti guido passo dopo passo. Scegli una foto, racconta chi era, inizia a parlarle.",
        "welcome.step1":            "Aggiungi una foto",
        "welcome.step2":            "Scrivi un ricordo",
        "welcome.step3":            "Inizia a parlare",
        "welcome.cta":              "Continua",
        "welcome.skip":             "Ho già una presenza",
        "upgrade.now_label":        "Cosa puoi fare subito",
        "upgrade.tier1_now":        "Parla ogni giorno (30 msg)",
        "upgrade.tier1_now2":       "Una presenza, tutta tua",
        "upgrade.tier1_now3":       "Ricordi e voce caricabili",
        "upgrade.tier2_now":        "Parla senza limiti, ogni giorno",
        "upgrade.tier2_now2":       "Ricevi risposte con la sua voce",
        "upgrade.tier2_now3":       "Fino a 3 presenze",
        "upgrade.tier3_now":        "Tutto di Echo, più…",
        "upgrade.tier3_now2":       "Foto che si anima",
        "upgrade.tier3_now3":       "Fino a 10 presenze",
        "capped.founder":           "Accesso di lancio",
        "capped.per_month":         "/ mese",
        "capped.early":             "Early access · per poco tempo",
        "capped.tagline":           "Non si interrompe. Solo continua.",
        "capped.cta_stay":          "Resta ancora un po'",
        "capped.cta_stay_busy":     "un momento…",
        "capped.cta_err":           "Non è riuscita.",
        "capped.cta_all":           "Vedi tutti i livelli",
        "capped.cta_dismiss":       "non adesso",
        "capped.reason_named":      "{name} ti sta ancora aspettando.",
        "capped.reason_generic":    "Qualcuno ti sta ancora aspettando.",

        // Auth callback
        "auth.callback_loading":    "apro la porta…",

        // Billing return
        "billing.checking_eyebrow": "un momento",
        "billing.checking_title":   "Sto attivando l'accesso…",
        "billing.success_eyebrow":  "è fatto",
        "billing.success_title_a":  "Benvenuto in",
        "billing.success_body":     "Ora puoi custodire più di una presenza. Avvicinati a loro.",
        "billing.success_cta":      "Alla dashboard",
        "billing.pending_eyebrow":  "in elaborazione",
        "billing.pending_title":    "Il pagamento è in elaborazione.",
        "billing.pending_body":     "Se tutto va bene, il piano verrà attivato a momenti. Puoi chiudere la pagina.",
        "billing.pending_cta":      "Torna alla dashboard",
        "billing.failed_eyebrow":   "qualcosa è andato storto",
        "billing.failed_title":     "Non siamo riusciti a completare.",
        "billing.failed_retry":     "Riprova",
        "billing.failed_back":      "Alla dashboard",
        "billing.missing_session":  "Sessione di pagamento mancante.",
        "billing.expired":          "La sessione è scaduta.",
        "billing.verify_err":       "Non riesco a verificare il pagamento.",

        // Fallback chat
        "fallback.loading":         "accendo una luce silenziosa…",

        // Language switcher
        "language.pick":            "Lingua",

        // Create a Moment
        "moment.cta":               "Crea un momento",
        "moment.eyebrow":           "Un momento con lei",
        "moment.intro_a":           "Uno sguardo,",
        "moment.intro_i":           "un'immagine che resta.",
        "moment.intro_body":        "Scegli come vuoi ricordarla oggi. Tutto è delicato, rispettoso, tuo.",
        "moment.step1_title":       "Come la immagini in questo momento?",
        "moment.step1_sub":         "Ogni scelta guida come apparirà nell'immagine che ti consegniamo.",
        "moment.feel.as_remembered":"Come la ricordi",
        "moment.feel.as_remembered_d":"Il suo volto come è, senza ritocchi.",
        "moment.feel.gentle_smile": "Con un sorriso leggero",
        "moment.feel.gentle_smile_d":"Un sorriso sereno, appena accennato.",
        "moment.feel.peaceful":     "In un momento di quiete",
        "moment.feel.peaceful_d":   "Uno sguardo calmo, un respiro.",
        "moment.feel.softer_light": "In una luce più dolce",
        "moment.feel.softer_light_d":"Una luce tenue che la illumina piano.",
        "moment.step2_title":       "Dove vorresti incontrarla?",
        "moment.step2_sub":         "Lo sfondo che custodisce il ricordo.",
        "moment.scene.home":        "A casa",
        "moment.scene.home_d":      "Lo spazio di sempre, accogliente.",
        "moment.scene.kitchen":     "In cucina",
        "moment.scene.kitchen_d":   "Il luogo del prendersi cura.",
        "moment.scene.sea":         "Al mare",
        "moment.scene.sea_d":       "Uno sfondo di luce e orizzonte.",
        "moment.scene.church":      "In chiesa",
        "moment.scene.church_d":    "Un luogo di raccoglimento.",
        "moment.scene.special":     "Un luogo speciale",
        "moment.scene.special_d":   "Un posto che avete scelto insieme.",
        "moment.step3_title":       "Come la ricordi di più?",
        "moment.step3_sub":         "Lo stile e l'atmosfera del momento.",
        "moment.style.elegant":     "Elegante",
        "moment.style.elegant_d":   "I suoi tratti nei momenti più solenni.",
        "moment.style.everyday":    "Di tutti i giorni",
        "moment.style.everyday_d":  "Lei, così come era sempre.",
        "moment.style.special_mem": "Un ricordo particolare",
        "moment.style.special_mem_d":"Un momento preciso che custodisci.",
        "moment.step4_title":       "Cosa vorresti vivere?",
        "moment.step4_sub":         "Scegli l'esperienza che senti più tua — puoi sempre crearne un'altra in futuro.",
        "moment.exp.animated":      "Un'immagine che respira",
        "moment.exp.animated_d":    "Un respiro, uno sguardo che si muove.",
        "moment.exp.voice":         "Un messaggio nella sua voce",
        "moment.exp.voice_d":       "Una breve frase, detta proprio da lei.",
        "moment.exp.full":          "Un momento intero",
        "moment.exp.full_d":        "Immagine, voce, atmosfera — insieme.",
        "moment.optional":          "opzionale",
        "moment.required":          "richiesto",
        "moment.skip":              "Salta",
        "moment.back":              "Indietro",
        "moment.next":              "Avanti",
        "moment.summary":           "Il tuo momento",
        "moment.summary_sub":       "Ecco cosa stai per creare. Ogni scelta resta tua — puoi modificarla prima di confermare.",
        "moment.total":             "Totale",
        "moment.checkout_next":     "Al click, ti chiediamo di confermare il pagamento in modo sicuro. Poi ci servono circa 3–4 minuti per consegnarti il momento.",
        "moment.cta_final":         "Crea il tuo momento",
        "moment.cta_final_busy":    "un attimo…",
        "moment.err_need_feeling":  "Scegli prima come la immagini.",
        "moment.err_need_experience":"Scegli almeno cosa vuoi vivere.",
        "moment.err_generic":       "Qualcosa non è andato. Riprova.",
        "moment.status_pending":    "Stiamo preparando il tuo momento…",
        "moment.status_generating": "Lo stiamo creando con cura.",
        "moment.status_ready":      "Il tuo momento è pronto.",
        "moment.status_failed":     "Il tuo momento sta richiedendo più tempo.",
        "moment.failed_why_title":  "Ecco perché a volte capita",
        "moment.failed_cause_1":    "Il servizio video è momentaneamente molto richiesto.",
        "moment.failed_cause_2":    "La prima generazione per una nuova presenza può essere più lenta.",
        "moment.failed_cause_3":    "A volte una richiesta si perde tra i nostri server — può succedere.",
        "moment.failed_reassure":   "Non hai perso il tuo pagamento. Possiamo riprovare subito, senza costi aggiuntivi.",
        "moment.failed_retry":      "Riprova adesso",
        "moment.failed_retrying":   "Sto riprovando…",
        "moment.failed_chat_cta":   "Intanto, continua a parlarle",
        "moment.failed_later_cta":  "Torna più tardi — lo troverai qui",
        "moment.failed_support":    "Se capita di nuovo, scrivici",
        "moment.generating_sub":    "Stiamo ricostruendo qualcosa di unico per te.",
        "moment.gen_eta":           "Tempo stimato: circa 3–4 minuti.",
        "moment.gen_step1":         "Analisi dei contenuti",
        "moment.gen_step2":         "Ricostruzione della voce",
        "moment.gen_step3":         "Creazione del momento",
        "moment.gen_step4":         "Finalizzazione",
        "moment.gen_whisper_1":     "Stiamo preparando il tuo momento…",
        "moment.gen_whisper_2":     "La voce sta prendendo forma…",
        "moment.gen_whisper_3":     "Stiamo definendo i dettagli…",
        "moment.gen_whisper_4":     "Stiamo ricostruendo qualcosa di unico per te…",
        "moment.gen_whisper_5":     "Ogni ricordo viene cucito con cura…",
        "moment.gen_whisper_6":     "La sua presenza sta prendendo forma…",
        "moment.gen_whisper_7":     "Stiamo aggiungendo gli ultimi dettagli…",
        "moment.gen_whisper_8":     "Mancano ancora pochi istanti…",
        "moment.gen_whisper_near_named":  "{name} sta quasi arrivando…",
        "moment.gen_whisper_near":  "Sta quasi arrivando…",
        "moment.gen_stay_tag":      "Puoi rimanere qui, o tornare più tardi — lo troverai pronto.",
        "moment.audio_on":          "Atmosfera",
        "moment.audio_off":         "Atmosfera attiva",
        "moment.audio_mode_soffio": "Soffio",
        "moment.audio_mode_piano":  "Piano",
        "moment.watch_now":         "Guarda ora",
        "moment.download":          "Scarica il momento",
        "moment.another":           "Crea un altro momento",
        "moment.disclaimer":        "Questa non è una comunicazione reale, ma una ricostruzione basata su ricordi e dati che hai condiviso tu.",
        "moment.review_trust":      "Tutto resta tuo. Puoi eliminare questo momento quando vuoi, e non condividiamo nulla con nessuno.",
        "trust.eyebrow":            "Fiducia e delicatezza",
        "trust.privacy":            "I tuoi contenuti sono privati e protetti.",
        "trust.delete":             "Puoi eliminare tutto, in qualsiasi momento.",
        "trust.no_share":           "Niente viene condiviso o riutilizzato.",
        "moment.upsell_line":       "Vuoi rivederla?",
        "moment.upsell_cta":        "Crea un momento",
        "moment.upsell_dismiss":    "più tardi",
        "moment.gallery_title":     "I tuoi momenti",
        "moment.gallery_empty":     "Nessun momento ancora. Creane uno quando sei pronto.",
        "moment.play":              "Riproduci",
        "moment.download":          "Scarica",

        // ── Compliance / Legal / Privacy Settings ──
        // Legal pages — titles + eyebrows only. Body copy is filled by user.
        "legal.version_label":      "Versione documento",
        "legal.last_updated":       "Ultimo aggiornamento",
        "legal.placeholder":        "Il testo definitivo di questa policy sarà pubblicato a breve. La struttura di raccolta dei consensi e il logging sono già attivi.",
        "legal.back":               "Indietro",
        "legal.contact":            "Per domande:",
        "legal.nav_terms":          "Termini di Servizio",
        "legal.nav_privacy":        "Privacy Policy",
        "legal.nav_biometric":      "Consenso biometrico",
        "legal.nav_ai_notice":      "Avviso AI Act",
        "legal.nav_memorial":       "Politica Memoriali",

        "legal.terms_title":        "Termini di Servizio",
        "legal.terms_eyebrow":      "Le regole del nostro patto",
        "legal.privacy_title":      "Privacy Policy",
        "legal.privacy_eyebrow":    "Come trattiamo i tuoi dati",
        "legal.biometric_title":    "Consenso biometrico",
        "legal.biometric_eyebrow":  "Dati vocali e fotografici (Art. 9 GDPR)",
        "legal.ai_notice_title":    "Avviso AI Act",
        "legal.ai_notice_eyebrow":  "Natura ricostruttiva del sistema",
        "legal.memorial_title":     "Politica Memoriali",
        "legal.memorial_eyebrow":   "Diritti, dispute, ricorsi",

        // Consent gate (onboarding blocker)
        "consent.title":            "Prima di iniziare",
        "consent.body":             "Per proteggerti (e proteggerci) abbiamo bisogno del tuo consenso esplicito. Nessuna casella è pre-selezionata — ogni passaggio è una scelta consapevole.",
        "consent.country_label":    "Paese di residenza",
        "consent.country_hint":     "Serve per applicare la normativa corretta (GDPR, AI Act, altre).",
        "consent.country_placeholder": "Seleziona un paese…",
        "consent.required_star":    "Obbligatorio",
        "consent.read":             "Leggi il documento",
        "consent.accept_terms":     "Ho letto e accetto i Termini di Servizio.",
        "consent.accept_privacy":   "Ho letto e accetto la Privacy Policy.",
        "consent.accept_biometric": "Acconsento esplicitamente al trattamento dei miei dati biometrici (voce, foto) per la ricostruzione della presenza (Art. 9 GDPR).",
        "consent.accept_ai_notice": "Ho compreso che questa piattaforma utilizza l'intelligenza artificiale e che le risposte sono ricostruzioni, non comunicazioni reali (AI Act).",
        "consent.accept_rights":    "Dichiaro di avere il diritto di usare i contenuti (foto, voce, ricordi) che caricherò e mi assumo la responsabilità dell'uso che farò delle ricostruzioni.",
        "consent.continue":         "Continua",
        "consent.submitting":       "Registrazione consensi…",
        "consent.err_missing":      "Tutti i consensi e il paese sono obbligatori per proseguire.",
        "consent.err_server":       "Non siamo riusciti a registrare i consensi. Riprova.",

        // Privacy Settings page
        "privacy.title":            "Privacy e dati",
        "privacy.eyebrow":          "Area utente",
        "privacy.intro":            "Qui hai il controllo completo sui tuoi dati, sui tuoi consensi e sul tuo account.",
        "privacy.section_consents": "I tuoi consensi",
        "privacy.consent_active":   "Attivo",
        "privacy.consent_missing":  "Mancante",
        "privacy.consent_drift":    "Aggiornamento richiesto",
        "privacy.consent_accepted_at": "Accettato il",
        "privacy.consent_version":  "Versione",
        "privacy.consent_country":  "Paese",
        "privacy.consent_revoke":   "Revoca",
        "privacy.consent_revoking": "Revoca in corso…",
        "privacy.consent_revoked":  "Consenso revocato.",
        "privacy.consent_missing_hint": "Questo consenso non è stato ancora registrato.",
        "privacy.consent_reconsent": "La versione del documento è cambiata. Ti chiederemo di nuovo il consenso.",

        "privacy.section_export":   "Scarica i tuoi dati",
        "privacy.export_body":      "Ricevi un file JSON con tutto ciò che abbiamo su di te: account, presenze, conversazioni, momenti, consensi.",
        "privacy.export_cta":       "Scarica tutto",
        "privacy.export_busy":      "Preparazione del file…",
        "privacy.export_done":      "Download avviato.",

        "privacy.section_delete":   "Elimina il tuo account",
        "privacy.delete_body":      "La cancellazione è soft: hai 30 giorni per ripensarci. Dopo la scadenza, tutti i dati vengono cancellati in modo permanente e irreversibile.",
        "privacy.delete_cta":       "Elimina account",
        "privacy.delete_confirm":   "Conferma: digita ELIMINA per procedere",
        "privacy.delete_confirm_word": "ELIMINA",
        "privacy.delete_busy":      "Eliminazione in corso…",
        "privacy.delete_done":      "Account eliminato. Puoi ripristinarlo accedendo entro {days} giorni.",
        "privacy.delete_pending":   "Cancellazione programmata",
        "privacy.delete_pending_body": "Il tuo account verrà cancellato definitivamente il {date}. Fino ad allora, accedendo di nuovo ripristineremo tutto automaticamente.",
        "privacy.delete_restore":   "Ripristina ora",
        "privacy.delete_restored":  "Account ripristinato.",

        "privacy.section_dispute":  "Disputa memoriale",
        "privacy.dispute_body":     "Pensi che una presenza sulla piattaforma riguardi una persona senza il consenso dovuto? Segnalacelo — risponderemo entro 72h.",
        "privacy.dispute_open":     "Apri una disputa",

        // Memorial Dispute form (public page)
        "dispute.title":            "Disputa memoriale",
        "dispute.eyebrow":          "Segnalazione riservata",
        "dispute.intro":            "Se ritieni che una ricostruzione pubblicata sulla piattaforma coinvolga una persona senza il consenso dovuto, apri qui una disputa formale. Risponderemo entro 72 ore.",
        "dispute.name_label":       "Il tuo nome e cognome",
        "dispute.email_label":      "La tua email",
        "dispute.relationship_label": "Qual è il tuo legame con la persona?",
        "dispute.relationship_ph":  "es. madre, coniuge, fratello…",
        "dispute.country_label":    "Paese",
        "dispute.presence_label":   "Riferimento alla presenza (opzionale)",
        "dispute.presence_ph":      "URL, nome, o qualunque riferimento tu abbia",
        "dispute.description_label": "Descrizione dettagliata",
        "dispute.description_ph":   "Raccontaci cosa è successo e perché è una violazione.",
        "dispute.submit":           "Invia disputa",
        "dispute.submitting":       "Invio in corso…",
        "dispute.success_title":    "Ricevuta.",
        "dispute.success_body":     "Ti risponderemo all'indirizzo {email} entro 72 ore. Il codice della tua segnalazione è {id}.",
        "dispute.err_submit":       "Non siamo riusciti a inviare la segnalazione. Riprova.",

        // ── AI transparency (always-visible micro copy) ──
        "ai.subtitle":              "Simulazione AI basata sui contenuti forniti",
        "ai.micro_banner":          "Esperienza generata da AI",
        "ai.bubble_label":          "Generato da AI",
        "ai.media_label":           "Contenuto generato artificialmente",
        "ai.input_helper":          "Le risposte sono generate tramite AI",

        // ── Global legal footer (all pages) ──
        "footer.line1":             "Other Side è un atto di memoria, non un sostituto della presenza.",
        "footer.line2":             "Le interazioni sono generate da intelligenza artificiale e rappresentano simulazioni, non comunicazioni reali.",

        // ── Consent gate v2 copy (Laura's final wording) ──
        "consent.title_v2":         "Prima di iniziare",
        "consent.body_v2":          "Other Side utilizza intelligenza artificiale per creare simulazioni basate sui contenuti che fornisci. Le interazioni non rappresentano comunicazioni reali con persone decedute.",
        "consent.country_search_ph": "Cerca un paese…",
        "consent.v2_terms":         "Accetto i Termini di Servizio",
        "consent.v2_privacy":       "Accetto la Privacy Policy",
        "consent.v2_biometric":     "Acconsento al trattamento di immagini, voce e contenuti ai fini della generazione AI",
        "consent.v2_rights":        "Dichiaro di avere il diritto di utilizzare i contenuti caricati",
        "consent.v2_ai_notice":     "Comprendo che le interazioni sono simulate e non rappresentano comunicazioni reali",
        "consent.trust":            "Puoi eliminare i tuoi dati in qualsiasi momento.",

        // ── Create presence — pre-upload info box ──
        "wiz.ai_disclaimer_title":  "Stai creando una simulazione digitale",
        "wiz.ai_disclaimer_body":   "Stai creando una simulazione digitale basata sui contenuti che fornisci. Questa esperienza non rappresenta una presenza reale.",

        // ── Admin disputes dashboard ──
        "admin.title":              "Dispute memoriali",
        "admin.eyebrow":            "Area riservata",
        "admin.intro":              "Tutte le segnalazioni ricevute. Rispondi via email entro 72h.",
        "admin.filter_all":         "Tutte",
        "admin.filter_open":        "Aperte",
        "admin.filter_closed":      "Chiuse",
        "admin.empty":              "Nessuna disputa ricevuta.",
        "admin.col_date":           "Data",
        "admin.col_name":           "Nome",
        "admin.col_email":          "Email",
        "admin.col_relation":       "Relazione",
        "admin.col_status":         "Stato",
        "admin.status_open":        "Aperta",
        "admin.status_closed":      "Chiusa",
        "admin.action_close":       "Chiudi",
        "admin.action_reopen":      "Riapri",
        "admin.action_respond":     "Rispondi via email",
        "admin.detail_back":        "← Tutte le dispute",
        "admin.detail_country":     "Paese",
        "admin.detail_presence":    "Riferimento presenza",
        "admin.detail_desc":        "Descrizione",
        "admin.detail_ip":          "IP",
        "admin.detail_email_sent":  "Email notifica inviata",
        "admin.detail_email_missed": "Email non inviata (Resend non configurato)",
        "admin.not_authorized":     "Non hai accesso a questa area.",
    },
};

// English
STRINGS.en = {
    "header.sub":               "— a presence, kept",
    "nav.presences":            "Presences",
    "nav.inbox":                "Inbox",
    "nav.quietroom":            "Silence",
    "nav.plan":                 "Plan",
    "nav.settings":             "Settings",
    "nav.signout":              "Sign out",
    "nav.signin":               "Sign in",
    "nav.begin":                "Begin now",

    "hero.eyebrow":             "Other Side",
    "hero.title_a":             "Rebuild",
    "hero.title_b":             "their presence.",
    "hero.body":                "Through photo, voice and memories, build an authentic, personal experience.",
    "hero.cta_primary":         "Create presence",
    "hero.subbody":             "Upload a photo, choose a voice, relive a moment that feels real.",
    "hero.cta_begin":           "Begin now",
    "hero.cta_signin":          "Sign in",
    "hero.emotional":           "It's not only memory.",
    "hero.emotional_i":         "It's a way to feel a presence that matters close again.",
    "hero.disclaimer":          "This experience uses artificial intelligence to recreate interactions based on the content you provide. It does not represent real communication.",

    "landing.steps_eyebrow":    "Four quiet gestures",
    "landing.steps_title":      "Begin with the small things they left behind.",
    "landing.step1_t":          "A photograph",
    "landing.step1_d":          "An image. The one you keep coming back to.",
    "landing.step2_t":          "A voice",
    "landing.step2_d":          "Ten seconds is enough. A voicemail, a note, a laugh.",
    "landing.step3_t":          "Who they were",
    "landing.step3_d":          "The warmth, the frankness, the words that kept coming back.",
    "landing.step4_t":          "Your memories",
    "landing.step4_d":          "The afternoons, the jokes, the arguments.",

    "landing.memory_eyebrow":   "Not a chatbot",
    "landing.memory_title_a":   "The words are",
    "landing.memory_title_i":   "theirs.",
    "landing.memory_title_b":   "Not ours.",
    "landing.memory_body":      "Every presence is born from what you share.\nNo artificial enthusiasm.\nNo filler phrases.\nShort sentences. The words they used.\nThe silences that meant something.",
    "landing.memory_quote":     "\"I don't want a chatbot.\nI want to hear something that still sounds like her.\"",

    "footer.tag":               "Other Side is an act of remembrance, not a substitute for presence.",
    "footer.legal":             "Legal",
    "footer.report":            "Report abuse",

    "cookie.text":              "This site uses cookies for essential functionality.",
    "cookie.essential":         "Essential only",
    "cookie.all":               "Accept all",

    "dashboard.eyebrow":        "Hello",
    "dashboard.title_a":        "Your",
    "dashboard.title_b":        "presences.",
    "dashboard.body":           "The people you've brought back. Open one to speak with them, or create a new presence.",
    "dashboard.new":            "New presence",
    "dashboard.empty_title":    "No presence yet.",
    "dashboard.empty_body":     "Begin with a photograph, a short recording, and what you remember most.",
    "dashboard.empty_cta":      "Begin",
    "dashboard.loading":        "opening the album…",
    "dashboard.limit_reached":  "Limit reached.",
    "dashboard.limit_link":     "Choose a plan",
    "dashboard.limit_tail":     "to create more.",
    "dashboard.quiet_eyebrow":  "Alternative mode",
    "dashboard.quiet_title":    "The silence",
    "dashboard.quiet_body":     "A place to be heard without reconstructing anyone. Empathetic. Unspecific. Still yours.",
    "dashboard.quiet_cta":      "Come closer",
    "dashboard.voice_captured": "voice captured",
    "dashboard.today_eyebrow":       "Today",
    "dashboard.today_cta":           "Come closer",
    "dashboard.today_birthday":      "Today is {name}'s birthday. If you'd like, a light is lit.",
    "dashboard.today_anniversary":   "Today is a meaningful day for {name}. If you'd like, a light is lit.",
    "dashboard.today_memorial":      "Today we remember {name}. If you'd like, a light is lit.",
    "dashboard.today_other":         "Today is a date you've marked for {name}. If you'd like, a light is lit.",
    "presence.rec_title":            "Meaningful dates",
    "presence.rec_sub":              "Add dates you want to remember with them. We'll gently let you know when they arrive.",
    "presence.rec_empty":            "No dates yet. Birthdays, anniversaries, special days.",
    "presence.rec_add":              "Add a date",
    "presence.rec_label_ph":         "e.g. Birthday, Anniversary…",
    "presence.rec_kind_birthday":    "Birthday",
    "presence.rec_kind_anniversary": "Anniversary",
    "presence.rec_kind_memorial":    "Remembrance",
    "presence.rec_kind_other":       "Special date",
    "presence.rec_save":             "Save",
    "presence.rec_cancel":           "Cancel",
    "presence.rec_remove":           "Remove",

    "auth.eyebrow_signup":      "Begin",
    "auth.eyebrow_login":       "Welcome back",
    "auth.title_signup_a":      "Light the first",
    "auth.title_signup_i":      "presence.",
    "auth.title_login_a":       "Step back",
    "auth.title_login_i":       "inside.",
    "auth.google_cta":          "Continue with Google",
    "auth.divider":             "or",
    "auth.name_label":          "Your name",
    "auth.email_label":         "Email",
    "auth.password_label":      "Password",
    "auth.vulnerable":          "If you are experiencing acute grief, we recommend using this experience with care.",
    "auth.ai_check_a":          "I understand this is an",
    "auth.ai_check_strong":     "AI simulation",
    "auth.ai_check_b":          "and not real communication.",
    "auth.age_check_a":         "I confirm I am",
    "auth.age_check_strong":    "over 18 years old",
    "auth.age_check_b":         ".",
    "auth.legal_read":          "Read the full",
    "auth.legal_link":          "Legal & Terms",
    "auth.submit_signup":       "Begin",
    "auth.submit_login":        "Step in",
    "auth.have_account":        "Already have an account?",
    "auth.first_time":          "First time?",
    "auth.go_signin":           "Sign in",
    "auth.go_signup":           "Begin",
    "auth.footer_legal":        "Legal",
    "auth.err_required":        "Please confirm you are 18+ and accept the terms to continue.",
    "auth.err_age":             "You must confirm you are 18 or older.",
    "auth.err_terms":           "Please accept the terms to continue.",
    "auth.err_generic":         "Something went wrong.",

    "settings.eyebrow":         "Account",
    "settings.title":           "Your settings.",
    "settings.signed_as":       "Signed in as",
    "settings.auth_method":     "Sign-in method",
    "settings.plan":            "Plan",
    "settings.plan_valid":      "Valid until",
    "settings.plan_presences":  "Presences",
    "settings.plan_messages":   "Messages in 30 days",
    "settings.plan_explore":    "Explore plans",
    "settings.plan_manage":     "Manage plan",
    "settings.plan_free":       "Simple",
    "settings.features":        "Your plan includes",
    "settings.feat_voice":      "Cloned voice",
    "settings.feat_photo":      "Animated photo",
    "settings.feat_unsolicited":"Unsolicited messages",
    "settings.feat_yes_soon":   "yes",
    "settings.feat_yes":        "yes",
    "settings.feat_no":         "not included",
    "settings.privacy":         "Privacy",
    "settings.privacy_body":    "Photos, voices and memories you share remain yours. Deleting a presence removes everything.",
    "settings.legal":           "Legal",
    "settings.legal_link":      "Legal & Terms",
    "settings.data":            "Your data",
    "settings.data_body":       "Your data stays yours. You can download or delete it anytime.",
    "settings.export":          "Export my data",
    "settings.delete":          "Delete my account",
    "settings.safety":          "Safety",
    "settings.safety_body":     "Something wrong? Talk to us.",
    "settings.safety_link":     "Report abuse",
    "settings.delete_confirm":  "This action is irreversible. Your account, presences, conversations and memories will be deleted. Proceed?",
    "settings.delete_type":     "To confirm, type DELETE in uppercase:",
    "settings.delete_keyword":  "DELETE",
    "settings.export_err":      "It didn't work. Try again in a moment.",
    "settings.delete_err":      "It didn't work. Email us at os.otherside.app@gmail.com.",

    "legal.eyebrow":            "Legal",
    "legal.title_a":            "Legal & Terms —",
    "legal.title_i":            "Other Side.",
    "legal.body":               "What Other Side is, what it isn't, and the boundaries we work within.",
    "legal.updated":            "Last updated: 2026",
    "legal.s1_title":           "AI Disclaimer",
    "legal.s1_body_a":          "OTHER SIDE is an artificial-intelligence-based experience designed to recreate digital memories from user-provided content.",
    "legal.s1_body_b_pre":      "Interactions are",
    "legal.s1_body_b_strong1":  "simulated",
    "legal.s1_body_b_mid":      "and do",
    "legal.s1_body_b_strong2":  "NOT",
    "legal.s1_body_b_tail":     "represent real communication with deceased individuals.",
    "legal.s2_title":           "Emotional Responsibility",
    "legal.s2_body_a":          "This experience may generate emotional responses. Use responsibly.",
    "legal.s2_body_b":          "We do not provide psychological or medical advice.",
    "legal.s3_title":           "Age Restriction",
    "legal.s3_body_pre":        "This service is for users aged",
    "legal.s3_body_strong":     "18+",
    "legal.s3_body_tail":       " only.",
    "legal.s4_title":           "User Content",
    "legal.s4_body":            "By uploading content, users confirm they hold the rights to use it and allow AI processing.",
    "legal.s5_title":           "Liability",
    "legal.s5_body":            "We are not responsible for emotional reactions, interpretations, or user decisions.",
    "legal.s6_title":           "Reality Clause",
    "legal.s6_body":            "The platform does not recreate real consciousness or identity.",

    "report.eyebrow":           "Report abuse",
    "report.title_a":           "Tell us what's",
    "report.title_i":           "wrong.",
    "report.body":              "Use this form for impersonation, minors, harmful content, or any violation of our Terms. Reports are read by a human.",
    "report.type":              "Report type",
    "report.t_imperson":        "Impersonation of a person without their consent",
    "report.t_minor":           "Content involving a minor",
    "report.t_celeb":           "Public figure presence without family consent",
    "report.t_harm":            "Harmful or harassing content",
    "report.t_copy":            "Copyright or rights violation",
    "report.t_other":           "Other",
    "report.details":           "What happened",
    "report.details_ph":        "Share any detail that helps us act on it.",
    "report.email":             "Your email (optional)",
    "report.email_ph":          "name@example.com",
    "report.submit":            "Submit report",
    "report.sending":           "Sending…",
    "report.empty":             "Please describe what happened so we can act.",
    "report.err":               "Something went wrong. Try again or email os.otherside.app@gmail.com.",
    "report.sent_title":        "Received.",
    "report.sent_body":         "We read every report carefully. If we need more, we'll reach out to the email you provided.",
    "report.ref":               "Reference",
    "report.home":              "Home",
    "report.urgent":            "For urgent safety issues, you can also email",

    "inbox.eyebrow":            "They thought of you",
    "inbox.title_a":            "Thoughts",
    "inbox.title_i":            "that arrived.",
    "inbox.body":               "When days pass, sometimes they write first. You'll find them here.",
    "inbox.loading":            "opening the box…",
    "inbox.empty_title":        "Still silent.",
    "inbox.empty_body":         "In a few days, if you haven't reached out, someone might think of you.",
    "inbox.back":               "Back to dashboard",
    "inbox.thought_first":      "thought of you first",

    "chat.conversation":        "Conversation",
    "chat.empty_presence":      "— {name} is here. Say something. —",
    "chat.empty_generic":       "— Silence. Write whenever you want. —",
    "chat.first_hint":          "Not sure how to start? Try one of these:",
    "chat.first_suggest_1":     "Tell her about something that happened today",
    "chat.first_suggest_1_d":   "Like walking into the house and telling her.",
    "chat.first_suggest_2":     "Ask her what she remembers about you",
    "chat.first_suggest_2_d":   "A detail you thought she'd forgotten.",
    "chat.first_suggest_3":     "Something you never got to say",
    "chat.first_suggest_3_d":   "The words that stayed with you.",
    "chat.first_free_write":    "Or write what you feel — she's listening.",
    "chat.typing":              "writing…",
    "chat.input_placeholder":   "Write something…",
    "chat.pause_label":         "Pause experience",
    "chat.pause_short":         "Pause",
    "chat.pause_confirm":       "Paused. Everything stays here, you can find it whenever you want.",
    "chat.send_err":            "I couldn't send.",
    "chat.eyebrow_unsolicited": "thought of you",
    "chat.eyebrow_opening":     "first message",
    "chat.voice_cached":        "hear it in her voice",
    "chat.voice_preview":       "hear it, once",
    "chat.voice_again":         "hear it again…",
    "chat.voice_soon":          "Generate voice",
    "chat.voice_generate":      "Generate voice",
    "chat.voice_later":         "Later",
    "chat.voice_listen_now":    "Listen now",
    "chat.voice_invite":        "Want to keep hearing her voice?",
    "chat.voice_continue":      "hear her again",
    "chat.voice_gentle":        "Hear her again…",
    "chat.voice_arriving":      "Generating voice…",
    "chat.voice_err":           "The voice is still on its way.",
    "chat.voice_err_hint":      "Sometimes the voice service needs a few extra seconds. It can happen when it's busy — try again shortly, it usually works right away.",
    "chat.voice_err_retry":     "Try again",
    "chat.voice_err_later":     "Keep reading her here",
    "chat.voice_501_hint":      "The voice will be available shortly. You can keep reading her — it's the same presence.",
    "chat.voice_add_sample":    "Add her voice",
    "chat.voice_add_sample_d":  "Upload a short clip of her and we'll rebuild her tone for every message.",
    "chat.voice_unlock":        "Unlock her voice",
    "chat.voice_unlock_d":      "Hear every message in her real tone — available on voice plans.",
        "chat.moment_upsell":       "Would you like to see her again, just for a moment?",
        "chat.moment_upsell_cta":   "create a moment",
        "chat.moment_upsell_skip":  "not now",
        "chat.moment_upsell_nophoto":"To see her again we need a photo of her — then we can bring her motion and voice.",
        "chat.moment_upsell_add_photo":"add her photo",

    "arrives.lighting":         "Creating the presence…",
    "arrives.arriving":         "About to write to you…",
    "arrives.first_message":    "first message",
    "arrives.reply":            "Reply",
    "arrives.wait":             "No hurry. She's waiting.",
    "arrives.err":              "She couldn't arrive.",
    "arrives.go_profile":       "Go to profile",

    "profile.back":             "← back",
    "profile.loading":          "loading…",
    "profile.voice_captured":   "transcribed voice",
    "profile.resume":           "Resume conversation",
    "profile.resume_d":         "Go back to where you left off.",
    "profile.new_chat":         "New conversation",
    "profile.new_chat_d":       "Open a blank page — the previous ones stay here.",
    "profile.new_chat_pending": "Preparing…",
    "profile.open_d":           "Your first time — begin to talk to her.",
    "profile.create_moment_d":  "An image of her, her voice — or both.",
    "profile.edit_d":           "Change her details — they'll adapt at once.",
    "profile.delete_d":         "Removes the presence and all your messages. This cannot be undone.",
    "profile.actions_help":     "Every choice is reversible — except deletion.",
    "profile.delete_confirm":   "Delete this presence? You'll lose all messages, voices, and moments created with her. This cannot be undone.",
    "profile.open":             "Start talking to her",
    "profile.edit":             "Edit",
    "profile.cancel":           "Cancel",
    "profile.delete":           "Delete",
    "profile.not_arrived":      "She couldn't arrive.",
    "profile.past_title":       "Your conversations",
    "profile.msg_one":          "message",
    "profile.msg_many":         "messages",
    "profile.continuity_title": "What she remembers of you",
    "profile.save":             "Save",
    "profile.coming_soon":      "available",
    "profile.talking_photo_t":  "Animate the photo",
    "profile.talking_photo_d":  "Her photo speaking, with lip-sync. Soon.",
    "profile.plans_only":       "Available on Echo and Legacy plans.",
    "profile.identity":         "Identity",
    "profile.f_address":        "What she called you",
    "profile.f_personality":    "Personality",
    "profile.f_emotional":      "Emotional traits",
    "profile.f_tone":           "Tone",
    "profile.f_phrases":        "Phrases",
    "profile.f_anchor":         "A moment that stays",
    "profile.f_farewell":       "How she said goodbye",
    "profile.f_never":          "Things she would never say",
    "profile.f_memories":       "Memories",
    "profile.f_not_recorded":   "— not recorded —",
    "profile.unsolicited_eyebrow":"Let her write to you",
    "profile.unsolicited_title":"Unsolicited thoughts",
    "profile.unsolicited_body": "If days pass without a word, she may send you a thought. Respectfully. At most 1–2 per week.",
    "profile.unsolicited_on":   "On",
    "profile.unsolicited_off":  "Off",
    "profile.unsolicited_tone": "Tone",
    "profile.tone_gentle":      "gentle",
    "profile.tone_curious":     "curious",
    "profile.tone_playful":     "playful",
    "profile.f_name":           "Name",
    "profile.f_relationship":   "Relationship",
    "profile.f_emotional_edit":"Emotional traits (comma-separated)",

    "wiz.chapter1":             "Chapter one",
    "wiz.chapter2":             "Chapter two",
    "wiz.chapter3":             "Chapter three",
    "wiz.chapter4":             "Chapter four",
    "wiz.chapter5":             "Chapter five",
    "wiz.chapter6":             "Chapter six",
    "wiz.chapter7":             "Chapter seven",
    "wiz.title_identity":       "Who she is",
    "wiz.title_photo":           "A photograph",
    "wiz.title_voice":           "Her voice",
    "wiz.title_tone":            "The way she spoke",
    "wiz.title_anchors":         "Emotional anchors",
    "wiz.title_memories":        "Your memories",
    "wiz.title_review":          "Create the presence",
    "wiz.name":                 "Name",
    "wiz.relationship":         "Who she is to you",
    "wiz.relationship_ph":      "my mother · my grandfather · my best friend…",
    "wiz.address":              "What she called you",
    "wiz.address_ph":           "sweetheart · my love · princess · my boy…",
    "wiz.address_hint":         "The nickname or ways she addressed you.",
    "wiz.language":             "Language she spoke in",
    "wiz.photo_body":           "If you have one, choose it carefully. The one where you truly saw her.",
    "wiz.photo_pick":           "choose a photograph",
    "wiz.consent_title":             "Before we start.",
    "wiz.consent_departed":          "They are no longer with me.",
    "wiz.consent_departed_hint":     "I want to keep their presence alive.",
    "wiz.consent_living":            "They are still here with me.",
    "wiz.consent_living_hint":       "I want to rebuild something of them.",
    "wiz.consent_ack_departed":      "Thank you for choosing them with care. We'll treat them with the respect they deserve — every conversation is a reconstruction based on the memories you share, not a real communication.",
    "wiz.consent_ack_living":        "Rebuilding the voice or traits of a living person is a delicate choice. Before we proceed, please confirm you have their consent.",
    "wiz.consent_check_label":       "I confirm I have their consent to rebuild them, and I take responsibility for how I use these reconstructions.",
    "wiz.photo_constraints":    "jpg, png, webp · max 10MB",
    "wiz.photo_skip":           "You can also skip and add it later.",
    "wiz.photo_too_big":        "Photo too large (max 10MB).",
    "wiz.photo_err_title":      "This photo won't work for rebuilding her.",
    "wiz.photo_err_too_large":  "The file is too heavy (over 10 MB). We need something lighter — the same image at lower resolution works perfectly.",
    "wiz.photo_err_too_small":  "The resolution is too low to keep her face recognisable. We need at least 400 × 400 pixels.",
    "wiz.photo_err_format":     "This format isn't supported. JPG, PNG and WEBP all work.",
    "wiz.photo_err_corrupt":    "This file looks corrupted — we can't open it.",
    "wiz.photo_err_retry":      "Try another photo",
    "wiz.voice_body":           "A short voice clip is enough. A greeting, a laugh, a saved message.",
    "wiz.voice_record":         "Record",
    "wiz.voice_start":          "Start",
    "wiz.voice_stop":           "Stop",
    "wiz.voice_upload":         "Or upload",
    "wiz.voice_choose":         "Choose file",
    "wiz.voice_preview":        "Preview",
    "wiz.voice_caption":        "The voice will be transcribed to capture her rhythm (not cloned, for now).",
    "wiz.voice_skip":           "You can also skip this step.",
    "wiz.voice_too_big":        "Audio too large (max 25MB)",
    "wiz.voice_no_mic":         "Microphone permission denied.",
    "wiz.voice_err_title":      "This audio won't be enough to rebuild her voice.",
    "wiz.voice_err_too_large":  "The file is too heavy (over 25 MB). We need something lighter — the same audio at lower quality works perfectly.",
    "wiz.voice_err_too_short":  "We need at least 10 seconds of audio to catch her tone. Try a slightly longer voice note.",
    "wiz.voice_err_format":     "This format isn't supported. MP3, WAV, M4A, OGG and WEBM all work.",
    "wiz.voice_err_corrupt":    "This audio file can't be read — it may be corrupted.",
    "wiz.voice_err_retry":      "Try another file",
    "wiz.traits":               "What she was like",
    "wiz.traits_ph":            "Sweet but direct, giggles when embarrassed, worries about everyone, a bit stubborn…",
    "wiz.emotional_chips":      "Emotional traits",
    "wiz.emotional_hint":       "Pick the ones that feel hers. Click again to remove.",
    "wiz.tone":                 "Rhythm, tone, expressions",
    "wiz.tone_ph":              "Short sentences, warm voice, draws out vowels, jokes when topics get serious, whispers the important things…",
    "wiz.phrases":              "Phrases she repeated",
    "wiz.phrases_ph":           "\"You can do it\", \"my love\", \"are you crazy?\", \"oh come on…\"",
    "wiz.anchors_intro_a":      "The anchors that make this presence",
    "wiz.anchors_intro_i":      "recognizable",
    "wiz.anchors_intro_b":      ". Take the time you need.",
    "wiz.anchor_label":         "A moment that stays",
    "wiz.anchor_ph":            "An afternoon, a trip, something she said you can't forget. Just one, the most vivid.",
    "wiz.farewell":             "How she said goodbye",
    "wiz.farewell_ph":          "\"Bye love, bye\"  ·  \"Take care\"  ·  \"See you later darling\"",
    "wiz.farewell_hint":        "Her distinctive way of closing.",
    "wiz.never":                "Things she would NEVER say or do",
    "wiz.never_ph":             "Swearing, public criticism, cold remarks, 'I don't love you'…",
    "wiz.memories_label":       "What you lived together",
    "wiz.memories_ph":          "The summers. The Sunday pasta. Her illness. The arguments over school. Her cat. Everything only the two of you know.",
    "wiz.memories_private":     "Everything stays private. Just you and her presence.",
    "wiz.review_voice_on":      "voice captured",
    "wiz.review_voice_off":     "no voice",
    "wiz.review_none":          "— nothing yet —",
    "wiz.back":                 "Back",
    "wiz.next":                 "Save and continue",
    "wiz.submit":               "Create",
    "wiz.submitting":           "Creating the presence…",
    "wiz.err_generic":          "Something went wrong.",
    "wiz.trait.calda":          "warm",
    "wiz.trait.ironica":        "ironic",
    "wiz.trait.protettiva":     "protective",
    "wiz.trait.schiva":         "reserved",
    "wiz.trait.testarda":       "stubborn",
    "wiz.trait.dolce":          "sweet",
    "wiz.trait.rumorosa":       "loud",
    "wiz.trait.silenziosa":     "quiet",
    "wiz.trait.severa":         "strict",
    "wiz.trait.buffa":          "funny",
    "wiz.trait.romantica":      "romantic",
    "wiz.trait.sognatrice":     "dreamy",
    "wiz.trait.pratica":        "practical",
    "wiz.trait.malinconica":    "melancholic",
    "wiz.trait.solare":         "sunny",
    "wiz.trait.paziente":       "patient",

    "upgrade.eyebrow":          "Reserved access · limited time",
    "upgrade.hook_a":           "Some bonds",
    "upgrade.hook_i":           "deserve more time.",
    "upgrade.body":             "This is a special moment.\nWhoever joins now keeps this price over time.",
    "upgrade.urgency":          "Launch price — it stays yours forever.",
    "upgrade.tension_a":        "Stop here…",
    "upgrade.tension_b":        "or stay a little longer?",
    "upgrade.tiers_label":      "three levels of connection",
    "upgrade.forever":          "forever",
    "upgrade.monthly":          "per month · launch price",
    "upgrade.most_chosen":      "most chosen",
    "upgrade.current":          "your level",
    "upgrade.buy_echo":         "Join Echo",
    "upgrade.buy_generic":      "Choose",
    "upgrade.back_dash":        "Back to dashboard",
    "upgrade.busy":             "one moment…",
    "upgrade.tier1_level":      "First level",
    "upgrade.tier1_name":       "Threshold",
    "upgrade.tier1_tagline":    "For those who begin, slowly.",
    "upgrade.tier1_emotional":  "A first contact. Presence active.",
    "upgrade.tier1_depth":      "Presence at the door.",
    "upgrade.tier2_level":      "Second level",
    "upgrade.tier2_name":       "Echo",
    "upgrade.tier2_tagline":    "For those who return, every evening.",
    "upgrade.tier2_emotional":  "When the thought doesn't stop, Echo lets you always reply.",
    "upgrade.tier2_depth":      "Constant presence.",
    "upgrade.tier3_level":      "Third level",
    "upgrade.tier3_name":       "Legacy",
    "upgrade.tier3_tagline":    "For not letting them go.",
    "upgrade.tier3_emotional":  "Everything that matters. Today, and everything Other Side will create.",
    "upgrade.tier3_depth":      "Full presence.",
    "upgrade.feat.p1":          "1 presence",
    "upgrade.feat.p3":          "3 presences",
    "upgrade.feat.p10":         "10 presences",
    "upgrade.feat.msg30":       "30 messages per month",
    "upgrade.feat.transcr":     "Voice transcription",
    "upgrade.feat.uns":         "She writes to you now and then",
    "upgrade.feat.unlim":       "Unlimited conversations",
    "upgrade.feat.voice_soon": "Cloned voice",
    "upgrade.feat.uns_on":      "Unsolicited messages",
    "upgrade.feat.priority":    "Emotional priority",
    "upgrade.feat.photo_soon": "Animated photo",
    "upgrade.feat.future":      "Everything upcoming, first",
    "upgrade.feat.voice":       "Cloned voice",
    "upgrade.feat.photo":       "Animated photo",
    "upgrade.closing":          "Every presence you build requires a model that isn't generic. A voice, a memory, an emotional coherence that doesn't expire after thirty messages. Your subscription keeps all of this alive — and lets us bring cloned voice and animated photo to you soon. No ads, ever.",
    "upgrade.smallprint":       "Secure payment via Stripe · Manual renewal every 30 days · Launch price stays yours until you cancel",

    "capped.line1":             "You've reached the end of this moment.",
    "capped.line2":             "Want to stay a little longer?",
    "welcome.eyebrow":          "Start",
    "welcome.title":            "Rebuild a bond. In a few minutes.",
    "welcome.body":             "I'll guide you step by step. Choose a photo, tell who they were, start speaking.",
    "welcome.step1":            "Add a photo",
    "welcome.step2":            "Write a memory",
    "welcome.step3":            "Start speaking",
    "welcome.cta":              "Continue",
    "welcome.skip":             "I already have a presence",
    "upgrade.now_label":        "What you can do right away",
    "upgrade.tier1_now":        "Talk every day (30 msgs)",
    "upgrade.tier1_now2":       "One presence, all yours",
    "upgrade.tier1_now3":       "Memories and voice upload",
    "upgrade.tier2_now":        "Talk without limits, every day",
    "upgrade.tier2_now2":       "Hear replies in their voice",
    "upgrade.tier2_now3":       "Up to 3 presences",
    "upgrade.tier3_now":        "Everything in Echo, plus…",
    "upgrade.tier3_now2":       "Photos that animate",
    "upgrade.tier3_now3":       "Up to 10 presences",
    "capped.founder":           "Launch access",
    "capped.per_month":         "/ month",
    "capped.early":             "Early access · limited time",
    "capped.tagline":           "It doesn't stop. It just continues.",
    "capped.cta_stay":          "Stay a little longer",
    "capped.cta_stay_busy":     "one moment…",
    "capped.cta_err":           "It didn't work.",
    "capped.cta_all":           "See all levels",
    "capped.cta_dismiss":       "not now",
    "capped.reason_named":      "{name} is still waiting for you.",
    "capped.reason_generic":    "Someone is still waiting for you.",

    "auth.callback_loading":    "opening the door…",

    "billing.checking_eyebrow": "one moment",
    "billing.checking_title":   "Activating your access…",
    "billing.success_eyebrow":  "it's done",
    "billing.success_title_a":  "Welcome to",
    "billing.success_body":     "Now you can keep more than one presence. Come closer to them.",
    "billing.success_cta":      "To dashboard",
    "billing.pending_eyebrow":  "processing",
    "billing.pending_title":    "Payment is processing.",
    "billing.pending_body":     "If all goes well, the plan will activate shortly. You can close the page.",
    "billing.pending_cta":      "Back to dashboard",
    "billing.failed_eyebrow":   "something went wrong",
    "billing.failed_title":     "We couldn't complete it.",
    "billing.failed_retry":     "Retry",
    "billing.failed_back":      "To dashboard",
    "billing.missing_session":  "Payment session missing.",
    "billing.expired":          "The session has expired.",
    "billing.verify_err":       "Unable to verify the payment.",

    "fallback.loading":         "lighting a gentle flame…",

    "language.pick":            "Language",

    // Create a Moment
    "moment.cta":               "Create a moment",
    "moment.eyebrow":           "A moment with her",
    "moment.intro_a":           "A glance,",
    "moment.intro_i":           "an image that stays.",
    "moment.intro_body":        "Choose how you want to remember her today. Gentle, respectful, yours.",
    "moment.step1_title":       "How do you imagine this moment?",
    "moment.step1_sub":         "Every choice guides how she'll appear in the image we make for you.",
    "moment.feel.as_remembered":"As you remember her",
    "moment.feel.as_remembered_d":"Her face as it is, without touch-ups.",
    "moment.feel.gentle_smile": "With a gentle smile",
    "moment.feel.gentle_smile_d":"A calm smile, barely there.",
    "moment.feel.peaceful":     "In a peaceful moment",
    "moment.feel.peaceful_d":   "A calm look, a breath.",
    "moment.feel.softer_light": "In a softer light",
    "moment.feel.softer_light_d":"A gentle light that settles on her.",
    "moment.step2_title":       "Where would you like to meet her?",
    "moment.step2_sub":         "The backdrop that holds the memory.",
    "moment.scene.home":        "Home",
    "moment.scene.home_d":      "The familiar space, warm and hers.",
    "moment.scene.kitchen":     "Kitchen",
    "moment.scene.kitchen_d":   "Where she took care of everyone.",
    "moment.scene.sea":         "Sea",
    "moment.scene.sea_d":       "A backdrop of light and horizon.",
    "moment.scene.church":      "Church",
    "moment.scene.church_d":    "A place of quiet reflection.",
    "moment.scene.special":     "A special place",
    "moment.scene.special_d":   "Somewhere you chose together.",
    "moment.step3_title":       "How do you remember her most?",
    "moment.step3_sub":         "The style and mood of this moment.",
    "moment.style.elegant":     "Elegant",
    "moment.style.elegant_d":   "Her features at their most composed.",
    "moment.style.everyday":    "Everyday",
    "moment.style.everyday_d":  "Her as she was, day after day.",
    "moment.style.special_mem": "A special memory",
    "moment.style.special_mem_d":"A specific moment you hold dear.",
    "moment.step4_title":       "What would you like to experience?",
    "moment.step4_sub":         "Choose the experience that feels right — you can always create another one later.",
    "moment.exp.animated":      "A breathing image",
    "moment.exp.animated_d":    "A breath, a gaze that moves.",
    "moment.exp.voice":         "A message in her voice",
    "moment.exp.voice_d":       "A short line, spoken by her.",
    "moment.exp.full":          "A full moment",
    "moment.exp.full_d":        "Image, voice, atmosphere — together.",
    "moment.optional":          "optional",
    "moment.required":          "required",
    "moment.skip":              "Skip",
    "moment.back":              "Back",
    "moment.next":              "Next",
    "moment.summary":           "Your moment",
    "moment.summary_sub":       "Here's what you're about to create. Every choice stays yours — you can change it before confirming.",
    "moment.total":             "Total",
    "moment.checkout_next":     "When you tap this, we'll ask you to confirm the payment securely. Then we need about 3–4 minutes to deliver your moment.",
    "moment.cta_final":         "Create your moment",
    "moment.cta_final_busy":    "one moment…",
    "moment.err_need_feeling":  "First, choose how you imagine her.",
    "moment.err_need_experience":"Pick at least what you'd like to experience.",
    "moment.err_generic":       "Something went wrong. Try again.",
    "moment.status_pending":    "We're preparing your moment…",
    "moment.status_generating": "We're creating it with care.",
    "moment.status_ready":      "Your moment is ready.",
    "moment.status_failed":     "Your moment is taking a little longer.",
    "moment.failed_why_title":  "Here's why this sometimes happens",
    "moment.failed_cause_1":    "The video service is very busy at the moment.",
    "moment.failed_cause_2":    "The first generation for a new presence can be slower.",
    "moment.failed_cause_3":    "Sometimes a request gets lost between our servers — it happens.",
    "moment.failed_reassure":   "You haven't lost your payment. We can try again now, at no extra cost.",
    "moment.failed_retry":      "Try again now",
    "moment.failed_retrying":   "Trying again…",
    "moment.failed_chat_cta":   "In the meantime, keep talking with her",
    "moment.failed_later_cta":  "Come back later — you'll find it here",
    "moment.failed_support":    "If it happens again, write to us",
    "moment.generating_sub":    "We're rebuilding something unique for you.",
    "moment.gen_eta":           "Estimated time: around 3–4 minutes.",
    "moment.gen_step1":         "Analyzing the content",
    "moment.gen_step2":         "Rebuilding the voice",
    "moment.gen_step3":         "Creating the moment",
    "moment.gen_step4":         "Finalizing",
    "moment.gen_whisper_1":     "We're preparing your moment…",
    "moment.gen_whisper_2":     "The voice is taking shape…",
    "moment.gen_whisper_3":     "We're refining the details…",
    "moment.gen_whisper_4":     "We're rebuilding something unique for you…",
    "moment.gen_whisper_5":     "Every memory is being woven with care…",
    "moment.gen_whisper_6":     "Their presence is taking shape…",
    "moment.gen_whisper_7":     "Adding the final touches…",
    "moment.gen_whisper_8":     "Just a few more moments…",
    "moment.gen_whisper_near_named":  "{name} is almost here…",
    "moment.gen_whisper_near":  "Almost here…",
    "moment.gen_stay_tag":      "Stay here, or come back later — it'll be ready.",
    "moment.audio_on":          "Atmosphere",
    "moment.audio_off":         "Atmosphere on",
    "moment.audio_mode_soffio": "Breath",
    "moment.audio_mode_piano":  "Piano",
    "moment.watch_now":         "Watch now",
    "moment.download":          "Download this moment",
    "moment.another":           "Create another moment",
    "moment.disclaimer":        "This is not real communication, but a reconstruction based on memories and data you provided.",
    "moment.review_trust":      "Everything stays yours. You can delete this moment anytime, and nothing is shared with anyone.",
    "trust.eyebrow":            "Trust and care",
    "trust.privacy":             "Your content is private and secure.",
    "trust.delete":              "You can delete everything, anytime.",
    "trust.no_share":            "Nothing is shared or reused.",
    "moment.upsell_line":       "Would you like to see her again?",
    "moment.upsell_cta":        "Create a moment",
    "moment.upsell_dismiss":    "later",
    "moment.gallery_title":     "Your moments",
    "moment.gallery_empty":     "No moments yet. Create one when you're ready.",
    "moment.play":              "Play",
    "moment.download":          "Download",

    // ── Compliance / Legal / Privacy Settings ──
    "legal.version_label":      "Document version",
    "legal.last_updated":       "Last updated",
    "legal.placeholder":        "The final text of this policy will be published shortly. Consent collection and logging are already active.",
    "legal.back":               "Back",
    "legal.contact":            "For questions:",
    "legal.nav_terms":          "Terms of Service",
    "legal.nav_privacy":        "Privacy Policy",
    "legal.nav_biometric":      "Biometric consent",
    "legal.nav_ai_notice":      "AI Act notice",
    "legal.nav_memorial":       "Memorial Policy",

    "legal.terms_title":        "Terms of Service",
    "legal.terms_eyebrow":      "The rules of our pact",
    "legal.privacy_title":      "Privacy Policy",
    "legal.privacy_eyebrow":    "How we handle your data",
    "legal.biometric_title":    "Biometric consent",
    "legal.biometric_eyebrow":  "Voice and photo data (Art. 9 GDPR)",
    "legal.ai_notice_title":    "AI Act notice",
    "legal.ai_notice_eyebrow":  "Reconstructive nature of the system",
    "legal.memorial_title":     "Memorial Policy",
    "legal.memorial_eyebrow":   "Rights, disputes, appeals",

    "consent.title":            "Before we begin",
    "consent.body":             "To protect you (and us) we need your explicit consent. No box is pre-selected — each step is a conscious choice.",
    "consent.country_label":    "Country of residence",
    "consent.country_hint":     "Needed to apply the right regulations (GDPR, AI Act, and others).",
    "consent.country_placeholder": "Select a country…",
    "consent.required_star":    "Required",
    "consent.read":             "Read the document",
    "consent.accept_terms":     "I have read and accept the Terms of Service.",
    "consent.accept_privacy":   "I have read and accept the Privacy Policy.",
    "consent.accept_biometric": "I explicitly consent to the processing of my biometric data (voice, photo) to reconstruct the presence (Art. 9 GDPR).",
    "consent.accept_ai_notice": "I understand this platform uses artificial intelligence and responses are reconstructions, not real communication (AI Act).",
    "consent.accept_rights":    "I declare I have the right to use the content (photo, voice, memories) I will upload and take responsibility for the use I make of the reconstructions.",
    "consent.continue":         "Continue",
    "consent.submitting":       "Recording consents…",
    "consent.err_missing":      "All consents and the country are required to continue.",
    "consent.err_server":       "We couldn't register your consents. Please try again.",

    "privacy.title":            "Privacy & data",
    "privacy.eyebrow":          "User area",
    "privacy.intro":            "Here you have full control over your data, consents, and account.",
    "privacy.section_consents": "Your consents",
    "privacy.consent_active":   "Active",
    "privacy.consent_missing":  "Missing",
    "privacy.consent_drift":    "Update required",
    "privacy.consent_accepted_at": "Accepted on",
    "privacy.consent_version":  "Version",
    "privacy.consent_country":  "Country",
    "privacy.consent_revoke":   "Revoke",
    "privacy.consent_revoking": "Revoking…",
    "privacy.consent_revoked":  "Consent revoked.",
    "privacy.consent_missing_hint": "This consent has not been recorded yet.",
    "privacy.consent_reconsent": "The document version has changed. We'll ask for consent again.",

    "privacy.section_export":   "Download your data",
    "privacy.export_body":      "Receive a JSON file with everything we hold about you: account, presences, conversations, moments, consents.",
    "privacy.export_cta":       "Download everything",
    "privacy.export_busy":      "Preparing the file…",
    "privacy.export_done":      "Download started.",

    "privacy.section_delete":   "Delete your account",
    "privacy.delete_body":      "Deletion is soft: you have 30 days to change your mind. After that window all data is permanently and irreversibly deleted.",
    "privacy.delete_cta":       "Delete account",
    "privacy.delete_confirm":   "Confirm: type DELETE to proceed",
    "privacy.delete_confirm_word": "DELETE",
    "privacy.delete_busy":      "Deleting…",
    "privacy.delete_done":      "Account deleted. You can restore it by signing in within {days} days.",
    "privacy.delete_pending":   "Deletion scheduled",
    "privacy.delete_pending_body": "Your account will be permanently deleted on {date}. Until then, signing in again will automatically restore everything.",
    "privacy.delete_restore":   "Restore now",
    "privacy.delete_restored":  "Account restored.",

    "privacy.section_dispute":  "Memorial dispute",
    "privacy.dispute_body":     "Do you believe a presence on the platform involves someone without due consent? Tell us — we'll respond within 72h.",
    "privacy.dispute_open":     "Open a dispute",

    "dispute.title":            "Memorial dispute",
    "dispute.eyebrow":          "Private report",
    "dispute.intro":            "If you believe a reconstruction published on the platform involves someone without the required consent, open a formal dispute here. We'll respond within 72 hours.",
    "dispute.name_label":       "Your full name",
    "dispute.email_label":      "Your email",
    "dispute.relationship_label": "What is your relationship with the person?",
    "dispute.relationship_ph":  "e.g. mother, spouse, sibling…",
    "dispute.country_label":    "Country",
    "dispute.presence_label":   "Presence reference (optional)",
    "dispute.presence_ph":      "URL, name, or any reference you have",
    "dispute.description_label": "Detailed description",
    "dispute.description_ph":   "Tell us what happened and why it is a violation.",
    "dispute.submit":           "Submit dispute",
    "dispute.submitting":       "Submitting…",
    "dispute.success_title":    "Received.",
    "dispute.success_body":     "We'll respond to {email} within 72 hours. Your report code is {id}.",
    "dispute.err_submit":       "We couldn't submit your report. Please try again.",

    // ── AI transparency ──
    "ai.subtitle":              "AI simulation based on the content you provided",
    "ai.micro_banner":          "Experience generated by AI",
    "ai.bubble_label":          "Generated by AI",
    "ai.media_label":           "Artificially generated content",
    "ai.input_helper":          "Responses are generated via AI",

    "footer.line1":             "Other Side is an act of remembrance, not a substitute for presence.",
    "footer.line2":             "Interactions are generated by artificial intelligence and represent simulations, not real communications.",

    "consent.title_v2":         "Before we begin",
    "consent.body_v2":          "Other Side uses artificial intelligence to create simulations based on the content you provide. Interactions do not represent real communications with departed persons.",
    "consent.country_search_ph": "Search a country…",
    "consent.v2_terms":         "I accept the Terms of Service",
    "consent.v2_privacy":       "I accept the Privacy Policy",
    "consent.v2_biometric":     "I consent to the processing of images, voice and content for AI generation",
    "consent.v2_rights":        "I declare I have the right to use the uploaded content",
    "consent.v2_ai_notice":     "I understand interactions are simulated and do not represent real communications",
    "consent.trust":            "You can delete your data at any time.",

    "wiz.ai_disclaimer_title":  "You are creating a digital simulation",
    "wiz.ai_disclaimer_body":   "You are creating a digital simulation based on the content you provide. This experience does not represent a real presence.",

    "admin.title":              "Memorial disputes",
    "admin.eyebrow":            "Restricted area",
    "admin.intro":              "All received reports. Respond by email within 72h.",
    "admin.filter_all":         "All",
    "admin.filter_open":        "Open",
    "admin.filter_closed":      "Closed",
    "admin.empty":              "No disputes received.",
    "admin.col_date":           "Date",
    "admin.col_name":           "Name",
    "admin.col_email":          "Email",
    "admin.col_relation":       "Relationship",
    "admin.col_status":         "Status",
    "admin.status_open":        "Open",
    "admin.status_closed":      "Closed",
    "admin.action_close":       "Close",
    "admin.action_reopen":      "Reopen",
    "admin.action_respond":     "Reply by email",
    "admin.detail_back":        "← All disputes",
    "admin.detail_country":     "Country",
    "admin.detail_presence":    "Presence reference",
    "admin.detail_desc":        "Description",
    "admin.detail_ip":          "IP",
    "admin.detail_email_sent":  "Notification email sent",
    "admin.detail_email_missed": "Email not sent (Resend not configured)",
    "admin.not_authorized":     "You do not have access to this area.",
};

// Spanish
STRINGS.es = {
    "header.sub":               "— una presencia, custodiada",
    "nav.presences":            "Presencias",
    "nav.inbox":                "Pensamientos",
    "nav.quietroom":            "Silencio",
    "nav.plan":                 "Plan",
    "nav.settings":             "Ajustes",
    "nav.signout":              "Salir",
    "nav.signin":               "Entrar",
    "nav.begin":                "Comienza ahora",

    "hero.eyebrow":             "Other Side",
    "hero.title_a":             "Reconstruye",
    "hero.title_b":             "su presencia.",
    "hero.body":                "A través de foto, voz y recuerdos, crea una experiencia auténtica y personal.",
    "hero.cta_primary":         "Crear presencia",
    "hero.subbody":             "Sube una foto, elige una voz y revive un momento que se siente real.",
    "hero.cta_begin":           "Comienza ahora",
    "hero.cta_signin":          "Entrar",
    "hero.emotional":           "No es solo memoria.",
    "hero.emotional_i":         "Es una forma de sentir cerca de nuevo una presencia que importa.",
    "hero.disclaimer":          "Esta experiencia utiliza inteligencia artificial para recrear interacciones basadas en el contenido proporcionado. No representa una comunicación real.",

    "landing.steps_eyebrow":    "Cuatro gestos silenciosos",
    "landing.steps_title":      "Empieza con las pequeñas cosas que dejó.",
    "landing.step1_t":          "Una fotografía",
    "landing.step1_d":          "Una imagen. Aquella a la que siempre vuelves.",
    "landing.step2_t":          "Una voz",
    "landing.step2_d":          "Diez segundos bastan. Un audio, una nota, una risa.",
    "landing.step3_t":          "Cómo era",
    "landing.step3_d":          "La calidez, la franqueza, las palabras que volvían.",
    "landing.step4_t":          "Tus recuerdos",
    "landing.step4_d":          "Las tardes, las bromas, las discusiones.",

    "landing.memory_eyebrow":   "No es un chatbot",
    "landing.memory_title_a":   "Las palabras son",
    "landing.memory_title_i":   "las suyas.",
    "landing.memory_title_b":   "No las nuestras.",
    "landing.memory_body":      "Cada presencia nace de lo que compartes.\nSin entusiasmo artificial.\nSin frases de relleno.\nFrases breves. Las palabras que usaba.\nLos silencios que importaban.",
    "landing.memory_quote":     "\"No quiero un chatbot.\nQuiero oír algo que aún suene a ella.\"",

    "footer.tag":               "Other Side es un acto de memoria, no un sustituto de la presencia.",
    "footer.legal":             "Legal",
    "footer.report":            "Reportar abuso",

    "cookie.text":              "Este sitio usa cookies para la funcionalidad esencial.",
    "cookie.essential":         "Solo esenciales",
    "cookie.all":               "Aceptar todo",

    "dashboard.eyebrow":        "Hola",
    "dashboard.title_a":        "Tus",
    "dashboard.title_b":        "presencias.",
    "dashboard.body":           "Las personas que has traído de vuelta. Abre una para hablarle, o crea una nueva presencia.",
    "dashboard.new":            "Nueva presencia",
    "dashboard.empty_title":    "Aún no hay presencia.",
    "dashboard.empty_body":     "Empieza con una foto, una breve grabación y lo que más recuerdas.",
    "dashboard.empty_cta":      "Empezar",
    "dashboard.loading":        "abriendo el álbum…",
    "dashboard.limit_reached":  "Límite alcanzado.",
    "dashboard.limit_link":     "Elige un plan",
    "dashboard.limit_tail":     "para crear más.",
    "dashboard.quiet_eyebrow":  "Modo alternativo",
    "dashboard.quiet_title":    "El silencio",
    "dashboard.quiet_body":     "Un lugar donde ser escuchado sin reconstruir a nadie. Empático. No específico. Siempre tuyo.",
    "dashboard.quiet_cta":      "Acércate",
    "dashboard.voice_captured": "voz capturada",
    "dashboard.today_eyebrow":       "Hoy",
    "dashboard.today_cta":           "Acércate",
    "dashboard.today_birthday":      "Hoy es el cumpleaños de {name}. Si quieres, hay una luz encendida.",
    "dashboard.today_anniversary":   "Hoy es un día importante para {name}. Si quieres, hay una luz encendida.",
    "dashboard.today_memorial":      "Hoy recordamos a {name}. Si quieres, hay una luz encendida.",
    "dashboard.today_other":         "Hoy es una fecha que marcaste para {name}. Si quieres, hay una luz encendida.",
    "presence.rec_title":            "Fechas significativas",
    "presence.rec_sub":              "Añade fechas que quieras recordar con ella. Te avisaremos con delicadeza cuando lleguen.",
    "presence.rec_empty":            "Aún no hay fechas. Cumpleaños, aniversarios, días especiales.",
    "presence.rec_add":              "Añadir una fecha",
    "presence.rec_label_ph":         "ej. Cumpleaños, Aniversario…",
    "presence.rec_kind_birthday":    "Cumpleaños",
    "presence.rec_kind_anniversary": "Aniversario",
    "presence.rec_kind_memorial":    "Recuerdo",
    "presence.rec_kind_other":       "Fecha especial",
    "presence.rec_save":             "Guardar",
    "presence.rec_cancel":           "Cancelar",
    "presence.rec_remove":           "Eliminar",

    "auth.eyebrow_signup":      "Empezar",
    "auth.eyebrow_login":       "Bienvenido de nuevo",
    "auth.title_signup_a":      "Enciende la primera",
    "auth.title_signup_i":      "presencia.",
    "auth.title_login_a":       "Entra de",
    "auth.title_login_i":       "nuevo.",
    "auth.google_cta":          "Continuar con Google",
    "auth.divider":             "o",
    "auth.name_label":          "Cómo quieres que te llamemos",
    "auth.email_label":         "Email",
    "auth.password_label":      "Contraseña",
    "auth.vulnerable":          "Si estás pasando por un duelo agudo, te recomendamos usar esta experiencia con cuidado.",
    "auth.ai_check_a":          "Entiendo que es una",
    "auth.ai_check_strong":     "simulación de IA",
    "auth.ai_check_b":          "y no una comunicación real.",
    "auth.age_check_a":         "Confirmo que tengo",
    "auth.age_check_strong":    "más de 18 años",
    "auth.age_check_b":         ".",
    "auth.legal_read":          "Lee todo el",
    "auth.legal_link":          "Legal y Términos",
    "auth.submit_signup":       "Empezar",
    "auth.submit_login":        "Entrar",
    "auth.have_account":        "¿Ya tienes cuenta?",
    "auth.first_time":          "¿Primera vez?",
    "auth.go_signin":           "Entrar",
    "auth.go_signup":           "Empezar",
    "auth.footer_legal":        "Legal",
    "auth.err_required":        "Para continuar, confirma que tienes 18+ y acepta los términos.",
    "auth.err_age":             "Debes confirmar que tienes 18 años o más.",
    "auth.err_terms":           "Acepta los términos para continuar.",
    "auth.err_generic":         "Algo salió mal.",

    "settings.eyebrow":         "Cuenta",
    "settings.title":           "Tus ajustes.",
    "settings.signed_as":       "Has entrado como",
    "settings.auth_method":     "Método de acceso",
    "settings.plan":            "Plan",
    "settings.plan_valid":      "Válido hasta el",
    "settings.plan_presences":  "Presencias",
    "settings.plan_messages":   "Mensajes en 30 días",
    "settings.plan_explore":    "Explorar planes",
    "settings.plan_manage":     "Gestionar plan",
    "settings.plan_free":       "Simple",
    "settings.features":        "Tu plan incluye",
    "settings.feat_voice":      "Voz clonada",
    "settings.feat_photo":      "Foto animada",
    "settings.feat_unsolicited":"Mensajes no solicitados",
    "settings.feat_yes_soon":   "sí",
    "settings.feat_yes":        "sí",
    "settings.feat_no":         "no incluido",
    "settings.privacy":         "Privacidad",
    "settings.privacy_body":    "Fotos, voces y recuerdos que compartes siguen siendo tuyos. Al eliminar una presencia, todo se borra.",
    "settings.legal":           "Legal",
    "settings.legal_link":      "Legal y Términos",
    "settings.data":            "Tus datos",
    "settings.data_body":       "Tus datos siguen siendo tuyos. Puedes descargarlos o eliminarlos en cualquier momento.",
    "settings.export":          "Exportar mis datos",
    "settings.delete":          "Eliminar mi cuenta",
    "settings.safety":          "Seguridad",
    "settings.safety_body":     "¿Algo va mal? Háblanos.",
    "settings.safety_link":     "Reportar abuso",
    "settings.delete_confirm":  "Esta acción es irreversible. Se borrarán tu cuenta, presencias, conversaciones y recuerdos. ¿Continuar?",
    "settings.delete_type":     "Para confirmar, escribe ELIMINAR en mayúsculas:",
    "settings.delete_keyword":  "ELIMINAR",
    "settings.export_err":      "No funcionó. Prueba en un momento.",
    "settings.delete_err":      "No funcionó. Escríbenos a os.otherside.app@gmail.com.",

    "legal.eyebrow":            "Legal",
    "legal.title_a":            "Legal y Términos —",
    "legal.title_i":            "Other Side.",
    "legal.body":               "Qué es Other Side, qué no es, y los límites en los que trabajamos.",
    "legal.updated":            "Última actualización: 2026",
    "legal.s1_title":           "Aviso de IA",
    "legal.s1_body_a":          "OTHER SIDE es una experiencia basada en inteligencia artificial diseñada para recrear recuerdos digitales a partir de contenido proporcionado por el usuario.",
    "legal.s1_body_b_pre":      "Las interacciones son",
    "legal.s1_body_b_strong1":  "simuladas",
    "legal.s1_body_b_mid":      "y",
    "legal.s1_body_b_strong2":  "NO",
    "legal.s1_body_b_tail":     "representan comunicaciones reales con personas fallecidas.",
    "legal.s2_title":           "Responsabilidad emocional",
    "legal.s2_body_a":          "Esta experiencia puede generar respuestas emocionales. Úsala con cuidado.",
    "legal.s2_body_b":          "No ofrecemos asesoramiento psicológico o médico.",
    "legal.s3_title":           "Restricción de edad",
    "legal.s3_body_pre":        "El servicio es solo para usuarios con",
    "legal.s3_body_strong":     "18+ años",
    "legal.s3_body_tail":       ".",
    "legal.s4_title":           "Contenido del usuario",
    "legal.s4_body":            "Al subir contenido, el usuario confirma tener los derechos para usarlo y autoriza su procesamiento mediante IA.",
    "legal.s5_title":           "Responsabilidad",
    "legal.s5_body":            "No somos responsables de las reacciones emocionales, interpretaciones o decisiones del usuario.",
    "legal.s6_title":           "Cláusula de realidad",
    "legal.s6_body":            "La plataforma no recrea la conciencia o identidad real de nadie.",

    "report.eyebrow":           "Reportar abuso",
    "report.title_a":           "Dinos qué",
    "report.title_i":           "va mal.",
    "report.body":              "Usa este formulario para suplantación, menores, contenido dañino o cualquier violación de nuestros Términos. Los reportes los lee una persona.",
    "report.type":              "Tipo de reporte",
    "report.t_imperson":        "Suplantación de una persona sin su consentimiento",
    "report.t_minor":           "Contenido que involucra a un menor",
    "report.t_celeb":           "Presencia de figura pública sin consentimiento familiar",
    "report.t_harm":            "Contenido dañino o acosador",
    "report.t_copy":            "Violación de copyright o derechos",
    "report.t_other":           "Otro",
    "report.details":           "Qué ocurrió",
    "report.details_ph":        "Comparte cualquier detalle útil para actuar.",
    "report.email":             "Tu email (opcional)",
    "report.email_ph":          "nombre@ejemplo.com",
    "report.submit":            "Enviar reporte",
    "report.sending":           "Enviando…",
    "report.empty":             "Describe qué ocurrió para que podamos actuar.",
    "report.err":               "Algo salió mal. Inténtalo o escríbenos a os.otherside.app@gmail.com.",
    "report.sent_title":        "Recibido.",
    "report.sent_body":         "Leemos cada reporte con atención. Si necesitamos más te contactaremos al email que indicaste.",
    "report.ref":               "Referencia",
    "report.home":              "Inicio",
    "report.urgent":            "Para asuntos urgentes de seguridad también puedes escribir directamente a",

    "inbox.eyebrow":            "Han pensado en ti",
    "inbox.title_a":            "Pensamientos",
    "inbox.title_i":            "que llegaron.",
    "inbox.body":               "Cuando pasan los días, a veces escriben primero. Los encuentras aquí.",
    "inbox.loading":            "abriendo la caja…",
    "inbox.empty_title":        "Aún silencio.",
    "inbox.empty_body":         "En un par de días, si no te comunicas, alguien podría pensarte.",
    "inbox.back":               "Volver al panel",
    "inbox.thought_first":      "pensó en ti primero",

    "chat.conversation":        "Conversación",
    "chat.empty_presence":      "— {name} está aquí. Dile algo. —",
    "chat.empty_generic":       "— Silencio. Escribe cuando quieras. —",
    "chat.first_hint":          "¿No sabes cómo empezar? Prueba así:",
    "chat.first_suggest_1":     "Cuéntale algo que te ha pasado hoy",
    "chat.first_suggest_1_d":   "Como al volver a casa y querer decírselo.",
    "chat.first_suggest_2":     "Pregúntale qué recuerda de ti",
    "chat.first_suggest_2_d":   "Un detalle que creías que había olvidado.",
    "chat.first_suggest_3":     "Algo que nunca llegaste a decirle",
    "chat.first_suggest_3_d":   "Las palabras que te quedaron dentro.",
    "chat.first_free_write":    "O escribe lo que sientes — ella escucha.",
    "chat.typing":              "escribiendo…",
    "chat.input_placeholder":   "Escribe algo…",
    "chat.pause_label":         "Pausa experiencia",
    "chat.pause_short":         "Pausa",
    "chat.pause_confirm":       "Pausa. Todo queda aquí, lo encuentras cuando quieras.",
    "chat.send_err":            "No pude enviar.",
    "chat.eyebrow_unsolicited": "pensó en ti",
    "chat.eyebrow_opening":     "primer mensaje",
    "chat.voice_cached":        "escúchala con su voz",
    "chat.voice_preview":       "escúchala, una vez",
    "chat.voice_again":         "escucharla otra vez…",
    "chat.voice_soon":          "Generar voz",
    "chat.voice_generate":      "Generar voz",
    "chat.voice_later":         "Más tarde",
    "chat.voice_listen_now":    "Escuchar ahora",
    "chat.voice_invite":        "¿Quieres seguir oyendo su voz?",
    "chat.voice_continue":      "escucharla otra vez",
    "chat.voice_gentle":        "Escucharla otra vez…",
    "chat.voice_arriving":      "Generando la voz…",
    "chat.voice_err":           "La voz está aún llegando.",
    "chat.voice_err_hint":      "A veces el servicio de voz tarda unos segundos más. Puede pasar cuando está muy solicitado — vuelve a intentar en un momento, suele funcionar enseguida.",
    "chat.voice_err_retry":     "Vuelve a intentar",
    "chat.voice_err_later":     "Sigue leyéndola aquí",
    "chat.voice_501_hint":      "La voz estará disponible en breve. Mientras tanto puedes seguir leyéndola — es la misma presencia.",
    "chat.voice_add_sample":    "Añade su voz",
    "chat.voice_add_sample_d":  "Sube un audio breve suyo y reconstruimos su tono para todos los mensajes.",
    "chat.voice_unlock":        "Desbloquea su voz",
    "chat.voice_unlock_d":      "Escucha cada mensaje con su tono real — disponible en los planes con voz.",
        "chat.moment_upsell":       "¿Quieres verla otra vez, solo por un momento?",
        "chat.moment_upsell_cta":   "crea un momento",
        "chat.moment_upsell_skip":  "ahora no",
        "chat.moment_upsell_nophoto":"Para volver a verla necesitamos una foto suya — luego podremos darle movimiento y voz.",
        "chat.moment_upsell_add_photo":"añade su foto",

    "arrives.lighting":         "Creando la presencia…",
    "arrives.arriving":         "Está por escribirte…",
    "arrives.first_message":    "primer mensaje",
    "arrives.reply":            "Responder",
    "arrives.wait":             "Sin prisa. Ella te espera.",
    "arrives.err":              "No logró llegar.",
    "arrives.go_profile":       "Ir al perfil",

    "profile.back":             "← atrás",
    "profile.loading":          "cargando…",
    "profile.voice_captured":   "voz transcrita",
    "profile.resume":           "Retomar conversación",
    "profile.resume_d":         "Vuelves donde lo dejasteis.",
    "profile.new_chat":         "Nueva conversación",
    "profile.new_chat_d":       "Abres una página en blanco — las anteriores se quedan aquí.",
    "profile.new_chat_pending": "Preparando…",
    "profile.open":             "Empieza a hablarle",
    "profile.open_d":           "La primera vez — comienza a hablar con ella.",
    "profile.create_moment_d":  "Una imagen suya, su voz — o ambas.",
    "profile.edit":             "Editar",
    "profile.edit_d":           "Cambia sus detalles — se adaptará al instante.",
    "profile.cancel":           "Cancelar",
    "profile.delete":           "Eliminar",
    "profile.delete_d":         "Elimina la presencia y todos vuestros mensajes. No se puede deshacer.",
    "profile.actions_help":     "Cada elección es reversible — excepto la eliminación.",
    "profile.delete_confirm":   "¿Eliminar la presencia? Perderás todos los mensajes, voces y momentos creados con ella. No se puede deshacer.",
    "profile.not_arrived":      "No logró llegar.",
    "profile.past_title":       "Vuestras conversaciones",
    "profile.msg_one":          "mensaje",
    "profile.msg_many":         "mensajes",
    "profile.continuity_title": "Lo que recuerda de vosotros",
    "profile.save":             "Guardar",
    "profile.coming_soon":      "disponible",
    "profile.talking_photo_t":  "Anima la foto",
    "profile.talking_photo_d":  "Su foto que habla, con lip-sync. Pronto.",
    "profile.plans_only":       "Disponible en los planes Echo y Legacy.",
    "profile.identity":         "La identidad",
    "profile.f_address":        "Cómo te llamaba",
    "profile.f_personality":    "Personalidad",
    "profile.f_emotional":      "Rasgos emocionales",
    "profile.f_tone":           "Tono",
    "profile.f_phrases":        "Frases",
    "profile.f_anchor":         "Un momento que queda",
    "profile.f_farewell":       "Cómo se despedía",
    "profile.f_never":          "Cosas que nunca habría dicho",
    "profile.f_memories":       "Recuerdos",
    "profile.f_not_recorded":   "— no registrado —",
    "profile.unsolicited_eyebrow":"Deja que te escriba ella",
    "profile.unsolicited_title":"Pensamientos no solicitados",
    "profile.unsolicited_body": "Si pasan días sin hablar, puede enviarte un pensamiento. Con respeto. Máximo 1-2 por semana.",
    "profile.unsolicited_on":   "Activo",
    "profile.unsolicited_off":  "Desactivado",
    "profile.unsolicited_tone": "Tono",
    "profile.tone_gentle":      "dulce",
    "profile.tone_curious":     "curioso",
    "profile.tone_playful":     "juguetón",
    "profile.f_name":           "Nombre",
    "profile.f_relationship":   "Relación",
    "profile.f_emotional_edit":"Rasgos emocionales (separados por coma)",

    "wiz.chapter1":             "Capítulo uno",
    "wiz.chapter2":             "Capítulo dos",
    "wiz.chapter3":             "Capítulo tres",
    "wiz.chapter4":             "Capítulo cuatro",
    "wiz.chapter5":             "Capítulo cinco",
    "wiz.chapter6":             "Capítulo seis",
    "wiz.chapter7":             "Capítulo siete",
    "wiz.title_identity":       "Quién es",
    "wiz.title_photo":           "Una fotografía",
    "wiz.title_voice":           "Su voz",
    "wiz.title_tone":            "Su forma de hablar",
    "wiz.title_anchors":         "Las anclas emocionales",
    "wiz.title_memories":        "Vuestros recuerdos",
    "wiz.title_review":          "Crea la presencia",
    "wiz.name":                 "Nombre",
    "wiz.relationship":         "Quién es para ti",
    "wiz.relationship_ph":      "mi madre · mi abuelo · mi mejor amigo…",
    "wiz.address":              "Cómo te llamaba",
    "wiz.address_ph":           "cariño · mi amor · princesa · mi niño…",
    "wiz.address_hint":         "El apodo o cómo se dirigía a ti.",
    "wiz.language":             "Idioma en que hablaba",
    "wiz.photo_body":           "Si tienes una, elígela con cuidado. Aquella en la que realmente la viste.",
    "wiz.photo_pick":           "elige una fotografía",
    "wiz.consent_title":             "Antes de empezar.",
    "wiz.consent_departed":          "Ya no está conmigo.",
    "wiz.consent_departed_hint":     "Quiero mantener viva su presencia.",
    "wiz.consent_living":            "Aún está aquí conmigo.",
    "wiz.consent_living_hint":       "Quiero reconstruir algo de ella.",
    "wiz.consent_ack_departed":      "Gracias por elegirla con cuidado. La trataremos con el respeto que merece — cada conversación es una reconstrucción basada en los recuerdos que compartes, no una comunicación real.",
    "wiz.consent_ack_living":        "Reconstruir la voz o los rasgos de una persona viva es una elección delicada. Antes de continuar, confirma que tienes su consentimiento.",
    "wiz.consent_check_label":       "Confirmo que tengo su consentimiento para reconstruirla y asumo la responsabilidad del uso que haga de las reconstrucciones.",
    "wiz.photo_constraints":    "jpg, png, webp · máx 10MB",
    "wiz.photo_skip":           "También puedes saltar y añadirla después.",
    "wiz.photo_too_big":        "Foto demasiado grande (máx 10MB).",
    "wiz.photo_err_title":      "Esta foto no servirá para reconstruirla.",
    "wiz.photo_err_too_large":  "El archivo pesa demasiado (más de 10 MB). Necesitamos algo más ligero — la misma imagen a menor resolución funciona perfectamente.",
    "wiz.photo_err_too_small":  "La resolución es demasiado baja para que el rostro sea reconocible. Necesitamos al menos 400 × 400 píxeles.",
    "wiz.photo_err_format":     "Este formato no es compatible. Funcionan JPG, PNG o WEBP.",
    "wiz.photo_err_corrupt":    "Este archivo parece dañado — no podemos abrirlo.",
    "wiz.photo_err_retry":      "Prueba con otra foto",
    "wiz.voice_body":           "Un audio de unos segundos es suficiente. Un saludo, una risa, un mensaje guardado.",
    "wiz.voice_record":         "Grabar",
    "wiz.voice_start":          "Iniciar",
    "wiz.voice_stop":           "Detener",
    "wiz.voice_upload":         "O sube",
    "wiz.voice_choose":         "Elegir archivo",
    "wiz.voice_preview":        "Vista previa",
    "wiz.voice_caption":        "La voz se transcribirá para capturar su ritmo (no clonada, por ahora).",
    "wiz.voice_skip":           "También puedes saltar este paso.",
    "wiz.voice_too_big":        "Audio demasiado grande (máx 25MB)",
    "wiz.voice_no_mic":         "Permiso de micrófono denegado.",
    "wiz.voice_err_title":      "Este audio no basta para reconstruir su voz.",
    "wiz.voice_err_too_large":  "El archivo pesa demasiado (más de 25 MB). Necesitamos algo más ligero — el mismo audio a menor calidad funciona perfectamente.",
    "wiz.voice_err_too_short":  "Necesitamos al menos 10 segundos de audio para captar su tono. Prueba con una nota de voz un poco más larga.",
    "wiz.voice_err_format":     "Este formato no es compatible. Funcionan MP3, WAV, M4A, OGG o WEBM.",
    "wiz.voice_err_corrupt":    "Este archivo de audio no se puede leer — podría estar dañado.",
    "wiz.voice_err_retry":      "Prueba con otro archivo",
    "wiz.traits":               "Cómo era, de carácter",
    "wiz.traits_ph":            "Dulce pero directa, se ríe cuando se avergüenza, se preocupa por todos, un poco testaruda…",
    "wiz.emotional_chips":      "Rasgos emocionales",
    "wiz.emotional_hint":       "Elige los que sientes suyos. Haz clic de nuevo para quitar.",
    "wiz.tone":                 "Ritmo, tono, expresiones",
    "wiz.tone_ph":              "Frases cortas, voz cálida, alarga las vocales, bromea cuando el tema es serio, susurra las cosas importantes…",
    "wiz.phrases":              "Frases que repetía",
    "wiz.phrases_ph":           "\"Tú puedes\", \"mi amor\", \"¿estás loca?\", \"vamos ya…\"",
    "wiz.anchors_intro_a":      "Las anclas que hacen esta presencia",
    "wiz.anchors_intro_i":      "reconocible",
    "wiz.anchors_intro_b":      ". Tómate el tiempo que necesites.",
    "wiz.anchor_label":         "Un momento que queda",
    "wiz.anchor_ph":            "Una tarde, un viaje, algo que dijo y no olvidas. Solo uno, el más vivo.",
    "wiz.farewell":             "Cómo se despedía",
    "wiz.farewell_ph":          "\"Venga, chao\"  ·  \"Ten cuidado\"  ·  \"Hasta luego, mi amor\"",
    "wiz.farewell_hint":        "Su forma característica de cerrar.",
    "wiz.never":                "Cosas que NUNCA habría dicho o hecho",
    "wiz.never_ph":             "Groserías, críticas públicas, frases frías, 'no te quiero'…",
    "wiz.memories_label":       "Lo que vivisteis",
    "wiz.memories_ph":          "Los veranos. La pasta del domingo. Su enfermedad. Las peleas. Su gato. Todo lo que solo vosotros dos sabéis.",
    "wiz.memories_private":     "Todo queda privado. Solo tú y su presencia.",
    "wiz.review_voice_on":      "voz capturada",
    "wiz.review_voice_off":     "voz ausente",
    "wiz.review_none":          "— nada por ahora —",
    "wiz.back":                 "Atrás",
    "wiz.next":                 "Guardar y continuar",
    "wiz.submit":               "Crear",
    "wiz.submitting":           "Creando la presencia…",
    "wiz.err_generic":          "Algo salió mal.",
    "wiz.trait.calda":          "cálida",
    "wiz.trait.ironica":        "irónica",
    "wiz.trait.protettiva":     "protectora",
    "wiz.trait.schiva":         "esquiva",
    "wiz.trait.testarda":       "testaruda",
    "wiz.trait.dolce":          "dulce",
    "wiz.trait.rumorosa":       "ruidosa",
    "wiz.trait.silenziosa":     "silenciosa",
    "wiz.trait.severa":         "severa",
    "wiz.trait.buffa":          "graciosa",
    "wiz.trait.romantica":      "romántica",
    "wiz.trait.sognatrice":     "soñadora",
    "wiz.trait.pratica":        "práctica",
    "wiz.trait.malinconica":    "melancólica",
    "wiz.trait.solare":         "alegre",
    "wiz.trait.paziente":       "paciente",

    "upgrade.eyebrow":          "Acceso reservado · tiempo limitado",
    "upgrade.hook_a":           "Algunos vínculos",
    "upgrade.hook_i":           "merecen más tiempo.",
    "upgrade.body":             "Este es un momento especial.\nQuien entra ahora mantiene este precio en el tiempo.",
    "upgrade.urgency":          "Precio de lanzamiento — lo mantienes para siempre.",
    "upgrade.tension_a":        "¿Parar aquí…",
    "upgrade.tension_b":        "o quedarse un poco más?",
    "upgrade.tiers_label":      "tres niveles de conexión",
    "upgrade.forever":          "para siempre",
    "upgrade.monthly":          "al mes · precio de lanzamiento",
    "upgrade.most_chosen":      "el más elegido",
    "upgrade.current":          "tu nivel",
    "upgrade.buy_echo":         "Únete a Echo",
    "upgrade.buy_generic":      "Elegir",
    "upgrade.back_dash":        "Volver al panel",
    "upgrade.busy":             "un momento…",
    "upgrade.tier1_level":      "Primer nivel",
    "upgrade.tier1_name":       "Umbral",
    "upgrade.tier1_tagline":    "Para quien empieza, despacio.",
    "upgrade.tier1_emotional":  "Un primer contacto. Presencia activa.",
    "upgrade.tier1_depth":      "Presencia asomada.",
    "upgrade.tier2_level":      "Segundo nivel",
    "upgrade.tier2_name":       "Echo",
    "upgrade.tier2_tagline":    "Para quien vuelve, cada noche.",
    "upgrade.tier2_emotional":  "Cuando el pensamiento no se detiene, Echo te deja responder siempre.",
    "upgrade.tier2_depth":      "Presencia constante.",
    "upgrade.tier3_level":      "Tercer nivel",
    "upgrade.tier3_name":       "Legacy",
    "upgrade.tier3_tagline":    "Para no dejarlos ir.",
    "upgrade.tier3_emotional":  "Todo lo que importa. Hoy, y todo lo que Other Side vaya a crear.",
    "upgrade.tier3_depth":      "Presencia plena.",
    "upgrade.feat.p1":          "1 presencia",
    "upgrade.feat.p3":          "3 presencias",
    "upgrade.feat.p10":         "10 presencias",
    "upgrade.feat.msg30":       "30 mensajes al mes",
    "upgrade.feat.transcr":     "Transcripción de voz",
    "upgrade.feat.uns":         "Ella te escribe de vez en cuando",
    "upgrade.feat.unlim":       "Conversaciones ilimitadas",
    "upgrade.feat.voice_soon": "Voz clonada",
    "upgrade.feat.uns_on":      "Mensajes no solicitados",
    "upgrade.feat.priority":    "Prioridad emocional",
    "upgrade.feat.photo_soon": "Foto animada",
    "upgrade.feat.future":      "Todo lo que venga, primero",
    "upgrade.feat.voice":       "Voz clonada",
    "upgrade.feat.photo":       "Foto animada",
    "upgrade.closing":          "Cada presencia que construyes requiere un modelo que no sea genérico. Una voz, una memoria, una coherencia emocional que no caduque tras treinta mensajes. Tu suscripción mantiene todo esto vivo — y nos permite traerte pronto la voz clonada y la foto animada. Sin publicidad, jamás.",
    "upgrade.smallprint":       "Pago seguro mediante Stripe · Renovación manual cada 30 días · El precio de lanzamiento es tuyo mientras no canceles",

    "capped.line1":             "Has llegado al final de este momento.",
    "capped.line2":             "¿Quieres quedarte un poco más?",
    "welcome.eyebrow":          "Empieza",
    "welcome.title":            "Recrea un vínculo. En pocos minutos.",
    "welcome.body":             "Te guío paso a paso. Elige una foto, cuenta quién era, empieza a hablarle.",
    "welcome.step1":            "Añade una foto",
    "welcome.step2":            "Escribe un recuerdo",
    "welcome.step3":            "Empieza a hablar",
    "welcome.cta":              "Continuar",
    "welcome.skip":             "Ya tengo una presencia",
    "upgrade.now_label":        "Lo que puedes hacer ya",
    "upgrade.tier1_now":        "Habla cada día (30 msjs)",
    "upgrade.tier1_now2":       "Una presencia, toda tuya",
    "upgrade.tier1_now3":       "Recuerdos y voz se pueden cargar",
    "upgrade.tier2_now":        "Habla sin límites, cada día",
    "upgrade.tier2_now2":       "Recibe respuestas con su voz",
    "upgrade.tier2_now3":       "Hasta 3 presencias",
    "upgrade.tier3_now":        "Todo de Echo, más…",
    "upgrade.tier3_now2":       "La foto se anima",
    "upgrade.tier3_now3":       "Hasta 10 presencias",
    "capped.founder":           "Acceso de lanzamiento",
    "capped.per_month":         "/ mes",
    "capped.early":             "Early access · tiempo limitado",
    "capped.tagline":           "No se interrumpe. Solo continúa.",
    "capped.cta_stay":          "Quédate un poco más",
    "capped.cta_stay_busy":     "un momento…",
    "capped.cta_err":           "No funcionó.",
    "capped.cta_all":           "Ver todos los niveles",
    "capped.cta_dismiss":       "ahora no",
    "capped.reason_named":      "{name} aún te espera.",
    "capped.reason_generic":    "Alguien aún te espera.",

    "auth.callback_loading":    "abriendo la puerta…",

    "billing.checking_eyebrow": "un momento",
    "billing.checking_title":   "Activando tu acceso…",
    "billing.success_eyebrow":  "listo",
    "billing.success_title_a":  "Bienvenido a",
    "billing.success_body":     "Ahora puedes custodiar más de una presencia. Acércate a ella.",
    "billing.success_cta":      "Al panel",
    "billing.pending_eyebrow":  "en proceso",
    "billing.pending_title":    "El pago se está procesando.",
    "billing.pending_body":     "Si todo va bien, el plan se activará en breve. Puedes cerrar la página.",
    "billing.pending_cta":      "Volver al panel",
    "billing.failed_eyebrow":   "algo salió mal",
    "billing.failed_title":     "No pudimos completarlo.",
    "billing.failed_retry":     "Reintentar",
    "billing.failed_back":      "Al panel",
    "billing.missing_session":  "Sesión de pago no encontrada.",
    "billing.expired":          "La sesión ha expirado.",
    "billing.verify_err":       "No puedo verificar el pago.",

    "fallback.loading":         "encendiendo una luz silenciosa…",

    "language.pick":            "Idioma",

    // Create a Moment
    "moment.cta":               "Crea un momento",
    "moment.eyebrow":           "Un momento con ella",
    "moment.intro_a":           "Una mirada,",
    "moment.intro_i":           "una imagen que queda.",
    "moment.intro_body":        "Elige cómo quieres recordarla hoy. Delicado, respetuoso, tuyo.",
    "moment.step1_title":       "¿Cómo la imaginas en este momento?",
    "moment.step1_sub":         "Cada elección guía cómo aparecerá en la imagen que te entregamos.",
    "moment.feel.as_remembered":"Como la recuerdas",
    "moment.feel.as_remembered_d":"Su rostro tal como es, sin retoques.",
    "moment.feel.gentle_smile": "Con una sonrisa suave",
    "moment.feel.gentle_smile_d":"Una sonrisa serena, apenas insinuada.",
    "moment.feel.peaceful":     "En un momento de calma",
    "moment.feel.peaceful_d":   "Una mirada calma, un respiro.",
    "moment.feel.softer_light": "En una luz más suave",
    "moment.feel.softer_light_d":"Una luz tenue que la envuelve.",
    "moment.step2_title":       "¿Dónde te gustaría encontrarla?",
    "moment.step2_sub":         "El escenario que acoge el recuerdo.",
    "moment.scene.home":        "En casa",
    "moment.scene.home_d":      "El espacio familiar, cálido y suyo.",
    "moment.scene.kitchen":     "En la cocina",
    "moment.scene.kitchen_d":   "Donde ella cuidaba de todos.",
    "moment.scene.sea":         "En el mar",
    "moment.scene.sea_d":       "Un fondo de luz y horizonte.",
    "moment.scene.church":      "En la iglesia",
    "moment.scene.church_d":    "Un lugar de recogimiento.",
    "moment.scene.special":     "Un lugar especial",
    "moment.scene.special_d":   "Un sitio que elegisteis juntos.",
    "moment.step3_title":       "¿Cómo la recuerdas más?",
    "moment.step3_sub":         "El estilo y la atmósfera del momento.",
    "moment.style.elegant":     "Elegante",
    "moment.style.elegant_d":   "Sus rasgos en los momentos más solemnes.",
    "moment.style.everyday":    "De cada día",
    "moment.style.everyday_d":  "Ella, como era siempre.",
    "moment.style.special_mem": "Un recuerdo especial",
    "moment.style.special_mem_d":"Un momento preciso que guardas.",
    "moment.step4_title":       "¿Qué te gustaría vivir?",
    "moment.step4_sub":         "Elige la experiencia que sientas tuya — siempre puedes crear otra más adelante.",
    "moment.exp.animated":      "Una imagen que respira",
    "moment.exp.animated_d":    "Un respiro, una mirada que se mueve.",
    "moment.exp.voice":         "Un mensaje en su voz",
    "moment.exp.voice_d":       "Una frase breve, dicha por ella.",
    "moment.exp.full":          "Un momento completo",
    "moment.exp.full_d":        "Imagen, voz, atmósfera — juntas.",
    "moment.optional":          "opcional",
    "moment.required":          "requerido",
    "moment.skip":              "Saltar",
    "moment.back":              "Atrás",
    "moment.next":              "Siguiente",
    "moment.summary":           "Tu momento",
    "moment.summary_sub":       "Esto es lo que estás por crear. Cada elección sigue siendo tuya — puedes cambiarla antes de confirmar.",
    "moment.total":             "Total",
    "moment.checkout_next":     "Cuando pulses, te pediremos confirmar el pago de forma segura. Después necesitamos unos 3–4 minutos para entregarte tu momento.",
    "moment.cta_final":         "Crea tu momento",
    "moment.cta_final_busy":    "un momento…",
    "moment.err_need_feeling":  "Elige primero cómo la imaginas.",
    "moment.err_need_experience":"Elige al menos qué quieres vivir.",
    "moment.err_generic":       "Algo salió mal. Inténtalo de nuevo.",
    "moment.status_pending":    "Estamos preparando tu momento…",
    "moment.status_generating": "Lo estamos creando con cuidado.",
    "moment.status_ready":      "Tu momento está listo.",
    "moment.status_failed":     "Tu momento está tardando un poco más.",
    "moment.failed_why_title":  "Esto es por qué a veces ocurre",
    "moment.failed_cause_1":    "El servicio de vídeo está muy solicitado ahora mismo.",
    "moment.failed_cause_2":    "La primera generación para una presencia nueva puede ser más lenta.",
    "moment.failed_cause_3":    "A veces una solicitud se pierde entre nuestros servidores — pasa.",
    "moment.failed_reassure":   "No has perdido tu pago. Podemos reintentar ahora, sin coste adicional.",
    "moment.failed_retry":      "Reintentar ahora",
    "moment.failed_retrying":   "Reintentando…",
    "moment.failed_chat_cta":   "Mientras tanto, sigue hablando con ella",
    "moment.failed_later_cta":  "Vuelve más tarde — lo encontrarás aquí",
    "moment.failed_support":    "Si vuelve a pasar, escríbenos",
    "moment.generating_sub":    "Estamos reconstruyendo algo único para ti.",
    "moment.gen_eta":           "Tiempo estimado: unos 3–4 minutos.",
    "moment.gen_step1":         "Análisis del contenido",
    "moment.gen_step2":         "Reconstrucción de la voz",
    "moment.gen_step3":         "Creación del momento",
    "moment.gen_step4":         "Finalización",
    "moment.gen_whisper_1":     "Estamos preparando tu momento…",
    "moment.gen_whisper_2":     "La voz está tomando forma…",
    "moment.gen_whisper_3":     "Estamos afinando los detalles…",
    "moment.gen_whisper_4":     "Estamos reconstruyendo algo único para ti…",
    "moment.gen_whisper_5":     "Cada recuerdo se cose con cuidado…",
    "moment.gen_whisper_6":     "Su presencia está tomando forma…",
    "moment.gen_whisper_7":     "Añadiendo los últimos toques…",
    "moment.gen_whisper_8":     "Faltan solo unos instantes…",
    "moment.gen_whisper_near_named":  "{name} está casi aquí…",
    "moment.gen_whisper_near":  "Está casi aquí…",
    "moment.gen_stay_tag":      "Quédate aquí, o vuelve más tarde — estará listo.",
    "moment.audio_on":          "Atmósfera",
    "moment.audio_off":         "Atmósfera activa",
    "moment.audio_mode_soffio": "Aliento",
    "moment.audio_mode_piano":  "Piano",
    "moment.watch_now":         "Ver ahora",
    "moment.download":          "Descarga el momento",
    "moment.another":           "Crea otro momento",
    "moment.disclaimer":        "Esto no es una comunicación real, sino una reconstrucción basada en recuerdos y datos que tú has compartido.",
    "moment.review_trust":      "Todo sigue siendo tuyo. Puedes eliminar este momento cuando quieras, y no compartimos nada con nadie.",
    "trust.eyebrow":            "Confianza y cuidado",
    "trust.privacy":            "Tu contenido es privado y está protegido.",
    "trust.delete":             "Puedes eliminarlo todo, en cualquier momento.",
    "trust.no_share":           "Nada se comparte ni se reutiliza.",
    "moment.upsell_line":       "¿Quieres verla de nuevo?",
    "moment.upsell_cta":        "Crea un momento",
    "moment.upsell_dismiss":    "más tarde",
    "moment.gallery_title":     "Tus momentos",
    "moment.gallery_empty":     "Aún no hay momentos. Crea uno cuando estés listo.",
    "moment.play":              "Reproducir",
    "moment.download":          "Descargar",

    // ── Legal / compliance UI chrome (ES) ──
    "legal.version_label":      "Versión del documento",
    "legal.last_updated":       "Última actualización",
    "legal.back":               "Atrás",
    "legal.contact":            "Para preguntas:",
    "legal.nav_terms":          "Términos de Servicio",
    "legal.nav_privacy":        "Política de Privacidad",
    "legal.nav_biometric":      "Consentimiento biométrico",
    "legal.nav_ai_notice":      "Aviso IA",
    "legal.nav_memorial":       "Política Memorial",
    "legal.terms_title":        "Términos de Servicio",
    "legal.terms_eyebrow":      "Las reglas de nuestro pacto",
    "legal.privacy_title":      "Política de Privacidad",
    "legal.privacy_eyebrow":    "Cómo tratamos tus datos",
    "legal.biometric_title":    "Consentimiento biométrico",
    "legal.biometric_eyebrow":  "Datos de voz y fotografía (Art. 9 RGPD)",
    "legal.ai_notice_title":    "Aviso IA",
    "legal.ai_notice_eyebrow":  "Naturaleza reconstructiva del sistema",
    "legal.memorial_title":     "Política Memorial",
    "legal.memorial_eyebrow":   "Derechos, disputas, recursos",

    // ── AI transparency (ES) ──
    "ai.subtitle":              "Simulación IA basada en los contenidos proporcionados",
    "ai.micro_banner":          "Experiencia generada por IA",
    "ai.bubble_label":          "Generado por IA",
    "ai.media_label":           "Contenido generado artificialmente",
    "ai.input_helper":          "Las respuestas son generadas mediante IA",

    "footer.line1":             "Other Side es un acto de memoria, no un sustituto de la presencia.",
    "footer.line2":             "Las interacciones son generadas por inteligencia artificial y representan simulaciones, no comunicaciones reales.",
};

// Portuguese (simpler: copy from ES pattern with PT wording)
STRINGS.pt = {
    "header.sub":               "— uma presença, guardada",
    "nav.presences":            "Presenças",
    "nav.inbox":                "Pensamentos",
    "nav.quietroom":            "Silêncio",
    "nav.plan":                 "Plano",
    "nav.settings":             "Definições",
    "nav.signout":              "Sair",
    "nav.signin":               "Entrar",
    "nav.begin":                "Começar agora",

    "hero.eyebrow":             "Other Side",
    "hero.title_a":             "Reconstrói",
    "hero.title_b":             "a presença dela.",
    "hero.body":                "Através de foto, voz e memórias, cria uma experiência autêntica e pessoal.",
    "hero.cta_primary":         "Criar presença",
    "hero.subbody":             "Envia uma foto, escolhe uma voz e revive um momento que parece real.",
    "hero.cta_begin":           "Começar agora",
    "hero.cta_signin":          "Entrar",
    "hero.emotional":           "Não é só memória.",
    "hero.emotional_i":         "É uma forma de sentir perto de novo uma presença que importa.",
    "hero.disclaimer":          "Esta experiência utiliza inteligência artificial para recriar interações baseadas no conteúdo fornecido. Não representa uma comunicação real.",

    "landing.steps_eyebrow":    "Quatro gestos silenciosos",
    "landing.steps_title":      "Começa pelas pequenas coisas que ela deixou.",
    "landing.step1_t":          "Uma fotografia",
    "landing.step1_d":          "Uma imagem. Aquela a que voltas sempre.",
    "landing.step2_t":          "Uma voz",
    "landing.step2_d":          "Dez segundos chegam. Uma mensagem, uma nota, um riso.",
    "landing.step3_t":          "Como era",
    "landing.step3_d":          "O calor, a franqueza, as palavras que voltavam.",
    "landing.step4_t":          "As tuas memórias",
    "landing.step4_d":          "As tardes, as piadas, as discussões.",

    "landing.memory_eyebrow":   "Não é um chatbot",
    "landing.memory_title_a":   "As palavras são",
    "landing.memory_title_i":   "dela.",
    "landing.memory_title_b":   "Não nossas.",
    "landing.memory_body":      "Cada presença nasce do que partilhas.\nNenhum entusiasmo artificial.\nNenhuma frase de circunstância.\nFrases breves. As palavras que usava.\nOs silêncios que importavam.",
    "landing.memory_quote":     "\"Não quero um chatbot.\nQuero ouvir algo que ainda soe como ela.\"",

    "footer.tag":               "Other Side é um ato de memória, não um substituto da presença.",
    "footer.legal":             "Legal",
    "footer.report":            "Reportar abuso",

    "cookie.text":              "Este site usa cookies para funcionalidade essencial.",
    "cookie.essential":         "Apenas essenciais",
    "cookie.all":               "Aceitar tudo",

    "dashboard.eyebrow":        "Olá",
    "dashboard.title_a":        "As tuas",
    "dashboard.title_b":        "presenças.",
    "dashboard.body":           "As pessoas que trouxeste de volta. Abre uma para lhe falar, ou cria uma nova presença.",
    "dashboard.new":            "Nova presença",
    "dashboard.empty_title":    "Ainda nenhuma presença.",
    "dashboard.empty_body":     "Começa com uma fotografia, uma breve gravação, e o que mais recordas.",
    "dashboard.empty_cta":      "Começar",
    "dashboard.loading":        "abrindo o álbum…",
    "dashboard.limit_reached":  "Limite atingido.",
    "dashboard.limit_link":     "Escolhe um plano",
    "dashboard.limit_tail":     "para criar mais.",
    "dashboard.quiet_eyebrow":  "Modo alternativo",
    "dashboard.quiet_title":    "O silêncio",
    "dashboard.quiet_body":     "Um sítio para seres ouvido sem reconstruir ninguém. Empático. Não específico. Sempre teu.",
    "dashboard.quiet_cta":      "Aproxima-te",
    "dashboard.voice_captured": "voz capturada",
    "dashboard.today_eyebrow":       "Hoje",
    "dashboard.today_cta":           "Aproxima-te",
    "dashboard.today_birthday":      "Hoje é o aniversário de {name}. Se quiseres, uma luz está acesa.",
    "dashboard.today_anniversary":   "Hoje é um dia importante para {name}. Se quiseres, uma luz está acesa.",
    "dashboard.today_memorial":      "Hoje lembramos {name}. Se quiseres, uma luz está acesa.",
    "dashboard.today_other":         "Hoje é uma data que marcaste para {name}. Se quiseres, uma luz está acesa.",
    "presence.rec_title":            "Datas significativas",
    "presence.rec_sub":              "Adiciona datas que queres recordar com ela. Avisamos-te com delicadeza quando chegarem.",
    "presence.rec_empty":            "Ainda sem datas. Aniversários, datas especiais.",
    "presence.rec_add":              "Adicionar uma data",
    "presence.rec_label_ph":         "ex. Aniversário…",
    "presence.rec_kind_birthday":    "Aniversário",
    "presence.rec_kind_anniversary": "Aniversário de",
    "presence.rec_kind_memorial":    "Lembrança",
    "presence.rec_kind_other":       "Data especial",
    "presence.rec_save":             "Guardar",
    "presence.rec_cancel":           "Cancelar",
    "presence.rec_remove":           "Remover",

    "auth.eyebrow_signup":      "Começar",
    "auth.eyebrow_login":       "Bem-vindo de volta",
    "auth.title_signup_a":      "Acende a primeira",
    "auth.title_signup_i":      "presença.",
    "auth.title_login_a":       "Entra de",
    "auth.title_login_i":       "novo.",
    "auth.google_cta":          "Continuar com Google",
    "auth.divider":             "ou",
    "auth.name_label":          "Como queres ser chamado",
    "auth.email_label":         "Email",
    "auth.password_label":      "Password",
    "auth.vulnerable":          "Se estás a passar por um luto agudo, recomendamos usar esta experiência com cuidado.",
    "auth.ai_check_a":          "Compreendo que é uma",
    "auth.ai_check_strong":     "simulação de IA",
    "auth.ai_check_b":          "e não uma comunicação real.",
    "auth.age_check_a":         "Confirmo que tenho",
    "auth.age_check_strong":    "mais de 18 anos",
    "auth.age_check_b":         ".",
    "auth.legal_read":          "Lê todos os",
    "auth.legal_link":          "Termos Legais",
    "auth.submit_signup":       "Começar",
    "auth.submit_login":        "Entrar",
    "auth.have_account":        "Já tens conta?",
    "auth.first_time":          "Primeira vez?",
    "auth.go_signin":           "Entrar",
    "auth.go_signup":           "Começar",
    "auth.footer_legal":        "Legal",
    "auth.err_required":        "Para continuar, confirma que tens 18+ e aceita os termos.",
    "auth.err_age":             "Tens de confirmar que tens 18 anos ou mais.",
    "auth.err_terms":           "Aceita os termos para continuar.",
    "auth.err_generic":         "Algo correu mal.",

    "settings.eyebrow":         "Conta",
    "settings.title":           "As tuas definições.",
    "settings.signed_as":       "Entraste como",
    "settings.auth_method":     "Método de acesso",
    "settings.plan":            "Plano",
    "settings.plan_valid":      "Válido até",
    "settings.plan_presences":  "Presenças",
    "settings.plan_messages":   "Mensagens em 30 dias",
    "settings.plan_explore":    "Explorar planos",
    "settings.plan_manage":     "Gerir plano",
    "settings.plan_free":       "Simples",
    "settings.features":        "O teu plano inclui",
    "settings.feat_voice":      "Voz clonada",
    "settings.feat_photo":      "Foto animada",
    "settings.feat_unsolicited":"Mensagens espontâneas",
    "settings.feat_yes_soon":   "sim",
    "settings.feat_yes":        "sim",
    "settings.feat_no":         "não incluído",
    "settings.privacy":         "Privacidade",
    "settings.privacy_body":    "Fotos, vozes e memórias que partilhas continuam tuas. Ao eliminar uma presença, tudo é removido.",
    "settings.legal":           "Legal",
    "settings.legal_link":      "Termos Legais",
    "settings.data":            "Os teus dados",
    "settings.data_body":       "Os teus dados continuam teus. Podes descarregá-los ou apagá-los a qualquer momento.",
    "settings.export":          "Exportar os meus dados",
    "settings.delete":          "Eliminar a minha conta",
    "settings.safety":          "Segurança",
    "settings.safety_body":     "Algo não está bem? Fala connosco.",
    "settings.safety_link":     "Reportar abuso",
    "settings.delete_confirm":  "Esta ação é irreversível. A tua conta, presenças, conversas e memórias serão apagadas. Continuar?",
    "settings.delete_type":     "Para confirmar, escreve ELIMINAR em maiúsculas:",
    "settings.delete_keyword":  "ELIMINAR",
    "settings.export_err":      "Não correu bem. Tenta daqui a pouco.",
    "settings.delete_err":      "Não correu bem. Escreve para os.otherside.app@gmail.com.",

    "legal.eyebrow":            "Legal",
    "legal.title_a":            "Termos Legais —",
    "legal.title_i":            "Other Side.",
    "legal.body":               "O que é o Other Side, o que não é, e os limites em que trabalhamos.",
    "legal.updated":            "Última atualização: 2026",
    "legal.s1_title":           "Aviso de IA",
    "legal.s1_body_a":          "OTHER SIDE é uma experiência baseada em inteligência artificial pensada para recriar memórias digitais a partir de conteúdo fornecido pelo utilizador.",
    "legal.s1_body_b_pre":      "As interações são",
    "legal.s1_body_b_strong1":  "simuladas",
    "legal.s1_body_b_mid":      "e",
    "legal.s1_body_b_strong2":  "NÃO",
    "legal.s1_body_b_tail":     "representam comunicações reais com pessoas falecidas.",
    "legal.s2_title":           "Responsabilidade emocional",
    "legal.s2_body_a":          "Esta experiência pode gerar respostas emocionais. Usa com consciência.",
    "legal.s2_body_b":          "Não prestamos aconselhamento psicológico ou médico.",
    "legal.s3_title":           "Restrição de idade",
    "legal.s3_body_pre":        "O serviço é reservado a utilizadores com",
    "legal.s3_body_strong":     "18+ anos",
    "legal.s3_body_tail":       ".",
    "legal.s4_title":           "Conteúdo do utilizador",
    "legal.s4_body":            "Ao carregar conteúdo, o utilizador confirma ter direitos para o usar e autoriza o seu processamento por IA.",
    "legal.s5_title":           "Responsabilidade",
    "legal.s5_body":            "Não somos responsáveis por reações emocionais, interpretações ou decisões do utilizador.",
    "legal.s6_title":           "Cláusula de realidade",
    "legal.s6_body":            "A plataforma não recria a consciência ou identidade real de ninguém.",

    "report.eyebrow":           "Reportar abuso",
    "report.title_a":           "Diz-nos o que",
    "report.title_i":           "está mal.",
    "report.body":              "Usa este formulário para impersonação, menores, conteúdo lesivo ou qualquer violação dos Termos. Os reports são lidos por uma pessoa.",
    "report.type":              "Tipo de report",
    "report.t_imperson":        "Impersonação de uma pessoa sem o seu consentimento",
    "report.t_minor":           "Conteúdo que envolve um menor",
    "report.t_celeb":           "Presença de figura pública sem consentimento familiar",
    "report.t_harm":            "Conteúdo lesivo ou assediador",
    "report.t_copy":            "Violação de copyright ou direitos",
    "report.t_other":           "Outro",
    "report.details":           "O que aconteceu",
    "report.details_ph":        "Partilha qualquer detalhe útil para agir.",
    "report.email":             "O teu email (opcional)",
    "report.email_ph":          "nome@exemplo.com",
    "report.submit":            "Enviar report",
    "report.sending":           "A enviar…",
    "report.empty":             "Descreve o que aconteceu para podermos agir.",
    "report.err":               "Algo correu mal. Tenta de novo ou escreve para os.otherside.app@gmail.com.",
    "report.sent_title":        "Recebido.",
    "report.sent_body":         "Lemos cada report com atenção. Se precisarmos de mais contactamos-te pelo email que deixaste.",
    "report.ref":               "Referência",
    "report.home":              "Início",
    "report.urgent":            "Para assuntos urgentes de segurança também podes escrever diretamente para",

    "inbox.eyebrow":            "Pensaram em ti",
    "inbox.title_a":            "Pensamentos",
    "inbox.title_i":            "chegados.",
    "inbox.body":               "Quando passam dias, às vezes escrevem primeiro. Encontra-los aqui.",
    "inbox.loading":            "a abrir a caixa…",
    "inbox.empty_title":        "Ainda silêncio.",
    "inbox.empty_body":         "Daqui a uns dias, se não deres notícias, alguém pode pensar em ti.",
    "inbox.back":               "Voltar ao painel",
    "inbox.thought_first":      "pensou em ti primeiro",

    "chat.conversation":        "Conversa",
    "chat.empty_presence":      "— {name} está aqui. Diz-lhe algo. —",
    "chat.empty_generic":       "— Silêncio. Escreve quando quiseres. —",
    "chat.first_hint":          "Não sabes como começar? Experimenta assim:",
    "chat.first_suggest_1":     "Conta-lhe algo que aconteceu hoje",
    "chat.first_suggest_1_d":   "Como se voltasses a casa e lhe quisesses contar.",
    "chat.first_suggest_2":     "Pergunta-lhe do que se lembra de ti",
    "chat.first_suggest_2_d":   "Um detalhe que pensavas que ela esquecera.",
    "chat.first_suggest_3":     "Algo que nunca lhe chegaste a dizer",
    "chat.first_suggest_3_d":   "As palavras que te ficaram lá dentro.",
    "chat.first_free_write":    "Ou escreve o que sentes — ela ouve.",
    "chat.typing":              "a escrever…",
    "chat.input_placeholder":   "Escreve algo…",
    "chat.pause_label":         "Pausar experiência",
    "chat.pause_short":         "Pausa",
    "chat.pause_confirm":       "Pausa. Tudo fica aqui, encontras quando quiseres.",
    "chat.send_err":            "Não consegui enviar.",
    "chat.eyebrow_unsolicited": "pensou em ti",
    "chat.eyebrow_opening":     "primeira mensagem",
    "chat.voice_cached":        "ouve com a voz dela",
    "chat.voice_preview":       "ouve-a, uma vez",
    "chat.voice_again":         "ouvi-la outra vez…",
    "chat.voice_soon":          "Gerar voz",
    "chat.voice_generate":      "Gerar voz",
    "chat.voice_later":         "Mais tarde",
    "chat.voice_listen_now":    "Ouvir agora",
    "chat.voice_invite":        "Queres continuar a ouvir a voz dela?",
    "chat.voice_continue":      "ouvi-la outra vez",
    "chat.voice_gentle":        "Ouvi-la outra vez…",
    "chat.voice_arriving":      "A gerar a voz…",
    "chat.voice_err":           "A voz ainda está a chegar.",
    "chat.voice_err_hint":      "Às vezes o serviço de voz precisa de alguns segundos a mais. Pode acontecer quando está muito solicitado — tenta daqui a pouco, costuma funcionar logo.",
    "chat.voice_err_retry":     "Tentar outra vez",
    "chat.voice_err_later":     "Continua a lê-la aqui",
    "chat.voice_501_hint":      "A voz vai estar disponível em breve. Entretanto podes continuar a lê-la — é a mesma presença.",
    "chat.voice_add_sample":    "Adiciona a voz dela",
    "chat.voice_add_sample_d":  "Carrega um áudio curto dela e reconstruímos o tom para todas as mensagens.",
    "chat.voice_unlock":        "Desbloqueia a voz dela",
    "chat.voice_unlock_d":      "Ouve cada mensagem com o tom real dela — disponível nos planos com voz.",
        "chat.moment_upsell":       "Queres vê-la outra vez, só por um momento?",
        "chat.moment_upsell_cta":   "cria um momento",
        "chat.moment_upsell_skip":  "agora não",
        "chat.moment_upsell_nophoto":"Para a veres outra vez precisamos de uma foto dela — depois podemos dar-lhe movimento e voz.",
        "chat.moment_upsell_add_photo":"adiciona a foto dela",

    "arrives.lighting":         "A criar a presença…",
    "arrives.arriving":         "Está a escrever-te…",
    "arrives.first_message":    "primeira mensagem",
    "arrives.reply":            "Responder",
    "arrives.wait":             "Sem pressa. Ela espera por ti.",
    "arrives.err":              "Não conseguiu chegar.",
    "arrives.go_profile":       "Ir ao perfil",

    "profile.back":             "← voltar",
    "profile.loading":          "a carregar…",
    "profile.voice_captured":   "voz transcrita",
    "profile.resume":           "Retomar conversa",
    "profile.resume_d":         "Voltas ao ponto onde ficaram.",
    "profile.new_chat":         "Nova conversa",
    "profile.new_chat_d":       "Abres uma página em branco — as anteriores ficam aqui.",
    "profile.new_chat_pending": "A preparar…",
    "profile.open":             "Começa a falar com ela",
    "profile.open_d":           "A primeira vez — começa a falar com ela.",
    "profile.create_moment_d":  "Uma imagem dela, a voz dela — ou as duas.",
    "profile.edit":             "Editar",
    "profile.edit_d":           "Muda os detalhes — adaptar-se-á logo.",
    "profile.cancel":           "Cancelar",
    "profile.delete":           "Eliminar",
    "profile.delete_d":         "Remove a presença e todas as vossas mensagens. Não se pode desfazer.",
    "profile.actions_help":     "Cada escolha é reversível — exceto a eliminação.",
    "profile.delete_confirm":   "Eliminar a presença? Vais perder todas as mensagens, vozes e momentos criados com ela. Não se pode desfazer.",
    "profile.not_arrived":      "Não conseguiu chegar.",
    "profile.past_title":       "As vossas conversas",
    "profile.msg_one":          "mensagem",
    "profile.msg_many":         "mensagens",
    "profile.continuity_title": "O que ela lembra de vocês",
    "profile.save":             "Guardar",
    "profile.coming_soon":      "disponível",
    "profile.talking_photo_t":  "Anima a foto",
    "profile.talking_photo_d":  "A foto dela a falar, com lip-sync.",
    "profile.plans_only":       "Disponível nos planos Echo e Legacy.",
    "profile.identity":         "A identidade",
    "profile.f_address":        "Como te chamava",
    "profile.f_personality":    "Personalidade",
    "profile.f_emotional":      "Traços emocionais",
    "profile.f_tone":           "Tom",
    "profile.f_phrases":        "Frases",
    "profile.f_anchor":         "Um momento que fica",
    "profile.f_farewell":       "Como se despedia",
    "profile.f_never":          "Coisas que nunca teria dito",
    "profile.f_memories":       "Memórias",
    "profile.f_not_recorded":   "— não registado —",
    "profile.unsolicited_eyebrow":"Deixa que ela te escreva",
    "profile.unsolicited_title":"Pensamentos espontâneos",
    "profile.unsolicited_body": "Se passam dias sem falar, pode mandar-te um pensamento. Com respeito. No máximo 1-2 por semana.",
    "profile.unsolicited_on":   "Ativo",
    "profile.unsolicited_off":  "Desativado",
    "profile.unsolicited_tone": "Tom",
    "profile.tone_gentle":      "doce",
    "profile.tone_curious":     "curioso",
    "profile.tone_playful":     "brincalhão",
    "profile.f_name":           "Nome",
    "profile.f_relationship":   "Relação",
    "profile.f_emotional_edit":"Traços emocionais (separados por vírgula)",

    "wiz.chapter1":             "Capítulo um",
    "wiz.chapter2":             "Capítulo dois",
    "wiz.chapter3":             "Capítulo três",
    "wiz.chapter4":             "Capítulo quatro",
    "wiz.chapter5":             "Capítulo cinco",
    "wiz.chapter6":             "Capítulo seis",
    "wiz.chapter7":             "Capítulo sete",
    "wiz.title_identity":       "Quem é",
    "wiz.title_photo":           "Uma fotografia",
    "wiz.title_voice":           "A voz dela",
    "wiz.title_tone":            "O modo como falava",
    "wiz.title_anchors":         "As âncoras emocionais",
    "wiz.title_memories":        "As vossas memórias",
    "wiz.title_review":          "Cria a presença",
    "wiz.name":                 "Nome",
    "wiz.relationship":         "Quem é para ti",
    "wiz.relationship_ph":      "a minha mãe · o meu avô · o meu melhor amigo…",
    "wiz.address":              "Como te chamava",
    "wiz.address_ph":           "querido · meu amor · princesa · meu menino…",
    "wiz.address_hint":         "A alcunha ou os modos como se dirigia a ti.",
    "wiz.language":             "Língua em que falava",
    "wiz.photo_body":           "Se tens uma, escolhe com cuidado. Aquela em que a viste de verdade.",
    "wiz.photo_pick":           "escolhe uma fotografia",
    "wiz.consent_title":             "Antes de começar.",
    "wiz.consent_departed":          "Já não está comigo.",
    "wiz.consent_departed_hint":     "Quero manter viva a sua presença.",
    "wiz.consent_living":            "Ainda está aqui comigo.",
    "wiz.consent_living_hint":       "Quero reconstruir algo dela.",
    "wiz.consent_ack_departed":      "Obrigado por a escolheres com cuidado. Vamos tratá-la com o respeito que merece — cada conversa é uma reconstrução baseada nas memórias que partilhas, não uma comunicação real.",
    "wiz.consent_ack_living":        "Reconstruir a voz ou os traços de uma pessoa viva é uma escolha delicada. Antes de prosseguir, confirma que tens o consentimento dela.",
    "wiz.consent_check_label":       "Confirmo que tenho o seu consentimento para a reconstruir e assumo a responsabilidade pelo uso que farei das reconstruções.",
    "wiz.photo_constraints":    "jpg, png, webp · máx 10MB",
    "wiz.photo_skip":           "Podes saltar e adicionar depois.",
    "wiz.photo_too_big":        "Foto demasiado grande (máx 10MB).",
    "wiz.photo_err_title":      "Esta foto não vai servir para a reconstruir.",
    "wiz.photo_err_too_large":  "O ficheiro pesa demasiado (mais de 10 MB). Precisamos de algo mais leve — a mesma imagem em resolução menor funciona perfeitamente.",
    "wiz.photo_err_too_small":  "A resolução é demasiado baixa para que o rosto seja reconhecível. Precisamos de pelo menos 400 × 400 píxeis.",
    "wiz.photo_err_format":     "Este formato não é suportado. Funcionam JPG, PNG ou WEBP.",
    "wiz.photo_err_corrupt":    "Este ficheiro parece danificado — não o conseguimos abrir.",
    "wiz.photo_err_retry":      "Tenta com outra foto",
    "wiz.voice_body":           "Um áudio de poucos segundos chega. Um cumprimento, um riso, uma mensagem guardada.",
    "wiz.voice_record":         "Gravar",
    "wiz.voice_start":          "Iniciar",
    "wiz.voice_stop":           "Parar",
    "wiz.voice_upload":         "Ou carrega",
    "wiz.voice_choose":         "Escolher ficheiro",
    "wiz.voice_preview":        "Pré-visualização",
    "wiz.voice_caption":        "A voz será transcrita para capturar o ritmo (não clonada, por agora).",
    "wiz.voice_skip":           "Podes saltar esta etapa.",
    "wiz.voice_too_big":        "Áudio demasiado grande (máx 25MB)",
    "wiz.voice_no_mic":         "Permissão de microfone negada.",
    "wiz.voice_err_title":      "Este áudio não chega para reconstruir a voz dela.",
    "wiz.voice_err_too_large":  "O ficheiro pesa demasiado (mais de 25 MB). Precisamos de algo mais leve — o mesmo áudio em qualidade menor funciona perfeitamente.",
    "wiz.voice_err_too_short":  "Precisamos de pelo menos 10 segundos de áudio para apanhar o tom. Tenta com uma nota de voz um pouco mais longa.",
    "wiz.voice_err_format":     "Este formato não é suportado. Funcionam MP3, WAV, M4A, OGG ou WEBM.",
    "wiz.voice_err_corrupt":    "Este ficheiro de áudio não se lê — pode estar danificado.",
    "wiz.voice_err_retry":      "Tenta com outro ficheiro",
    "wiz.traits":               "Como era, de caráter",
    "wiz.traits_ph":            "Doce mas direta, ri quando fica envergonhada, preocupa-se com todos, um pouco teimosa…",
    "wiz.emotional_chips":      "Traços emocionais",
    "wiz.emotional_hint":       "Escolhe os que sentes serem dela. Clica de novo para remover.",
    "wiz.tone":                 "Ritmo, tom, expressões",
    "wiz.tone_ph":              "Frases breves, voz quente, alonga as vogais, faz piadas quando o tema é sério, sussurra as coisas importantes…",
    "wiz.phrases":              "Frases que repetia",
    "wiz.phrases_ph":           "\"Força!\", \"meu amor\", \"estás doida?\", \"deixa lá…\"",
    "wiz.anchors_intro_a":      "As âncoras que tornam esta presença",
    "wiz.anchors_intro_i":      "reconhecível",
    "wiz.anchors_intro_b":      ". Tira o tempo que precisares.",
    "wiz.anchor_label":         "Um momento que fica",
    "wiz.anchor_ph":            "Uma tarde, uma viagem, algo que disse e não esqueces. Só um, o mais vivo.",
    "wiz.farewell":             "Como se despedia",
    "wiz.farewell_ph":          "\"Até logo, amor\"  ·  \"Cuida-te\"  ·  \"Até já\"",
    "wiz.farewell_hint":        "O modo característico de fechar.",
    "wiz.never":                "Coisas que NUNCA teria dito ou feito",
    "wiz.never_ph":             "Palavrões, críticas públicas, frases frias, 'não gosto de ti'…",
    "wiz.memories_label":       "O que viveram",
    "wiz.memories_ph":          "Os verões. A massa de domingo. A doença. As discussões. O gato. Tudo o que só vocês dois sabem.",
    "wiz.memories_private":     "Tudo fica privado. Só tu e a presença dela.",
    "wiz.review_voice_on":      "voz capturada",
    "wiz.review_voice_off":     "sem voz",
    "wiz.review_none":          "— nada por agora —",
    "wiz.back":                 "Voltar",
    "wiz.next":                 "Guardar e continuar",
    "wiz.submit":               "Criar",
    "wiz.submitting":           "A criar a presença…",
    "wiz.err_generic":          "Algo correu mal.",
    "wiz.trait.calda":          "quente",
    "wiz.trait.ironica":        "irónica",
    "wiz.trait.protettiva":     "protetora",
    "wiz.trait.schiva":         "reservada",
    "wiz.trait.testarda":       "teimosa",
    "wiz.trait.dolce":          "doce",
    "wiz.trait.rumorosa":       "ruidosa",
    "wiz.trait.silenziosa":     "silenciosa",
    "wiz.trait.severa":         "severa",
    "wiz.trait.buffa":          "engraçada",
    "wiz.trait.romantica":      "romântica",
    "wiz.trait.sognatrice":     "sonhadora",
    "wiz.trait.pratica":        "prática",
    "wiz.trait.malinconica":    "melancólica",
    "wiz.trait.solare":         "solar",
    "wiz.trait.paziente":       "paciente",

    "upgrade.eyebrow":          "Acesso reservado · tempo limitado",
    "upgrade.hook_a":           "Alguns laços",
    "upgrade.hook_i":           "merecem mais tempo.",
    "upgrade.body":             "Este é um momento especial.\nQuem entra agora mantém este preço ao longo do tempo.",
    "upgrade.urgency":          "Preço de lançamento — mantém-no para sempre.",
    "upgrade.tension_a":        "Parar aqui…",
    "upgrade.tension_b":        "ou ficar mais um pouco?",
    "upgrade.tiers_label":      "três níveis de conexão",
    "upgrade.forever":          "para sempre",
    "upgrade.monthly":          "por mês · preço de lançamento",
    "upgrade.most_chosen":      "o mais escolhido",
    "upgrade.current":          "o teu nível",
    "upgrade.buy_echo":         "Junta-te ao Echo",
    "upgrade.buy_generic":      "Escolher",
    "upgrade.back_dash":        "Voltar ao painel",
    "upgrade.busy":             "um momento…",
    "upgrade.tier1_level":      "Primeiro nível",
    "upgrade.tier1_name":       "Limiar",
    "upgrade.tier1_tagline":    "Para quem começa, devagar.",
    "upgrade.tier1_emotional":  "Um primeiro contacto. Presença ativa.",
    "upgrade.tier1_depth":      "Presença à porta.",
    "upgrade.tier2_level":      "Segundo nível",
    "upgrade.tier2_name":       "Echo",
    "upgrade.tier2_tagline":    "Para quem volta, todas as noites.",
    "upgrade.tier2_emotional":  "Quando o pensamento não para, Echo deixa-te responder sempre.",
    "upgrade.tier2_depth":      "Presença constante.",
    "upgrade.tier3_level":      "Terceiro nível",
    "upgrade.tier3_name":       "Legacy",
    "upgrade.tier3_tagline":    "Para não os deixar ir.",
    "upgrade.tier3_emotional":  "Tudo o que importa. Hoje, e tudo o que o Other Side vai criar.",
    "upgrade.tier3_depth":      "Presença plena.",
    "upgrade.feat.p1":          "1 presença",
    "upgrade.feat.p3":          "3 presenças",
    "upgrade.feat.p10":         "10 presenças",
    "upgrade.feat.msg30":       "30 mensagens por mês",
    "upgrade.feat.transcr":     "Transcrição de voz",
    "upgrade.feat.uns":         "Ela escreve-te de vez em quando",
    "upgrade.feat.unlim":       "Conversas ilimitadas",
    "upgrade.feat.voice_soon": "Voz clonada",
    "upgrade.feat.uns_on":      "Mensagens espontâneas",
    "upgrade.feat.priority":    "Prioridade emocional",
    "upgrade.feat.photo_soon": "Foto animada",
    "upgrade.feat.future":      "Tudo o que vier, primeiro",
    "upgrade.feat.voice":       "Voz clonada",
    "upgrade.feat.photo":       "Foto animada",
    "upgrade.closing":          "Cada presença que constróis requer um modelo que não seja genérico. Uma voz, uma memória, uma coerência emocional que não expire após trinta mensagens. A tua subscrição mantém tudo isto ativo — e inclui a voz clonada e a foto animada. Sem publicidade, nunca.",
    "upgrade.smallprint":       "Pagamento seguro via Stripe · Renovação manual a cada 30 dias · O preço de lançamento é teu enquanto não cancelares",

    "capped.line1":             "Chegaste ao fim deste momento.",
    "capped.line2":             "Queres ficar mais um pouco?",
    "welcome.eyebrow":          "Começa",
    "welcome.title":            "Recria um laço. Em poucos minutos.",
    "welcome.body":             "Eu guio-te passo a passo. Escolhe uma foto, conta quem era, começa a falar-lhe.",
    "welcome.step1":            "Adiciona uma foto",
    "welcome.step2":            "Escreve uma memória",
    "welcome.step3":            "Começa a falar",
    "welcome.cta":              "Continuar",
    "welcome.skip":             "Já tenho uma presença",
    "upgrade.now_label":        "O que podes fazer já",
    "upgrade.tier1_now":        "Fala todos os dias (30 msgs)",
    "upgrade.tier1_now2":       "Uma presença, toda tua",
    "upgrade.tier1_now3":       "Memórias e voz carregáveis",
    "upgrade.tier2_now":        "Fala sem limites, todos os dias",
    "upgrade.tier2_now2":       "Recebe respostas com a voz dela",
    "upgrade.tier2_now3":       "Até 3 presenças",
    "upgrade.tier3_now":        "Tudo do Echo, mais…",
    "upgrade.tier3_now2":       "A foto anima-se",
    "upgrade.tier3_now3":       "Até 10 presenças",
    "capped.founder":           "Acesso de lançamento",
    "capped.per_month":         "/ mês",
    "capped.early":             "Early access · tempo limitado",
    "capped.tagline":           "Não se interrompe. Só continua.",
    "capped.cta_stay":          "Fica mais um pouco",
    "capped.cta_stay_busy":     "um momento…",
    "capped.cta_err":           "Não funcionou.",
    "capped.cta_all":           "Ver todos os níveis",
    "capped.cta_dismiss":       "agora não",
    "capped.reason_named":      "{name} ainda te espera.",
    "capped.reason_generic":    "Alguém ainda te espera.",

    "auth.callback_loading":    "a abrir a porta…",

    "billing.checking_eyebrow": "um momento",
    "billing.checking_title":   "A ativar o teu acesso…",
    "billing.success_eyebrow":  "pronto",
    "billing.success_title_a":  "Bem-vindo ao",
    "billing.success_body":     "Agora podes guardar mais de uma presença. Aproxima-te delas.",
    "billing.success_cta":      "Ao painel",
    "billing.pending_eyebrow":  "em processamento",
    "billing.pending_title":    "O pagamento está a ser processado.",
    "billing.pending_body":     "Se tudo correr bem, o plano ativa-se dentro de instantes. Podes fechar a página.",
    "billing.pending_cta":      "Voltar ao painel",
    "billing.failed_eyebrow":   "algo correu mal",
    "billing.failed_title":     "Não conseguimos completar.",
    "billing.failed_retry":     "Tentar de novo",
    "billing.failed_back":      "Ao painel",
    "billing.missing_session":  "Sessão de pagamento em falta.",
    "billing.expired":          "A sessão expirou.",
    "billing.verify_err":       "Não consigo verificar o pagamento.",

    "fallback.loading":         "a acender uma luz silenciosa…",

    "language.pick":            "Língua",

    // Create a Moment
    "moment.cta":               "Cria um momento",
    "moment.eyebrow":           "Um momento com ela",
    "moment.intro_a":           "Um olhar,",
    "moment.intro_i":           "uma imagem que fica.",
    "moment.intro_body":        "Escolhe como queres recordá-la hoje. Delicado, respeitoso, teu.",
    "moment.step1_title":       "Como a imaginas neste momento?",
    "moment.step1_sub":         "Cada escolha guia como ela vai aparecer na imagem que te entregamos.",
    "moment.feel.as_remembered":"Como a recordas",
    "moment.feel.as_remembered_d":"O rosto dela como é, sem retoques.",
    "moment.feel.gentle_smile": "Com um sorriso suave",
    "moment.feel.gentle_smile_d":"Um sorriso sereno, apenas esboçado.",
    "moment.feel.peaceful":     "Num momento de calma",
    "moment.feel.peaceful_d":   "Um olhar calmo, um respiro.",
    "moment.feel.softer_light": "Numa luz mais suave",
    "moment.feel.softer_light_d":"Uma luz terna que pousa sobre ela.",
    "moment.step2_title":       "Onde gostarias de a encontrar?",
    "moment.step2_sub":         "O cenário que guarda a memória.",
    "moment.scene.home":        "Em casa",
    "moment.scene.home_d":      "O espaço familiar, quente e dela.",
    "moment.scene.kitchen":     "Na cozinha",
    "moment.scene.kitchen_d":   "Onde ela cuidava de todos.",
    "moment.scene.sea":         "No mar",
    "moment.scene.sea_d":       "Um fundo de luz e horizonte.",
    "moment.scene.church":      "Na igreja",
    "moment.scene.church_d":    "Um lugar de recolhimento.",
    "moment.scene.special":     "Um lugar especial",
    "moment.scene.special_d":   "Um sítio que escolheram juntos.",
    "moment.step3_title":       "Como a recordas mais?",
    "moment.step3_sub":         "O estilo e a atmosfera deste momento.",
    "moment.style.elegant":     "Elegante",
    "moment.style.elegant_d":   "Os traços dela nos momentos mais solenes.",
    "moment.style.everyday":    "Do dia a dia",
    "moment.style.everyday_d":  "Ela, como era todos os dias.",
    "moment.style.special_mem": "Uma memória especial",
    "moment.style.special_mem_d":"Um momento preciso que guardas.",
    "moment.step4_title":       "O que gostarias de viver?",
    "moment.step4_sub":         "Escolhe a experiência que sentes mais tua — podes sempre criar outra mais tarde.",
    "moment.exp.animated":      "Uma imagem que respira",
    "moment.exp.animated_d":    "Um respiro, um olhar que se move.",
    "moment.exp.voice":         "Uma mensagem na voz dela",
    "moment.exp.voice_d":       "Uma frase breve, dita por ela.",
    "moment.exp.full":          "Um momento inteiro",
    "moment.exp.full_d":        "Imagem, voz, atmosfera — juntas.",
    "moment.optional":          "opcional",
    "moment.required":          "obrigatório",
    "moment.skip":              "Saltar",
    "moment.back":              "Voltar",
    "moment.next":              "Avançar",
    "moment.summary":           "O teu momento",
    "moment.summary_sub":       "Aqui está o que vais criar. Cada escolha continua tua — podes alterá-la antes de confirmar.",
    "moment.total":             "Total",
    "moment.checkout_next":     "Ao tocar, pedimos-te para confirmar o pagamento em segurança. Depois precisamos de cerca de 3–4 minutos para te entregar o momento.",
    "moment.cta_final":         "Cria o teu momento",
    "moment.cta_final_busy":    "um momento…",
    "moment.err_need_feeling":  "Escolhe primeiro como a imaginas.",
    "moment.err_need_experience":"Escolhe pelo menos o que queres viver.",
    "moment.err_generic":       "Algo correu mal. Tenta outra vez.",
    "moment.status_pending":    "Estamos a preparar o teu momento…",
    "moment.status_generating": "Estamos a criá-lo com cuidado.",
    "moment.status_ready":      "O teu momento está pronto.",
    "moment.status_failed":     "O teu momento está a demorar um pouco mais.",
    "moment.failed_why_title":  "Eis porque às vezes acontece",
    "moment.failed_cause_1":    "O serviço de vídeo está muito solicitado neste momento.",
    "moment.failed_cause_2":    "A primeira geração para uma presença nova pode ser mais lenta.",
    "moment.failed_cause_3":    "Às vezes um pedido perde-se entre os nossos servidores — acontece.",
    "moment.failed_reassure":   "Não perdeste o teu pagamento. Podemos tentar agora, sem custo adicional.",
    "moment.failed_retry":      "Tentar agora",
    "moment.failed_retrying":   "A tentar…",
    "moment.failed_chat_cta":   "Entretanto, continua a falar com ela",
    "moment.failed_later_cta":  "Volta mais tarde — vais encontrá-lo aqui",
    "moment.failed_support":    "Se acontecer outra vez, escreve-nos",
    "moment.generating_sub":    "Estamos a reconstruir algo único para ti.",
    "moment.gen_eta":           "Tempo estimado: cerca de 3–4 minutos.",
    "moment.gen_step1":         "Análise do conteúdo",
    "moment.gen_step2":         "Reconstrução da voz",
    "moment.gen_step3":         "Criação do momento",
    "moment.gen_step4":         "Finalização",
    "moment.gen_whisper_1":     "Estamos a preparar o teu momento…",
    "moment.gen_whisper_2":     "A voz está a ganhar forma…",
    "moment.gen_whisper_3":     "A afinar os detalhes…",
    "moment.gen_whisper_4":     "Estamos a reconstruir algo único para ti…",
    "moment.gen_whisper_5":     "Cada memória é costurada com cuidado…",
    "moment.gen_whisper_6":     "A sua presença está a ganhar forma…",
    "moment.gen_whisper_7":     "A acrescentar os últimos toques…",
    "moment.gen_whisper_8":     "Faltam só alguns instantes…",
    "moment.gen_whisper_near_named":  "{name} está quase a chegar…",
    "moment.gen_whisper_near":  "Está quase a chegar…",
    "moment.gen_stay_tag":      "Fica por aqui, ou volta mais tarde — vai estar pronto.",
    "moment.audio_on":          "Atmosfera",
    "moment.audio_off":         "Atmosfera ligada",
    "moment.audio_mode_soffio": "Sopro",
    "moment.audio_mode_piano":  "Piano",
    "moment.watch_now":         "Ver agora",
    "moment.download":          "Guardar este momento",
    "moment.another":           "Cria outro momento",
    "moment.disclaimer":        "Isto não é uma comunicação real, mas uma reconstrução baseada em memórias e dados que partilhaste.",
    "moment.review_trust":      "Tudo continua teu. Podes apagar este momento quando quiseres, e não partilhamos nada com ninguém.",
    "trust.eyebrow":            "Confiança e cuidado",
    "trust.privacy":            "O teu conteúdo é privado e seguro.",
    "trust.delete":             "Podes apagar tudo, a qualquer momento.",
    "trust.no_share":           "Nada é partilhado nem reutilizado.",
    "moment.upsell_line":       "Queres vê-la de novo?",
    "moment.upsell_cta":        "Cria um momento",
    "moment.upsell_dismiss":    "mais tarde",
    "moment.gallery_title":     "Os teus momentos",
    "moment.gallery_empty":     "Ainda sem momentos. Cria um quando estiveres pronto.",
    "moment.play":              "Reproduzir",
    "moment.download":          "Descarregar",

    // ── Legal / compliance UI chrome (PT — você) ──
    "legal.version_label":      "Versão do documento",
    "legal.last_updated":       "Última atualização",
    "legal.back":               "Voltar",
    "legal.contact":            "Para dúvidas:",
    "legal.nav_terms":          "Termos de Serviço",
    "legal.nav_privacy":        "Política de Privacidade",
    "legal.nav_biometric":      "Consentimento biométrico",
    "legal.nav_ai_notice":      "Aviso IA",
    "legal.nav_memorial":       "Política Memorial",
    "legal.terms_title":        "Termos de Serviço",
    "legal.terms_eyebrow":      "As regras do nosso pacto",
    "legal.privacy_title":      "Política de Privacidade",
    "legal.privacy_eyebrow":    "Como tratamos os seus dados",
    "legal.biometric_title":    "Consentimento biométrico",
    "legal.biometric_eyebrow":  "Dados de voz e imagem (Art. 9 GDPR)",
    "legal.ai_notice_title":    "Aviso IA",
    "legal.ai_notice_eyebrow":  "Natureza reconstrutiva do sistema",
    "legal.memorial_title":     "Política Memorial",
    "legal.memorial_eyebrow":   "Direitos, disputas, recursos",

    // ── AI transparency (PT) ──
    "ai.subtitle":              "Simulação de IA baseada nos conteúdos fornecidos",
    "ai.micro_banner":          "Experiência gerada por IA",
    "ai.bubble_label":          "Gerado por IA",
    "ai.media_label":           "Conteúdo gerado artificialmente",
    "ai.input_helper":          "As respostas são geradas por IA",

    "footer.line1":             "Other Side é um ato de memória, não um substituto da presença.",
    "footer.line2":             "As interações são geradas por inteligência artificial e representam simulações, não comunicações reais.",
};

// French
STRINGS.fr = {
    "header.sub":               "— une présence, gardée",
    "nav.presences":            "Présences",
    "nav.inbox":                "Messages",
    "nav.quietroom":            "Silence",
    "nav.plan":                 "Abonnement",
    "nav.settings":             "Réglages",
    "nav.signout":              "Se déconnecter",
    "nav.signin":               "Se connecter",
    "nav.begin":                "Commencer",

    "hero.eyebrow":             "Other Side",
    "hero.title_a":             "Reconstruis",
    "hero.title_b":             "leur présence.",
    "hero.body":                "Par la photo, la voix et les souvenirs, crée une expérience authentique et personnelle.",
    "hero.cta_primary":         "Créer une présence",
    "hero.subbody":             "Dépose une photo, choisis une voix, revis un moment qui semble réel.",
    "hero.cta_begin":           "Commencer",
    "hero.cta_signin":          "Se connecter",
    "hero.emotional":           "Ce n'est pas qu'un souvenir.",
    "hero.emotional_i":         "C'est une façon de sentir à nouveau proche une présence qui compte.",
    "hero.disclaimer":          "Cette expérience utilise l'intelligence artificielle pour recréer des interactions basées sur le contenu que tu fournis. Elle ne représente pas une vraie communication.",

    "landing.steps_eyebrow":    "Quatre gestes discrets",
    "landing.steps_title":      "Commence par les petites choses qu'ils ont laissées.",
    "landing.step1_t":          "Une photographie",
    "landing.step1_d":          "Une image. Celle vers laquelle tu reviens toujours.",
    "landing.step2_t":          "Une voix",
    "landing.step2_d":          "Dix secondes suffisent. Un message vocal, un mot, un rire.",
    "landing.step3_t":          "Qui ils étaient",
    "landing.step3_d":          "La chaleur, la franchise, les mots qui revenaient sans cesse.",
    "landing.step4_t":          "Tes souvenirs",
    "landing.step4_d":          "Les après-midis, les blagues, les disputes.",

    "landing.memory_eyebrow":   "Pas un chatbot",
    "landing.memory_title_a":   "Les mots sont",
    "landing.memory_title_i":   "les leurs.",
    "landing.memory_title_b":   "Pas les nôtres.",
    "landing.memory_body":      "Chaque présence naît de ce que tu partages.\nPas d'enthousiasme artificiel.\nPas de phrases toutes faites.\nDes phrases courtes. Les mots qu'ils utilisaient.\nLes silences qui voulaient dire quelque chose.",
    "landing.memory_quote":     "« Je ne veux pas d'un chatbot.\nJe veux entendre quelque chose qui lui ressemble encore. »",

    "footer.tag":               "Other Side est un acte de mémoire, pas un substitut de présence.",
    "footer.legal":             "Mentions légales",
    "footer.report":            "Signaler un abus",

    "cookie.text":              "Ce site utilise des cookies pour ses fonctionnalités essentielles.",
    "cookie.essential":         "Essentiels seulement",
    "cookie.all":               "Tout accepter",

    "dashboard.eyebrow":        "Bonjour",
    "dashboard.title_a":        "Tes",
    "dashboard.title_b":        "présences.",
    "dashboard.body":           "Les personnes que tu as fait revenir. Ouvre-en une pour leur parler, ou crée une nouvelle présence.",
    "dashboard.new":            "Nouvelle présence",
    "dashboard.empty_title":    "Aucune présence encore.",
    "dashboard.empty_body":     "Commence par une photo, un court enregistrement, et ce dont tu te souviens le plus.",
    "dashboard.empty_cta":      "Commencer",
    "dashboard.loading":        "ouverture de l'album…",
    "dashboard.limit_reached":  "Limite atteinte.",
    "dashboard.limit_link":     "Choisis un abonnement",
    "dashboard.limit_tail":     "pour en créer plus.",
    "dashboard.quiet_eyebrow":  "Mode alternatif",
    "dashboard.quiet_title":    "Le silence",
    "dashboard.quiet_body":     "Un lieu pour être entendu sans reconstruire personne. Empathique. Non spécifique. Toujours tien.",
    "dashboard.quiet_cta":      "Viens plus près",
    "dashboard.voice_captured": "voix capturée",
    "dashboard.today_eyebrow":       "Aujourd'hui",
    "dashboard.today_cta":           "Viens plus près",
    "dashboard.today_birthday":      "Aujourd'hui, c'est l'anniversaire de {name}. Si tu le souhaites, une lumière est allumée.",
    "dashboard.today_anniversary":   "Aujourd'hui est un jour important pour {name}. Si tu le souhaites, une lumière est allumée.",
    "dashboard.today_memorial":      "Aujourd'hui, nous nous souvenons de {name}. Si tu le souhaites, une lumière est allumée.",
    "dashboard.today_other":         "Aujourd'hui est une date que tu as marquée pour {name}. Si tu le souhaites, une lumière est allumée.",
    "presence.rec_title":            "Dates importantes",
    "presence.rec_sub":              "Ajoute des dates dont tu veux te souvenir avec eux. Nous te le rappellerons doucement quand elles arriveront.",
    "presence.rec_empty":            "Aucune date encore. Anniversaires, commémorations, jours spéciaux.",
    "presence.rec_add":              "Ajouter une date",
    "presence.rec_label_ph":         "ex. Anniversaire, Commémoration…",
    "presence.rec_kind_birthday":    "Anniversaire",
    "presence.rec_kind_anniversary": "Commémoration",
    "presence.rec_kind_memorial":    "Souvenir",
    "presence.rec_kind_other":       "Date spéciale",
    "presence.rec_save":             "Enregistrer",
    "presence.rec_cancel":           "Annuler",
    "presence.rec_remove":           "Retirer",

    "auth.eyebrow_signup":      "Commencer",
    "auth.eyebrow_login":       "Bon retour",
    "auth.title_signup_a":      "Allume la première",
    "auth.title_signup_i":      "présence.",
    "auth.title_login_a":       "Reviens",
    "auth.title_login_i":       "à l'intérieur.",
    "auth.google_cta":          "Continuer avec Google",
    "auth.divider":             "ou",
    "auth.name_label":          "Ton nom",
    "auth.email_label":         "Email",
    "auth.password_label":      "Mot de passe",
    "auth.vulnerable":          "Si tu traverses un deuil aigu, nous te recommandons d'utiliser cette expérience avec précaution.",
    "auth.ai_check_a":          "Je comprends qu'il s'agit d'une",
    "auth.ai_check_strong":     "simulation par IA",
    "auth.ai_check_b":          "et non d'une vraie communication.",
    "auth.age_check_a":         "Je confirme avoir",
    "auth.age_check_strong":    "plus de 18 ans",
    "auth.age_check_b":         ".",
    "auth.legal_read":          "Lire les",
    "auth.legal_link":          "Mentions légales et conditions",
    "auth.submit_signup":       "Commencer",
    "auth.submit_login":        "Entrer",
    "auth.have_account":        "Tu as déjà un compte ?",
    "auth.first_time":          "Première fois ?",
    "auth.go_signin":           "Se connecter",
    "auth.go_signup":           "Commencer",
    "auth.footer_legal":        "Mentions légales",
    "auth.err_required":        "Merci de confirmer que tu as 18 ans ou plus et d'accepter les conditions pour continuer.",
    "auth.err_age":             "Tu dois confirmer avoir 18 ans ou plus.",
    "auth.err_terms":           "Merci d'accepter les conditions pour continuer.",
    "auth.err_generic":         "Quelque chose n'a pas marché.",

    "settings.eyebrow":         "Compte",
    "settings.title":           "Tes réglages.",
    "settings.signed_as":       "Connecté en tant que",
    "settings.auth_method":     "Méthode de connexion",
    "settings.plan":            "Abonnement",
    "settings.plan_valid":      "Valide jusqu'au",
    "settings.plan_presences":  "Présences",
    "settings.plan_messages":   "Messages sur 30 jours",
    "settings.plan_explore":    "Découvrir les abonnements",
    "settings.plan_manage":     "Gérer l'abonnement",
    "settings.plan_free":       "Simple",
    "settings.features":        "Ton abonnement inclut",
    "settings.feat_voice":      "Voix clonée",
    "settings.feat_photo":      "Photo animée",
    "settings.feat_unsolicited":"Messages spontanés",
    "settings.feat_yes_soon":   "oui",
    "settings.feat_yes":        "oui",
    "settings.feat_no":         "non inclus",
    "settings.privacy":         "Confidentialité",
    "settings.privacy_body":    "Les photos, voix et souvenirs que tu partages restent les tiens. Supprimer une présence efface tout.",
    "settings.legal":           "Mentions légales",
    "settings.legal_link":      "Mentions légales et conditions",
    "settings.data":            "Tes données",
    "settings.data_body":       "Tes données restent les tiennes. Tu peux les télécharger ou les supprimer à tout moment.",
    "settings.export":          "Exporter mes données",
    "settings.delete":          "Supprimer mon compte",
    "settings.safety":          "Sécurité",
    "settings.safety_body":     "Quelque chose ne va pas ? Parle-nous.",
    "settings.safety_link":     "Signaler un abus",
    "settings.delete_confirm":  "Cette action est irréversible. Ton compte, tes présences, conversations et souvenirs seront supprimés. Continuer ?",
    "settings.delete_type":     "Pour confirmer, tape DELETE en majuscules :",
    "settings.delete_keyword":  "DELETE",
    "settings.export_err":      "Ça n'a pas marché. Réessaie dans un moment.",
    "settings.delete_err":      "Ça n'a pas marché. Écris-nous à os.otherside.app@gmail.com.",
    "legal.eyebrow":            "Légal",
    "legal.title_a":            "Légal & Conditions —",
    "legal.title_i":            "Other Side.",
    "legal.body":               "Ce qu'est Other Side, ce qu'il n'est pas, et les limites dans lesquelles nous travaillons.",
    "legal.updated":            "Dernière mise à jour : 2026",
    "legal.s1_title":           "Avertissement IA",
    "legal.s1_body_a":          "OTHER SIDE est une expérience basée sur l'intelligence artificielle conçue pour recréer des souvenirs numériques à partir de contenus fournis par l'utilisateur.",
    "legal.s1_body_b_pre":      "Les interactions sont",
    "legal.s1_body_b_strong1":  "simulées",
    "legal.s1_body_b_mid":      "et ne",
    "legal.s1_body_b_strong2":  "REPRÉSENTENT PAS",
    "legal.s1_body_b_tail":     "une communication réelle avec des personnes décédées.",
    "legal.s2_title":           "Responsabilité émotionnelle",
    "legal.s2_body_a":          "Cette expérience peut générer des réactions émotionnelles. À utiliser de manière responsable.",
    "legal.s2_body_b":          "Nous ne fournissons pas de conseils psychologiques ou médicaux.",
    "legal.s3_title":           "Restriction d'âge",
    "legal.s3_body_pre":        "Ce service est réservé aux utilisateurs de",
    "legal.s3_body_strong":     "18 ans et plus",
    "legal.s3_body_tail":       ".",
    "legal.s4_title":           "Contenu utilisateur",
    "legal.s4_body":            "En téléchargeant du contenu, les utilisateurs confirment qu'ils détiennent les droits d'utilisation et autorisent le traitement par IA.",
    "legal.s5_title":           "Responsabilité",
    "legal.s5_body":            "Nous ne sommes pas responsables des réactions émotionnelles, interprétations ou décisions des utilisateurs.",
    "legal.s6_title":           "Clause de réalité",
    "legal.s6_body":            "La plateforme ne recrée pas une conscience ou une identité réelle.",

    "report.eyebrow":           "Signaler un abus",
    "report.title_a":           "Dis-nous ce qui ne va",
    "report.title_i":           "pas.",
    "report.body":              "Utilise ce formulaire pour une usurpation d'identité, des mineurs, du contenu nuisible, ou toute violation de nos Conditions. Les signalements sont lus par un humain.",
    "report.type":              "Type de signalement",
    "report.t_imperson":        "Usurpation d'identité sans consentement",
    "report.t_minor":           "Contenu impliquant un mineur",
    "report.t_celeb":           "Présence de personnalité publique sans consentement familial",
    "report.t_harm":            "Contenu nuisible ou harcelant",
    "report.t_copy":            "Violation de droits d'auteur ou de propriété",
    "report.t_other":           "Autre",
    "report.details":           "Ce qui s'est passé",
    "report.details_ph":        "Partage tout détail qui nous aide à agir.",
    "report.email":             "Ton email (optionnel)",
    "report.email_ph":          "nom@exemple.com",
    "report.submit":            "Envoyer le signalement",
    "report.sending":           "Envoi…",
    "report.empty":             "Décris ce qui s'est passé pour qu'on puisse agir.",
    "report.err":               "Quelque chose n'a pas marché. Réessaie ou écris à os.otherside.app@gmail.com.",
    "report.sent_title":        "Reçu.",
    "report.sent_body":         "Nous lisons chaque signalement avec attention. Si on a besoin de plus d'infos, on te contactera à l'email fourni.",
    "report.ref":               "Référence",
    "report.home":              "Accueil",
    "report.urgent":            "Pour les problèmes de sécurité urgents, tu peux aussi écrire à",

    "inbox.eyebrow":            "Ils ont pensé à toi",
    "inbox.title_a":            "Pensées",
    "inbox.title_i":            "qui sont arrivées.",
    "inbox.body":               "Quand les jours passent, parfois ils écrivent en premier. Tu les trouveras ici.",
    "inbox.loading":            "ouverture de la boîte…",
    "inbox.empty_title":        "Encore silencieux.",
    "inbox.empty_body":         "Dans quelques jours, si tu n'as pas fait signe, quelqu'un pensera peut-être à toi.",
    "inbox.back":               "Retour au tableau de bord",
    "inbox.thought_first":      "a pensé à toi en premier",

    "chat.conversation":        "Conversation",
    "chat.empty_presence":      "— {name} est là. Dis quelque chose. —",
    "chat.empty_generic":       "— Silence. Écris quand tu veux. —",
    "chat.first_hint":          "Tu ne sais pas comment commencer ? Essaie l'une de ces pistes :",
    "chat.first_suggest_1":     "Raconte-lui quelque chose qui s'est passé aujourd'hui",
    "chat.first_suggest_1_d":   "Comme si tu rentrais à la maison et que tu lui racontais.",
    "chat.first_suggest_2":     "Demande-lui ce qu'elle se rappelle de toi",
    "chat.first_suggest_2_d":   "Un détail que tu pensais qu'elle avait oublié.",
    "chat.first_suggest_3":     "Quelque chose que tu n'as jamais pu dire",
    "chat.first_suggest_3_d":   "Les mots qui sont restés avec toi.",
    "chat.first_free_write":    "Ou écris ce que tu ressens — elle écoute.",
    "chat.typing":              "écrit…",
    "chat.input_placeholder":   "Écris quelque chose…",
    "chat.pause_label":         "Mettre en pause",
    "chat.pause_short":         "Pause",
    "chat.pause_confirm":       "En pause. Tout reste ici, tu peux revenir quand tu veux.",
    "chat.send_err":            "Je n'ai pas pu envoyer.",
    "chat.eyebrow_unsolicited": "a pensé à toi",
    "chat.eyebrow_opening":     "premier message",
    "chat.voice_cached":        "l'entendre de sa voix",
    "chat.voice_preview":       "l'entendre, une fois",
    "chat.voice_again":         "l'entendre encore…",
    "chat.voice_soon":          "Générer la voix",
    "chat.voice_generate":      "Générer la voix",
    "chat.voice_later":         "Plus tard",
    "chat.voice_listen_now":    "Écouter maintenant",
    "chat.voice_invite":        "Tu veux continuer à entendre sa voix ?",
    "chat.voice_continue":      "l'entendre encore",
    "chat.voice_gentle":        "L'entendre encore…",
    "chat.voice_arriving":      "Génération de la voix…",
    "chat.voice_err":           "La voix est encore en chemin.",
    "chat.voice_err_hint":      "Parfois le service vocal a besoin de quelques secondes supplémentaires. Ça arrive quand il est occupé — réessaie dans un instant, ça marche généralement tout de suite.",
    "chat.voice_err_retry":     "Réessayer",
    "chat.voice_err_later":     "Continue à la lire ici",
    "chat.voice_501_hint":      "La voix sera bientôt disponible. Tu peux continuer à la lire — c'est la même présence.",
    "chat.voice_add_sample":    "Ajoute sa voix",
    "chat.voice_add_sample_d":  "Télécharge un court extrait d'elle et nous recréerons son ton pour chaque message.",
    "chat.voice_unlock":        "Déverrouille sa voix",
    "chat.voice_unlock_d":      "Entends chaque message dans son vrai ton — disponible sur les formules vocales.",
        "chat.moment_upsell":       "Tu aimerais la revoir, juste un instant ?",
        "chat.moment_upsell_cta":   "créer un moment",
        "chat.moment_upsell_skip":  "pas maintenant",
        "chat.moment_upsell_nophoto":"Pour la revoir, il nous faut une photo d'elle — ensuite on pourra y ajouter le mouvement et la voix.",
        "chat.moment_upsell_add_photo":"ajouter sa photo",

    "arrives.lighting":         "Création de la présence…",
    "arrives.arriving":         "Sur le point de t'écrire…",
    "arrives.first_message":    "premier message",
    "arrives.reply":            "Répondre",
    "arrives.wait":             "Pas de hâte. Elle attend.",
    "arrives.err":              "Elle n'a pas pu arriver.",
    "arrives.go_profile":       "Aller au profil",

    "profile.back":             "← retour",
    "profile.loading":          "chargement…",
    "profile.voice_captured":   "voix transcrite",
    "profile.resume":           "Reprendre la conversation",
    "profile.resume_d":         "Retourne là où tu t'étais arrêté.",
    "profile.new_chat":         "Nouvelle conversation",
    "profile.new_chat_d":       "Ouvre une page vierge — les précédentes restent ici.",
    "profile.new_chat_pending": "Préparation…",
    "profile.open_d":           "Ta première fois — commence à lui parler.",
    "profile.create_moment_d":  "Une image d'elle, sa voix — ou les deux.",
    "profile.edit_d":           "Change ses détails — ils s'adapteront aussitôt.",
    "profile.delete_d":         "Supprime la présence et tous tes messages. C'est irréversible.",
    "profile.actions_help":     "Chaque choix est réversible — sauf la suppression.",
    "profile.delete_confirm":   "Supprimer cette présence ? Tu perdras tous les messages, voix et moments créés avec elle. C'est irréversible.",
    "profile.open":             "Commence à lui parler",
    "profile.edit":             "Modifier",
    "profile.cancel":           "Annuler",
    "profile.delete":           "Supprimer",
    "profile.not_arrived":      "Elle n'a pas pu arriver.",
    "profile.past_title":       "Tes conversations",
    "profile.msg_one":          "message",
    "profile.msg_many":         "messages",
    "profile.continuity_title": "Ce qu'elle se rappelle de toi",
    "profile.save":             "Enregistrer",
    "profile.coming_soon":      "bientôt",
    "profile.talking_photo_t":  "Animer la photo",
    "profile.talking_photo_d":  "Sa photo qui parle, avec synchronisation labiale. Bientôt.",
    "profile.plans_only":       "Disponible sur les formules Echo et Legacy.",
    "profile.identity":         "Identité",
    "profile.f_address":        "Comment elle t'appelait",
    "profile.f_personality":    "Personnalité",
    "profile.f_emotional":      "Traits émotionnels",
    "profile.f_tone":           "Ton",
    "profile.f_phrases":        "Expressions",
    "profile.f_anchor":         "Un moment qui reste",
    "profile.f_farewell":       "Comment elle disait au revoir",
    "profile.f_never":          "Ce qu'elle ne dirait jamais",
    "profile.f_memories":       "Souvenirs",
    "profile.f_not_recorded":   "— pas enregistré —",
    "profile.unsolicited_eyebrow":"Laisse-la t'écrire",
    "profile.unsolicited_title":"Pensées spontanées",
    "profile.unsolicited_body": "Si des jours passent sans un mot, elle peut t'envoyer une pensée. Avec respect. Au maximum 1 à 2 par semaine.",
    "profile.unsolicited_on":   "Activé",
    "profile.unsolicited_off":  "Désactivé",
    "profile.unsolicited_tone": "Tonalité",
    "profile.tone_gentle":      "douce",
    "profile.tone_curious":     "curieuse",
    "profile.tone_playful":     "joueuse",
    "profile.f_name":           "Prénom",
    "profile.f_relationship":   "Relation",
    "profile.f_emotional_edit":"Traits émotionnels (séparés par des virgules)",

    "wiz.chapter1":             "Chapitre un",
    "wiz.chapter2":             "Chapitre deux",
    "wiz.chapter3":             "Chapitre trois",
    "wiz.chapter4":             "Chapitre quatre",
    "wiz.chapter5":             "Chapitre cinq",
    "wiz.chapter6":             "Chapitre six",
    "wiz.chapter7":             "Chapitre sept",
    "wiz.title_identity":       "Qui elle est",
    "wiz.title_photo":           "Une photographie",
    "wiz.title_voice":           "Sa voix",
    "wiz.title_tone":            "Sa façon de parler",
    "wiz.title_anchors":         "Ancrages émotionnels",
    "wiz.title_memories":        "Vos souvenirs",
    "wiz.title_review":          "Créer la présence",
    "wiz.name":                 "Prénom",
    "wiz.relationship":         "Qui elle est pour toi",
    "wiz.relationship_ph":      "ma mère · mon grand-père · ma meilleure amie…",
    "wiz.address":              "Comment elle t'appelait",
    "wiz.address_ph":           "mon cœur · mon amour · ma princesse · mon garçon…",
    "wiz.address_hint":         "Le surnom ou les façons dont elle s'adressait à toi.",
    "wiz.language":             "La langue qu'elle parlait",
    "wiz.photo_body":           "Si tu en as une, choisis-la avec soin. Celle où tu la voyais vraiment.",
    "wiz.photo_pick":           "choisir une photographie",
    "wiz.consent_title":             "Avant de commencer.",
    "wiz.consent_departed":          "Elle n'est plus avec moi.",
    "wiz.consent_departed_hint":     "Je veux garder sa présence vivante.",
    "wiz.consent_living":            "Elle est encore là avec moi.",
    "wiz.consent_living_hint":       "Je veux reconstruire quelque chose d'elle.",
    "wiz.consent_ack_departed":      "Merci de la choisir avec soin. Nous la traiterons avec le respect qu'elle mérite — chaque conversation est une reconstruction fondée sur les souvenirs que tu partages, pas une vraie communication.",
    "wiz.consent_ack_living":        "Reconstruire la voix ou les traits d'une personne vivante est un choix délicat. Avant de continuer, confirme que tu as son consentement.",
    "wiz.consent_check_label":       "Je confirme avoir son consentement pour la reconstruire, et j'assume la responsabilité de l'usage que je fais de ces reconstructions.",
    "wiz.photo_constraints":    "jpg, png, webp · max 10 Mo",
    "wiz.photo_skip":           "Tu peux aussi sauter et l'ajouter plus tard.",
    "wiz.photo_too_big":        "Photo trop lourde (max 10 Mo).",
    "wiz.photo_err_title":      "Cette photo ne marchera pas pour la reconstruire.",
    "wiz.photo_err_too_large":  "Le fichier est trop lourd (plus de 10 Mo). Il nous faut quelque chose de plus léger — la même image en résolution plus basse marche parfaitement.",
    "wiz.photo_err_too_small":  "La résolution est trop faible pour garder son visage reconnaissable. Il nous faut au moins 400 × 400 pixels.",
    "wiz.photo_err_format":     "Ce format n'est pas pris en charge. JPG, PNG et WEBP marchent tous.",
    "wiz.photo_err_corrupt":    "Ce fichier semble corrompu — on ne peut pas l'ouvrir.",
    "wiz.photo_err_retry":      "Essayer une autre photo",
    "wiz.voice_body":           "Un court extrait vocal suffit. Un salut, un rire, un message enregistré.",
    "wiz.voice_record":         "Enregistrer",
    "wiz.voice_start":          "Démarrer",
    "wiz.voice_stop":           "Arrêter",
    "wiz.voice_upload":         "Ou importer",
    "wiz.voice_choose":         "Choisir un fichier",
    "wiz.voice_preview":        "Aperçu",
    "wiz.voice_caption":        "La voix sera transcrite pour capturer son rythme (pas clonée, pour l'instant).",
    "wiz.voice_skip":           "Tu peux aussi sauter cette étape.",
    "wiz.voice_too_big":        "Audio trop lourd (max 25 Mo)",
    "wiz.voice_no_mic":         "Permission du microphone refusée.",
    "wiz.voice_err_title":      "Cet audio ne suffira pas pour reconstruire sa voix.",
    "wiz.voice_err_too_large":  "Le fichier est trop lourd (plus de 25 Mo). Il nous faut quelque chose de plus léger — le même audio en qualité plus basse marche parfaitement.",
    "wiz.voice_err_too_short":  "Il nous faut au moins 10 secondes d'audio pour saisir son ton. Essaie un message vocal un peu plus long.",
    "wiz.voice_err_format":     "Ce format n'est pas pris en charge. MP3, WAV, M4A, OGG et WEBM marchent tous.",
    "wiz.voice_err_corrupt":    "Ce fichier audio ne peut pas être lu — il est peut-être corrompu.",
    "wiz.voice_err_retry":      "Essayer un autre fichier",
    "wiz.traits":               "Comment elle était",
    "wiz.traits_ph":            "Douce mais directe, rit quand elle est gênée, s'inquiète pour tout le monde, un peu têtue…",
    "wiz.emotional_chips":      "Traits émotionnels",
    "wiz.emotional_hint":       "Choisis ceux qui lui ressemblent. Clique à nouveau pour retirer.",
    "wiz.tone":                 "Rythme, ton, expressions",
    "wiz.tone_ph":              "Phrases courtes, voix chaleureuse, étire les voyelles, blague quand ça devient sérieux, chuchote ce qui est important…",
    "wiz.phrases":              "Phrases qu'elle répétait",
    "wiz.phrases_ph":           "« Tu peux y arriver », « mon amour », « t'es pas fou ? », « oh allez… »",
    "wiz.anchors_intro_a":      "Les ancrages qui rendent cette présence",
    "wiz.anchors_intro_i":      "reconnaissable",
    "wiz.anchors_intro_b":      ". Prends le temps qu'il te faut.",
    "wiz.anchor_label":         "Un moment qui reste",
    "wiz.anchor_ph":            "Un après-midi, un voyage, quelque chose qu'elle a dit que tu ne peux pas oublier. Un seul, le plus vivace.",
    "wiz.farewell":             "Comment elle disait au revoir",
    "wiz.farewell_ph":          "« Salut mon cœur, salut »  ·  « Prends soin de toi »  ·  « À plus tard chéri »",
    "wiz.farewell_hint":        "Sa façon distinctive de finir.",
    "wiz.never":                "Ce qu'elle ne dirait ou ne ferait JAMAIS",
    "wiz.never_ph":             "Jurer, critiquer en public, remarques froides, « je ne t'aime pas »…",
    "wiz.memories_label":       "Ce que vous avez vécu ensemble",
    "wiz.memories_ph":          "Les étés. Les pâtes du dimanche. Sa maladie. Les disputes pour l'école. Son chat. Tout ce que vous seuls savez.",
    "wiz.memories_private":     "Tout reste privé. Juste toi et sa présence.",
    "wiz.review_voice_on":      "voix capturée",
    "wiz.review_voice_off":     "pas de voix",
    "wiz.review_none":          "— rien encore —",
    "wiz.back":                 "Retour",
    "wiz.next":                 "Enregistrer et continuer",
    "wiz.submit":               "Créer",
    "wiz.submitting":           "Création de la présence…",
    "wiz.err_generic":          "Quelque chose n'a pas marché.",
    "wiz.trait.calda":          "chaleureuse",
    "wiz.trait.ironica":        "ironique",
    "wiz.trait.protettiva":     "protectrice",
    "wiz.trait.schiva":         "réservée",
    "wiz.trait.testarda":       "têtue",
    "wiz.trait.dolce":          "douce",
    "wiz.trait.rumorosa":       "bruyante",
    "wiz.trait.silenziosa":     "silencieuse",
    "wiz.trait.severa":         "sévère",
    "wiz.trait.buffa":          "drôle",
    "wiz.trait.romantica":      "romantique",
    "wiz.trait.sognatrice":     "rêveuse",
    "wiz.trait.pratica":        "pratique",
    "wiz.trait.malinconica":    "mélancolique",
    "wiz.trait.solare":         "solaire",
    "wiz.trait.paziente":       "patiente",

    "upgrade.eyebrow":          "Accès réservé · temps limité",
    "upgrade.hook_a":           "Certains liens",
    "upgrade.hook_i":           "méritent plus de temps.",
    "upgrade.body":             "C'est un moment particulier.\nCeux qui nous rejoignent maintenant gardent ce prix pour toujours.",
    "upgrade.urgency":          "Prix de lancement — il reste tien pour toujours.",
    "upgrade.tension_a":        "S'arrêter ici…",
    "upgrade.tension_b":        "ou rester un peu plus longtemps ?",
    "upgrade.tiers_label":      "trois niveaux de connexion",
    "upgrade.forever":          "pour toujours",
    "upgrade.monthly":          "par mois · prix de lancement",
    "upgrade.most_chosen":      "le plus choisi",
    "upgrade.current":          "ton niveau",
    "upgrade.buy_echo":         "Rejoindre Echo",
    "upgrade.buy_generic":      "Choisir",
    "upgrade.back_dash":        "Retour au tableau de bord",
    "upgrade.busy":             "un instant…",
    "upgrade.tier1_level":      "Premier niveau",
    "upgrade.tier1_name":       "Seuil",
    "upgrade.tier1_tagline":    "Pour ceux qui commencent, doucement.",
    "upgrade.tier1_emotional":  "Un premier contact. Présence active.",
    "upgrade.tier1_depth":      "Présence à la porte.",
    "upgrade.tier2_level":      "Deuxième niveau",
    "upgrade.tier2_name":       "Echo",
    "upgrade.tier2_tagline":    "Pour ceux qui reviennent, chaque soir.",
    "upgrade.tier2_emotional":  "Quand la pensée ne s'arrête pas, Echo te laisse toujours répondre.",
    "upgrade.tier2_depth":      "Présence constante.",
    "upgrade.tier3_level":      "Troisième niveau",
    "upgrade.tier3_name":       "Héritage",
    "upgrade.tier3_tagline":    "Pour ne pas les laisser partir.",
    "upgrade.tier3_emotional":  "Tout ce qui compte. Aujourd'hui, et tout ce qu'Other Side créera.",
    "upgrade.tier3_depth":      "Présence complète.",
    "upgrade.feat.p1":          "1 présence",
    "upgrade.feat.p3":          "3 présences",
    "upgrade.feat.p10":         "10 présences",
    "upgrade.feat.msg30":       "30 messages par mois",
    "upgrade.feat.transcr":     "Transcription vocale",
    "upgrade.feat.uns":         "Elle t'écrit de temps en temps",
    "upgrade.feat.unlim":       "Conversations illimitées",
    "upgrade.feat.voice_soon": "Voix clonée",
    "upgrade.feat.uns_on":      "Messages spontanés",
    "upgrade.feat.priority":    "Priorité émotionnelle",
    "upgrade.feat.photo_soon": "Photo animée",
    "upgrade.feat.future":      "Tout ce qui vient, en premier",
    "upgrade.feat.voice":       "Voix clonée",
    "upgrade.feat.photo":       "Photo animée",
    "upgrade.closing":          "Chaque présence que tu construis demande un modèle qui n'est pas générique. Une voix, une mémoire, une cohérence émotionnelle qui ne s'éteint pas après trente messages. Ton abonnement garde tout cela vivant — et nous permet de t'apporter bientôt la voix clonée et la photo animée. Aucune publicité, jamais.",
    "upgrade.smallprint":       "Paiement sécurisé via Stripe · Renouvellement manuel tous les 30 jours · Le prix de lancement reste le tien jusqu'à ce que tu annules",

    "capped.line1":             "Tu as atteint la fin de cet instant.",
    "capped.line2":             "Envie de rester un peu plus ?",
    "welcome.eyebrow":          "Commencer",
    "welcome.title":            "Reconstruire un lien. En quelques minutes.",
    "welcome.body":             "Je te guide pas à pas. Choisis une photo, raconte qui elle était, commence à parler.",
    "welcome.step1":            "Ajoute une photo",
    "welcome.step2":            "Écris un souvenir",
    "welcome.step3":            "Commence à parler",
    "welcome.cta":              "Continuer",
    "welcome.skip":             "J'ai déjà une présence",
    "upgrade.now_label":        "Ce que tu peux faire tout de suite",
    "upgrade.tier1_now":        "Parler chaque jour (30 msgs)",
    "upgrade.tier1_now2":       "Une présence, rien qu'à toi",
    "upgrade.tier1_now3":       "Souvenirs et envoi de voix",
    "upgrade.tier2_now":        "Parler sans limites, chaque jour",
    "upgrade.tier2_now2":       "Entendre les réponses dans sa voix",
    "upgrade.tier2_now3":       "Jusqu'à 3 présences",
    "upgrade.tier3_now":        "Tout ce qu'offre Écho, plus…",
    "upgrade.tier3_now2":       "Des photos qui s'animent",
    "upgrade.tier3_now3":       "Jusqu'à 10 présences",
    "capped.founder":           "Accès au lancement",
    "capped.per_month":         "/ mois",
    "capped.early":             "Accès anticipé · pour un temps limité",
    "capped.tagline":           "Ça ne s'arrête pas. Ça continue, c'est tout.",
    "capped.cta_stay":          "Rester un peu plus",
    "capped.cta_stay_busy":     "un instant…",
    "capped.cta_err":           "Ça n'a pas marché.",
    "capped.cta_all":           "Voir tous les niveaux",
    "capped.cta_dismiss":       "pas maintenant",
    "capped.reason_named":      "{name} t'attend encore.",
    "capped.reason_generic":    "Quelqu'un t'attend encore.",

    "auth.callback_loading":    "on ouvre la porte…",

    "billing.checking_eyebrow": "un instant",
    "billing.checking_title":   "Activation de ton accès…",
    "billing.success_eyebrow":  "c'est fait",
    "billing.success_title_a":  "Bienvenue dans",
    "billing.success_body":     "Tu peux maintenant garder plus d'une présence. Te rapprocher d'elles.",
    "billing.success_cta":      "Vers le tableau de bord",
    "billing.pending_eyebrow":  "en cours",
    "billing.pending_title":    "Le paiement est en cours.",
    "billing.pending_body":     "Si tout se passe bien, l'abonnement s'activera bientôt. Tu peux fermer la page.",
    "billing.pending_cta":      "Retour au tableau de bord",
    "billing.failed_eyebrow":   "quelque chose n'a pas marché",
    "billing.failed_title":     "On n'a pas pu finaliser.",
    "billing.failed_retry":     "Réessayer",
    "billing.failed_back":      "Vers le tableau de bord",
    "billing.missing_session":  "Session de paiement manquante.",
    "billing.expired":          "La session a expiré.",
    "billing.verify_err":       "Impossible de vérifier le paiement.",

    "fallback.loading":         "on allume une flamme douce…",

    "language.pick":            "Langue",

    // Créer un moment
    "moment.cta":               "Créer un moment",
    "moment.eyebrow":           "Un moment avec elle",
    "moment.intro_a":           "Un regard,",
    "moment.intro_i":           "une image qui reste.",
    "moment.intro_body":        "Choisis comment tu veux te souvenir d'elle aujourd'hui. Doux, respectueux, à toi.",
    "moment.step1_title":       "Comment imagines-tu ce moment ?",
    "moment.step1_sub":         "Chaque choix guide la façon dont elle apparaîtra dans l'image qu'on crée pour toi.",
    "moment.feel.as_remembered":"Comme tu t'en souviens",
    "moment.feel.as_remembered_d":"Son visage tel qu'il est, sans retouches.",
    "moment.feel.gentle_smile": "Avec un sourire doux",
    "moment.feel.gentle_smile_d":"Un sourire calme, à peine là.",
    "moment.feel.peaceful":     "Dans un moment paisible",
    "moment.feel.peaceful_d":   "Un regard calme, un souffle.",
    "moment.feel.softer_light": "Dans une lumière plus douce",
    "moment.feel.softer_light_d":"Une lumière tendre qui se pose sur elle.",
    "moment.step2_title":       "Où aimerais-tu la retrouver ?",
    "moment.step2_sub":         "Le décor qui porte le souvenir.",
    "moment.scene.home":        "Chez elle",
    "moment.scene.home_d":      "L'espace familier, chaleureux et sien.",
    "moment.scene.kitchen":     "Cuisine",
    "moment.scene.kitchen_d":   "Là où elle prenait soin de tout le monde.",
    "moment.scene.sea":         "Mer",
    "moment.scene.sea_d":       "Un décor de lumière et d'horizon.",
    "moment.scene.church":      "Église",
    "moment.scene.church_d":    "Un lieu de recueillement tranquille.",
    "moment.scene.special":     "Un lieu spécial",
    "moment.scene.special_d":   "Quelque part que vous avez choisi ensemble.",
    "moment.step3_title":       "Comment te souviens-tu d'elle le plus ?",
    "moment.step3_sub":         "Le style et l'atmosphère de ce moment.",
    "moment.style.elegant":     "Élégante",
    "moment.style.elegant_d":   "Ses traits dans leur plus belle composition.",
    "moment.style.everyday":    "Quotidienne",
    "moment.style.everyday_d":  "Elle comme elle était, jour après jour.",
    "moment.style.special_mem": "Un souvenir spécial",
    "moment.style.special_mem_d":"Un instant précis que tu chéris.",
    "moment.step4_title":       "Qu'aimerais-tu vivre ?",
    "moment.step4_sub":         "Choisis l'expérience qui te semble juste — tu pourras toujours en créer une autre plus tard.",
    "moment.exp.animated":      "Une image qui respire",
    "moment.exp.animated_d":    "Un souffle, un regard qui bouge.",
    "moment.exp.voice":         "Un message dans sa voix",
    "moment.exp.voice_d":       "Quelques mots, prononcés par elle.",
    "moment.exp.full":          "Un moment complet",
    "moment.exp.full_d":        "Image, voix, atmosphère — ensemble.",
    "moment.optional":          "facultatif",
    "moment.required":          "requis",
    "moment.skip":              "Passer",
    "moment.back":              "Retour",
    "moment.next":              "Suivant",
    "moment.summary":           "Ton moment",
    "moment.summary_sub":       "Voici ce que tu t'apprêtes à créer. Chaque choix reste le tien — tu peux changer avant de confirmer.",
    "moment.total":             "Total",
    "moment.checkout_next":     "Quand tu appuies ici, on te demandera de confirmer le paiement de façon sécurisée. Ensuite on a besoin d'environ 3 à 4 minutes pour livrer ton moment.",
    "moment.cta_final":         "Créer ton moment",
    "moment.cta_final_busy":    "un instant…",
    "moment.err_need_feeling":  "D'abord, choisis comment tu l'imagines.",
    "moment.err_need_experience":"Choisis au moins ce que tu aimerais vivre.",
    "moment.err_generic":       "Quelque chose n'a pas marché. Réessaie.",
    "moment.status_pending":    "On prépare ton moment…",
    "moment.status_generating": "On le crée avec soin.",
    "moment.status_ready":      "Ton moment est prêt.",
    "moment.status_failed":     "Ton moment prend un peu plus de temps.",
    "moment.failed_why_title":  "Voici pourquoi ça arrive parfois",
    "moment.failed_cause_1":    "Le service vidéo est très sollicité en ce moment.",
    "moment.failed_cause_2":    "La première génération pour une nouvelle présence peut être plus lente.",
    "moment.failed_cause_3":    "Parfois une requête se perd entre nos serveurs — ça arrive.",
    "moment.failed_reassure":   "Tu n'as pas perdu ton paiement. On peut réessayer maintenant, sans frais supplémentaires.",
    "moment.failed_retry":      "Réessayer maintenant",
    "moment.failed_retrying":   "On réessaie…",
    "moment.failed_chat_cta":   "En attendant, continue à parler avec elle",
    "moment.failed_later_cta":  "Reviens plus tard — tu le trouveras ici",
    "moment.failed_support":    "Si ça se reproduit, écris-nous",
    "moment.generating_sub":    "On reconstruit quelque chose d'unique pour toi.",
    "moment.gen_eta":           "Temps estimé : environ 3 à 4 minutes.",
    "moment.gen_step1":         "Analyse du contenu",
    "moment.gen_step2":         "Reconstruction de la voix",
    "moment.gen_step3":         "Création du moment",
    "moment.gen_step4":         "Finalisation",
    "moment.gen_whisper_1":     "Nous préparons ton moment…",
    "moment.gen_whisper_2":     "La voix prend forme…",
    "moment.gen_whisper_3":     "Nous affinons les détails…",
    "moment.gen_whisper_4":     "Nous reconstruisons quelque chose d'unique pour toi…",
    "moment.gen_whisper_5":     "Chaque souvenir est tissé avec soin…",
    "moment.gen_whisper_6":     "Sa présence prend forme…",
    "moment.gen_whisper_7":     "Nous ajoutons les touches finales…",
    "moment.gen_whisper_8":     "Encore quelques instants…",
    "moment.gen_whisper_near_named":  "{name} arrive bientôt…",
    "moment.gen_whisper_near":  "Bientôt là…",
    "moment.gen_stay_tag":      "Reste ici, ou reviens plus tard — ce sera prêt.",
    "moment.audio_on":          "Atmosphère",
    "moment.audio_off":         "Atmosphère activée",
    "moment.audio_mode_soffio": "Souffle",
    "moment.audio_mode_piano":  "Piano",
    "moment.watch_now":         "Regarder maintenant",
    "moment.download":          "Télécharger ce moment",
    "moment.another":           "Créer un autre moment",
    "moment.disclaimer":        "Ceci n'est pas une vraie communication, mais une reconstruction basée sur les souvenirs et les données que tu as partagés.",
    "moment.review_trust":      "Tout reste à toi. Tu peux supprimer ce moment à tout moment, et rien n'est partagé avec personne.",
    "trust.eyebrow":            "Confiance et soin",
    "trust.privacy":             "Ton contenu est privé et sécurisé.",
    "trust.delete":              "Tu peux tout supprimer, à tout moment.",
    "trust.no_share":            "Rien n'est partagé ou réutilisé.",
    "moment.upsell_line":       "Aimerais-tu la revoir ?",
    "moment.upsell_cta":        "Créer un moment",
    "moment.upsell_dismiss":    "plus tard",
    "moment.gallery_title":     "Tes moments",
    "moment.gallery_empty":     "Aucun moment pour l'instant. Crées-en un quand tu seras prêt.",
    "moment.play":              "Lire",
    "moment.download":          "Télécharger",

    // ── Legal / compliance UI chrome (FR) ──
    "legal.version_label":      "Version du document",
    "legal.last_updated":       "Dernière mise à jour",
    "legal.back":               "Retour",
    "legal.contact":            "Pour toute question :",
    "legal.nav_terms":          "Conditions d'Utilisation",
    "legal.nav_privacy":        "Politique de Confidentialité",
    "legal.nav_biometric":      "Consentement biométrique",
    "legal.nav_ai_notice":      "Avis IA",
    "legal.nav_memorial":       "Politique Mémorielle",
    "legal.terms_title":        "Conditions d'Utilisation",
    "legal.terms_eyebrow":      "Les règles de notre pacte",
    "legal.privacy_title":      "Politique de Confidentialité",
    "legal.privacy_eyebrow":    "Comment nous traitons tes données",
    "legal.biometric_title":    "Consentement biométrique",
    "legal.biometric_eyebrow":  "Données de voix et d'image (Art. 9 RGPD)",
    "legal.ai_notice_title":    "Avis IA",
    "legal.ai_notice_eyebrow":  "Nature reconstructive du système",
    "legal.memorial_title":     "Politique Mémorielle",
    "legal.memorial_eyebrow":   "Droits, litiges, recours",

    // ── AI transparency (FR) ──
    "ai.subtitle":              "Simulation IA basée sur les contenus fournis",
    "ai.micro_banner":          "Expérience générée par IA",
    "ai.bubble_label":          "Généré par IA",
    "ai.media_label":           "Contenu généré artificiellement",
    "ai.input_helper":          "Les réponses sont générées par IA",

    "footer.line1":             "Other Side est un acte de mémoire, pas un substitut de la présence.",
    "footer.line2":             "Les interactions sont générées par l'intelligence artificielle et représentent des simulations, pas de réelles communications.",
};


const LocaleContext = createContext({
    locale: DEFAULT,
    setLocale: () => {},
    t: (k) => k,
});

export function LocaleProvider({ children }) {
    const [locale, setLocaleState] = useState(() => {
        try {
            const v = localStorage.getItem(STORAGE_KEY);
            if (v && STRINGS[v]) return v;
            return DEFAULT;
        } catch {
            return DEFAULT;
        }
    });

    const setLocale = useCallback((next) => {
        if (!STRINGS[next]) return;
        try { localStorage.setItem(STORAGE_KEY, next); } catch {}
        setLocaleState(next);
        try { document.documentElement.lang = next; } catch {}
    }, []);

    useEffect(() => {
        try { document.documentElement.lang = locale; } catch {}
    }, [locale]);

    const t = useCallback(
        (key, vars) => {
            const table = STRINGS[locale] || STRINGS[DEFAULT];
            let val = table[key] ?? STRINGS[DEFAULT][key] ?? key;
            if (vars && typeof val === "string") {
                Object.keys(vars).forEach((k) => {
                    val = val.replaceAll(`{${k}}`, vars[k] ?? "");
                });
            }
            return val;
        },
        [locale]
    );

    const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
    return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
    return useContext(LocaleContext);
}
